// StreamingController
//
// === LOAD MODEL: coverage-gated, distance-prioritized, rest-aware LOD ===
//
// Three rules, in order:
//
//   (1) FETCH gate is screen-space pixel COVERAGE, not frustum overlap.
//       For each tile we project the 8 bbox corners through the current
//       view+projection matrix, clip to NDC, and estimate the pixel
//       area. Only tiles whose projected area exceeds
//       `coverageThresholdPixels` are eligible to load. This is what
//       ThatOpen's GeometryCullerRenderer does (theirs is a real
//       offscreen pass with readPixels; ours is a CPU-side projection,
//       same end result for the streaming gate). The threshold means a
//       tile that occupies ~50px on the user's screen at most is too
//       small to bother fetching — by the time they zoom in to make it
//       larger, the coverage rises and the gate admits it.
//
//   (2) UNLOAD gate uses the same coverage metric with hysteresis.
//       A VISIBLE tile whose coverage stays below threshold for
//       `maxHiddenTimeMs` (default 5 s) gets unloaded. The hysteresis
//       window is what stops a quick pan from churning — the tile has
//       to be truly out-of-relevance for several seconds before we
//       pay the unload+reload cost. Mirrors ThatOpen's `maxHiddenTime`.
//
//   (3) LOD evaluation is debounced to CAMERA REST.
//       Per-frame we still walk tiles for the load/unload gates, but
//       the per-tile LOD recompute (lodSelector.select(distance, …))
//       only fires when the camera has been stationary for at least
//       `lodRestMs` (default 150 ms). The user's mouse is moving →
//       no LOD swaps → the geometry-pointer churn from constant LOD
//       reassignment stays out of the hot path. The user settles →
//       LOD recomputes once.
//
// Per tick:
//   1. project camera state → viewProj matrix + camera-rest check
//   2. walk every tile in trimmed bounds:
//      - VISIBLE   + coverage low for >maxHiddenTimeMs   → unload
//      - VISIBLE   + camera at rest + LOD changed       → swap LOD
//      - non-visible + coverage > threshold              → enqueue load
//      - anything else                                   → no-op
//   3. dispatch up to `maxConcurrentTileLoads` from the queue, sorted
//      by distance from camera (closest first)
//   4. LRU cache eviction — unchanged
//
// === STAGE 1 OF MULTI-STAGE PERF WORK ===
//
// This file is the contained, low-risk perf layer. The bigger wins
// — consolidating per-(tile, hash) InstancedMeshes into one global
// InstancedMesh per hash, then packing those into a few `BatchedMesh`
// instances to land at single-digit draw calls — sit in their own
// commits, because they touch InstanceMaterializer, MapMeshesAdapter,
// and InstanceStateApplicator. WASM/worker offload is a separate
// multi-week project after that.
//
// === STATE-STORE INTEGRATION ===
//
// Every InstancedMesh built via _materializeTile gets the applicator
// threaded through InstanceMaterializer.materialize. This guarantees
// that any pre-existing store state (hide/isolate/highlight set on a
// uid before this tile was visible) is applied to the brand-new mesh
// at construction time. Without this, a hidden uid would un-hide the
// first time its tile loaded after the hide command — a classic
// "fresh state on stale geometry" bug.
//
// === Memory eviction === (unchanged)
//
// Without eviction, both `cache.instancedEntries` and
// `cache.libraryEntries` grow monotonically over a long session.
// Two soft caps after every dispatch tick. Eviction order is
// instanced-first, library-second so we don't dangle InstancedMesh
// geometry pointers.
//
// === Missing fragmentUrls handling === (unchanged)

import { Box3, Matrix4, Vector3 } from 'three';
import { reportClientError } from '../../../../helpers/clientLog';
import { TileState } from './TileRegistry';
import InstanceMaterializer from './instanceMaterializer';
import buildTileBatch from './tileBatch';
import { coincidentKey } from './coincidentKey';
import { MERGE_LOD_NAMES, buildMergeMesh, parseMergeBuffer } from './tileMerge';
import { flushDirtyPools } from './GlobalInstancePool';
import { markSceneChanged } from './renderSignal';
import { isolationOutranksBudget } from './isolationRule';
import { splitByPixelSize, instanceWorldPosition } from './pixelSkip';
import { shouldHideGroup, isClearanceHash, createClearanceState } from './clearanceFilter';
import thrashLedger from './thrashLedger';

function isInstancedEntryInUse(entry) {
  if (!entry || !Array.isArray(entry.fragments)) return false;
  for (const f of entry.fragments) {
    if (f && f.parent !== null && f.parent !== undefined) return true;
  }
  return false;
}


// The fittings that join runs into a network (fragment key head).
const CONNECTOR_RE = /^(ConduitFitting|PipeFitting|DuctFitting|CableTrayFitting)_/;
const connectorCache = new Map();
// A module function rather than only a method: tests (and the revive
// path) call _shouldDefer on objects that carry just _pxOf.
// THE THRASH LEDGER (thrashLedger.js) a controller counts into: its own when
// a test hands one, else the module ledger perfSnapshot reads. Module level,
// like noteConeDrops, so bookkeeping works whatever `this` a caller borrows
// a method with.
function thrashOf(ctrl) { return (ctrl && ctrl.thrashLedger) || thrashLedger; }

// The retire reasons the dwell holds back (see retireDwellMs): what the
// rest check derived from the floor, the cone set and the size floors.
const DWELL_REASONS = new Set(['budget', 'cone', 'size', 'heavy']);
// Tiles with rows the budget pass needs in view before it estimates the
// rest from them (see _anticipateUnseen).
const ANTICIPATE_MIN_TILES = 8;

// Rows the cone admitted before and does not now, per tile, kept until a
// retire of that tile consumes them or the cone admits them again: lets the
// thrash ledger name 'cone' as a retire reason. O(rows admitted) per cone
// pass, which is throttled; no behaviour reads it.
function noteConeDrops(ctrl, prev, next) {
  if (!(prev instanceof Map) && !(next instanceof Map)) return;
  const drops = ctrl._coneDropped || (ctrl._coneDropped = new Map());
  if (prev instanceof Map) {
    prev.forEach((set, key) => {
      const now = next instanceof Map ? next.get(key) : null;
      set.forEach((h) => {
        if (now && now.has(h)) return;
        let d = drops.get(key);
        if (!d) { d = new Set(); drops.set(key, d); }
        d.add(h);
      });
    });
  }
  if (next instanceof Map) {
    next.forEach((set, key) => {
      const d = drops.get(key);
      if (!d) return;
      set.forEach((h) => d.delete(h));
      if (!d.size) drops.delete(key);
    });
  }
}

function rankPx(ctrl, hash, distance, focalPx) {
  const px = ctrl._pxOf(hash, distance, focalPx);
  if (px === null || px === undefined || !(ctrl.connectorPxBoost > 1)) return px;
  return isConnectorHash(hash) ? px * ctrl.connectorPxBoost : px;
}
export function isConnectorHash(hash) {
  let v = connectorCache.get(hash);
  if (v === undefined) {
    v = CONNECTOR_RE.test(String(hash || ''));
    if (connectorCache.size > 200000) connectorCache.clear();
    connectorCache.set(hash, v);
  }
  return v;
}

// FOLD SMALL POOLS INTO THE TILE BATCH (round 9). A group of up to this
// many rows in a tile goes into the tile's batch rather than a pool of its
// own, as long as copying it is cheap (see batchFoldMaxTris). A pool is one
// draw call whether it holds 3 instances or 3,000, and at rest on SpaceX the
// visible pools with fewer than ten live instances were calls that
// instancing was not paying for. localStorage bl_fold_max=<n> overrides for
// an A/B; 0 turns folding off (back to batchMaxInstances alone).
export function defaultFoldMax() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('bl_fold_max') : null;
    const v = raw == null ? NaN : Number(raw);
    if (Number.isFinite(v) && v >= 0 && v <= 64) return Math.floor(v);
  } catch (e) { /* storage blocked */ }
  return 9;
}

export function defaultAnticipate() {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('bl_anticipate') === 'off') return false;
  } catch (e) { /* storage unavailable: the default stands */ }
  return true;
}

export function defaultBatchMax() {
  try {
    const v = typeof localStorage !== 'undefined' ? Number(localStorage.getItem('bl_batch_max')) : NaN;
    if (Number.isFinite(v) && v >= 1 && v <= 64) return Math.floor(v);
  } catch (e) { /* storage blocked */ }
  return 2;
}

export default class StreamingController {
  constructor({
    viewer, registry, frustumTester, lodSelector,
    fetcher, manifestLoader, cache, adapter, sceneRoot,
    // NEW: applicator threaded through to materialize so newly built
    // meshes pick up pre-existing store state immediately.
    applicator = null,
    // Defaults are tuned for Forge-style eager streaming.
    //
    // fetchTickMs: 16 ≈ one dispatch per animation frame at 60 fps.
    // The previous 100 ms cap meant only 10 dispatch opportunities per
    // second, which limited steady-state throughput regardless of how
    // many concurrent slots were free. With 16 ms ticks the dispatch
    // loop simply keeps the concurrency window full.
    //
    // maxConcurrentTileLoads: 64 in-flight fetches (raised from 32
    // on 2026-05-13). Cloudflare R2 (and any modern HTTP/2 origin)
    // multiplexes these over a couple of TCP connections cheaply;
    // 64 is still well below the per-connection HTTP/2 stream limit
    // (Chromium defaults to 100). The previous cap of 4 was the
    // single biggest reason an 850-tile project took minutes to fill
    // in. With 32 we saw network underutilization on fast links
    // (sequential RTT-bound 302→R2 chain per fetch); doubling lets
    // the dispatch loop saturate the redirect/handshake pipeline
    // while staying well clear of per-stream limits. Origin throttle
    // risk is low — R2 is per-bucket not per-IP, and signed-URL
    // generation is on the Express side which has plenty of headroom.
    //
    // maxInstancedEntries / maxLibraryEntries: pushed up so eager
    // loading isn't immediately evicting the same tiles we just
    // materialized. The caches are LRU and only evict entries NOT
    // currently in the scene, so the headroom matters most for
    // tiles that have been unloaded once and may be re-shown.
    // maxConcurrentTileLoads matched to the ManifestLoader sidecar
    // semaphore. Both raised 32 → 48 to use more of the available
    // HTTP/2 budget (Chromium ~100 streams/connection; 48 sidecars +
    // 48 fragments = 96 worst-case, comfortably under).
    //
    // maxLibraryEntries 8000 (raised from 2000) so the cache holds
    // every hash a real BIM model produces. Eliminates the eviction-
    // and-re-fetch churn that the previous 2000 cap caused.
    // fetchTickMs 4: dispatch loop polls the queue almost continuously
    // while there's pending work. maxConcurrentTileLoads 96 matches the
    // fetcher cap so the tile-side queue can saturate the network.
    fetchTickMs = 4,
    maxConcurrentTileLoads = 96,
    sidecarPrefetchRadius = null,
    maxInstancedEntries = 1500,
    maxLibraryEntries = 8000,
    evictionTickMs = 1000,
    // Coverage gate — see class doc.
    //
    // coverageThresholdPixels lowered from 50 → 10 after user testing
    // reported "model doesn't load fully": at 50 px², distant tiles
    // that legitimately should render (a window in a wall 30 m away,
    // ~6×8 px on screen) were getting filtered. 10 px² = a 3×3 speck,
    // which is below the user's ability to perceive a missing element
    // and far below the cost of one extra fetch.
    //
    // maxHiddenTimeMs: a VISIBLE tile must be below threshold for
    //   this long before it gets unloaded. Hysteresis kills the
    //   "pan-and-thrash" pattern.
    //
    // lodRestMs: see class doc — no LOD swaps actually happen in
    //   pooled mode, kept for future per-LOD-pool work.
    //
    // poolRegistry: REQUIRED. The materializer routes every (tile,
    //   hash) acquisition through it, and the consolidation is what
    //   collapses draw calls.
    coverageThresholdPixels = 10,
    maxHiddenTimeMs = 5000,
    lodRestMs = 150,
    // Frustum-padding factor. >1.0 treats the camera frustum as wider
    // than it actually is for the tile-hide / tile-unload gate. Tiles
    // just outside peripheral vision stay rendered and resident, so a
    // small pan doesn't pop them in. Default 1.25 = 25% wider AABB.
    // Set to 1.0 for the literal frustum (more aggressive culling);
    // set to a large value (e.g. 100) to effectively disable.
    frustumPaddingFactor = 1.25,
    // WHERE THE CAMERA POINTS, EVERYTHING LOADS.
    //
    // The size floors below (minFragmentPixels, the heavy floor and the
    // triangle budget's dynamic floor) are what keep a whole site
    // affordable, and they are right for the model at large. They are
    // wrong for what the user is actually looking at: a fragment two
    // pixels across in the middle of the screen is still something they
    // asked to see, and "the model does not load fully" is that floor
    // seen from the other side.
    //
    // So once the camera has been still for focusRestMs, every tile in
    // the padded frustum is in FOCUS: its fragments load whatever their
    // size. Detail still follows size - a speck loads at the coarse
    // level of detail rather than being skipped - so the cost of the
    // guarantee is bounded by the screen, not by the model. Outside the
    // frustum the floors apply exactly as before.
    focusGuarantee = true,
    focusRestMs = 350,
    poolRegistry = null,
    // Per-hash fragment facts from the exporter (manifest.fragmentsIndex
    // -> tiles/fragments-index.json.gz): { <hash>: { r: bounding-sphere
    // radius ft, t: [fine, medium, coarse] triangles, h: heavy } } and
    // manifest.heavyFragments. Both optional: without them nothing is
    // deferred and older exports behave exactly as before.
    fragmentsIndex = null,
    heavyFragments = null,
    // Screen-size deferral (2026-09-08, SpaceX BA03 FPS work): a hash
    // whose fragment would cover fewer than minFragmentPixels on screen
    // from the tile's distance is not materialised (its rows wait in
    // tile._deferredHashes); a HEAVY fragment waits until it covers
    // heavyFragmentPixels. Re-checked at camera rest; revived rows are
    // registered under a virtual tile key so the adapter's per-uid
    // ownership and the unload path stay unchanged.
    // PER-INSTANCE size gate. The floors above test a whole (tile, hash)
    // GROUP against its centroid distance; this tests each row against its
    // own. Measured on the live SpaceX export before it was written: 53% of
    // INSTANCES sit in groups spread wider than half their cull distance,
    // even though 84% of GROUPS are tight - the big groups are the spread
    // ones. Held rows go into tile._deferredHashes and are revived by the
    // rest window exactly like a deferred group, so nothing is dropped.
    // Clearance zones are OFF until somebody asks for them. Hidden groups go
    // into the same tile._deferredHashes the size floors use, so turning them
    // on is a REVIVE rather than a reload - the machinery already exists and
    // is already driven.
    clearanceState = null,
    perInstancePixelSkip = true,
    minFragmentPixels = 2,
    // THE CONE RULE'S THRESHOLD (owner, 2026-09-27). At camera rest every
    // group in the true frustum covering at least this many pixels is
    // admitted whatever the adaptive floor says. A PARAMETER rather than a
    // constant because the cost depends entirely on it and is measured per
    // model: on BA03, 20 px is 6,039 deferred rows and 0.84 M triangles and
    // fits inside an 8 M budget, while no threshold at all is 87,031 rows
    // and 11.55 M and does not. He asked for 4; nobody has measured 4 yet,
    // so this must be changeable without touching the design. 0 disables.
    coneMinPx = 4,
    // The cone's allowance, as a share of maxTrianglesPerFrame (and of
    // maxDrawCalls), for rows the budget floor holds back (see _admitCone).
    // Since round 9 it is reserved INSIDE the caps: the floor fills the
    // other 85% (_coneReserve), so floor + cone never exceeds the budget.
    coneBudgetShare = 0.15,
    // Slots _frustumShow may restore per tick (see tick()). 0 = no cap.
    frustumShowSlotsPerTick = 50000,
    // How often the cone set is RECOMPUTED while the camera stays at rest.
    // The first rest tick after any camera move recomputes at once, so the
    // response when the user stops is unchanged; after that the answer only
    // changes as tiles arrive, which is a seconds-scale event.
    //
    // Measured 2026-10-13 on BA03 (staging, warm cache, ~730 visible
    // tiles): one _admitCone pass is ~44 ms of main thread, and tick() runs
    // every 16 ms from a setInterval - so at rest the cone alone took ~80%
    // of the main thread (tick averaged 55 ms, 180 ticks per 10 s) and the
    // materialisation slice, worker hand-offs and frames queued behind it.
    // Recomputing once a second: tick 5 ms, main thread 58% idle.
    coneRefreshMs = 1000,
    // THE FAR PLANE WHILE THE CAMERA IS MOVING (owner, 2026-09-27: "we can
    // use near far cut plane of the camera also, during movement, we make it
    // short, when user stops we revert it far"). In export-frame feet.
    //
    // This is the cheap form of occlusion culling and it turned out to
    // matter MORE than the pixel threshold: measured on BA03 at the Level-1
    // camera, admitting the deferred rows at 4 px costs +4.38 M triangles on
    // the full cone and +0.73 M capped at 200 ft - so 4 px CAPPED is cheaper
    // than 20 px UNCAPPED. The cone is dominated by geometry standing behind
    // what you are looking at, and a distance cap removes it without an
    // occlusion query.
    //
    // The cost is that distant geometry is clipped while you move and
    // returns when you stop. 200 ft was the owner's choice with that trade
    // stated. 0 disables.
    //
    // OFF since 2026-10-19 (owner: "increase camera near and far plane
    // during mouse movement, to pre optimization"): the far plane stays
    // where it is while moving, as before 2026-09-27. Moving draws the
    // geometry behind what you look at again, and the sky is no longer
    // cut while orbiting. The cap code stays; a number here turns it on.
    moveFarCap = 0,
    // Fittings rank this many times their own size against the pixel
    // floors and the budget (owner, 2026-09-18: "a lot of conduit
    // fittings in the bend between 2 conduits are missing"). An elbow
    // is ~9x smaller than the run beside it (SpaceX median radius 0.46
    // vs 4.24 ft), so over budget it was always the first thing floored
    // and every bend opened a gap - a network with holes reads as broken
    // where an equally small loose object would not. 0 = off.
    connectorPxBoost = 8,
    // Heavy fragments (exporter flag / > 500k tris at some LOD): since
    // the soup-aware rebuild their medium/coarse levels are real
    // (SpaceX: coarse 1-3k, medium 5-24k tris), so they draw from
    // anywhere; only their FINE level (186k-724k tris) is held back
    // until the fragment fills a large part of the screen.
    heavyFragmentPixels = 24,
    heavyFinePx = 400,
    heavyMediumPx = 120,
    // Per-hash screen-size LOD (That Open style): a fragment whose
    // projected diameter is under lodMediumPx draws coarse, under
    // lodFinePx medium, above it fine - instead of one LOD per tile
    // from the camera distance bands. quality scales every pixel
    // threshold (2 = twice as aggressive, 0.5 = twice as detailed).
    lodFinePx = 32,
    lodMediumPx = 8,
    // Triangles a fragment may spend per projected pixel of area: a
    // 100 px fragment gets up to 5k triangles, 400 px up to 80k, 1,000 px
    // up to 500k. Applied after the size thresholds so a medium level
    // that is still too dense for its footprint drops to coarse.
    trisPerPx2 = 0.5,
    quality = 1,
    // Main-thread budget for materialisation per animation frame
    // (pool.acquire per row is the cost); over budget the work yields
    // to the next frame so the page keeps painting.
    materializeBudgetMs = 18,
    // Slice while tiles are materialising (render drops to ~15 fps then,
    // see viewer.animate); the 18 ms value applies once loading is done
    // and only revive/retire work remains.
    materializeBudgetMsLoading = 40,
    // GPU bytes the fragment library may keep resident before LRU
    // eviction of libraries no pool draws (That Open: deviceMemory GB
    // x 100 MB). Our libraries carry three LODs each, so x 120 MB;
    // 480 MB when the device does not say.
    maxLibraryBytes = null,
    // Hard triangle budget per frame (That Open bounds memory; we bound
    // triangles, which is what the GPU pays for). At camera rest the
    // controller sizes every visible fragment in pixels, keeps the
    // largest ones until the budget is spent and raises the pixel floor
    // for the rest, so a big site always renders within the budget and
    // detail comes back as the user gets closer. 0 disables.
    maxTrianglesPerFrame = null,
    // DRAW-CALL BUDGET (2026-09-17). Triangles were never the ceiling on
    // SpaceX - draw calls are: 11,257 calls for 5.4M triangles at 120 s
    // with most of the site still loading, because nearly every element
    // is a pool of its own. Counted the same way as triangles at camera
    // rest (a pool per hash and level, one per tile for batched rows),
    // and whichever budget runs out first sets the floor. null = sized
    // from the device (StreamingController.deviceBudget); 0 disables.
    maxDrawCalls = null,
    // What the camera points at goes first, not free (owner, 2026-09-17):
    // when the view is over budget, an in-view group ranks as if it were
    // this many times larger on screen, so the floor takes off-view and
    // then the smallest in-view pieces - never a large piece in view
    // before a small one out of it. Everything in view still loads
    // whenever it fits.
    focusPxBoost = 4,
    // Fragment downloads in flight per tile (see _materializeTileBody).
    maxFetchesPerTile = 12,
    // COLD-START BURST (2026-09-16).
    //
    // The steady-state budgets above protect interaction: they keep the
    // main thread painting while the user orbits. For the first seconds
    // after a model opens there is no interaction to protect - the user
    // is waiting for the model to appear - so the same budgets simply
    // make the wait longer. Autodesk's viewer behaves the same way: it
    // spends everything it has on the first view, then settles.
    //
    // So for `coldStartMs` after the first tile is dispatched the
    // controller runs on burst values: a bigger main-thread slice per
    // frame, more fragment fetches per tile, more tiles loading at
    // once, and a raised fetch ceiling on the fetcher. The window ends
    // on a timer, and also early once nothing is loading any more, and
    // everything reverts to the steady-state numbers.
    coldStartMs = 30000,
    coldMaterializeBudgetMs = 64,
    coldMaxFetchesPerTile = 24,
    coldMaxConcurrentTileLoads = 160,
    coldMaxConcurrentFetches = 160,
    // Rest-window work per tick (see tick()): tiles examined for
    // deferral/LOD drift, tiles re-materialised, and how often the
    // triangle budget is re-sized over every visible group.
    restChecksPerTick = 24,
    forcedSwapsPerTick = 2,
    // COARSEN WHILE MOVING (2026-09-16).
    //
    // A tile picks its level of detail when it materialises and the
    // rest window re-checks it - but only once the camera is still.
    // Fly in close and a tile loads fine; fly out again and it stays
    // fine, because the camera never rested. Over a walkthrough of a
    // big site the view accumulates fine geometry for everything ever
    // approached, which is what "heavy, low fps when navigating" is.
    //
    // So while the camera moves, tiles may still go COARSER - never
    // finer. Coarsening is the cheap direction: the coarse geometry is
    // already in the same cached library, so no fetch is involved, and
    // dropping detail on something shrinking in the distance is not a
    // visible loss. Going finer stays on the rest window, where it
    // cannot churn during a drag-orbit.
    coarsenWhileMoving = true,
    coarsenSwapsPerTick = 2,
    // PROGRESSIVE INSTALL (2026-09-16).
    //
    // A tile used to draw nothing until every one of its fragments had
    // downloaded: the pools' GPU upload ran once at the end, and the long
    // tail (fragments with one or two instances, most of a model like
    // SpaceX) goes into a BatchedMesh that was only built then, because
    // a BatchedMesh cannot grow. A dense tile needs thousands of files,
    // so the view stayed empty for minutes (26 of 1,377 tiles after 90 s).
    //
    // Now, every `progressiveFlushMs` while a tile loads, the pools are
    // uploaded and whatever long-tail items have arrived since the last
    // flush are built into a chunk and drawn. When the tile completes,
    // one batch is built as before and the chunks are dropped, so the
    // finished tile has exactly the draw calls it always had. A tile that
    // finishes inside the interval never builds a chunk at all.
    progressiveFlushMs = 150,
    progressiveChunkMs = 1000,
    progressiveMinItems = 24,
    // GLOBAL ORDER BY SCREEN SIZE (2026-09-16). Fetch slots go to the
    // fragment that is largest on screen anywhere in the model, not tile by
    // tile: every loading tile queues ALL its fragments with a live rank
    // (slotQueue) instead of trickling a dozen at a time, the rank is
    // re-read when the camera moves (`reprioritizeMs`), and work for a tile
    // that unloaded is dropped before it takes a slot.
    globalOrder = true,
    reprioritizeMs = 300,
    // The one exception to the focus guarantee: a fragment whose
    // cheapest level is still over this many triangles keeps the heavy
    // pixel floor instead of loading at any size. See _shouldDefer.
    focusTriangleCeiling = 150000,
    budgetRebalanceMs = 2000,
    // HYSTERESIS ON THE REST PASS (round 11). A tile installed, swapped or
    // revived less than retireDwellMs ago is not re-materialised to retire
    // groups the budget, the cone or the size floor no longer want - it is
    // held until the dwell is over and checked then, once, against whatever
    // the floor is by then. While a cold load is still bringing tiles in the
    // floor climbs every budget pass, and without a dwell every climb
    // re-materialised the same near tiles again (Tom, r10: 195-232 tiles
    // reloaded more than once, up to 9 times). A LOD change, an isolation
    // or a merge retire is not held. 0 turns the dwell off.
    retireDwellMs = 12000,
    // A deferred group comes back (revive) only once it ranks this much
    // ABOVE the floor; a drawn group is retired below the floor itself. The
    // two thresholds differ so a floor that wobbles a little does not
    // retire and revive the same rows. The margin sits on the revive side
    // on purpose: what it leaves out is UNDER the budget, never over it.
    reviveMargin = 0.15,
    // The budget pass estimates the in-view tiles whose sidecars have not
    // landed yet, so the floor is near its real value before most tiles
    // install (see _anticipateUnseen). localStorage bl_anticipate=off turns
    // it off for an A/B.
    anticipateUnseen = defaultAnticipate(),
    // Per-tile batching (2026-09-09). A hash with at most
    // batchMaxInstances rows in a tile goes into that tile's
    // BatchedMesh instead of getting an InstancedMesh pool of its own:
    // on the live SpaceX export half of all hashes have ONE instance
    // and carry 4% of the geometry, while a headed capture read 9,290
    // draw calls against 12,899 pools with 432 tiles visible. Off
    // until the captures show parity on selection, hide, isolate and
    // the revision overlay - see docs/viewer-batching.md.
    batching = false,
    // 2. Round 6 tried 8 (draw calls 2,655 -> 1,501 on the BA03 replay)
    // and REGRESSED on a real GPU: per-frame BatchedMesh cost
    // (onBeforeRender, renderMultiDraw) and slower settles outweighed the
    // calls saved. localStorage bl_batch_max=<n> still overrides for an A/B.
    batchMaxInstances = defaultBatchMax(),
    // Groups of up to batchFoldMaxInstances rows also go into the batch
    // when rows x (triangles at the level it draws; the finest when that is
    // not known) <= batchFoldMaxTris: a batch copies the geometry per row,
    // so a heavy fragment keeps its pool and its instancing. See
    // defaultFoldMax and _batchEligible.
    batchFoldMaxInstances = defaultFoldMax(),
    batchFoldMaxTris = 8192,
    // A tile batch rebuilt because a revive did not fit is sized this much
    // larger (items, vertices, indices), so the tile's NEXT revive appends
    // in place. First builds stay exact: most tiles never grow.
    batchGrowHeadroom = 0.5,
    // The exporter's pre-merged singleton meshes (manifest
    // `singletonMerge`, per tile `merge`). A fragment used by exactly one
    // element in the whole model has nothing to instance against, so the
    // exporter bakes those into one mesh per tile per level of detail:
    // 58,215 elements over 1,039 tiles on the live SpaceX export. Only
    // medium and coarse are merged; up close the singletons stay
    // instanced. Off until a capture shows parity.
    singletonMerge = false,
    // What the merges may hold, in bytes.
    //
    // A merged mesh is not instanced and is not in the library cache, so
    // nothing evicts it: it lives as long as its tile. Fine merges are
    // 1.3 GB of geometry across this model, and a capture with merges on
    // reached 1,440 MB of heap before the renderer died on the next run.
    // Past this, a tile materialises its fragments instead - which is
    // slower per tile and cannot exhaust the browser.
    maxMergeBytes = 400 * 1024 * 1024,
    // A merged mesh is one object: it submits every triangle it holds,
    // whether or not the pixel floor wants the element drawn. So it is
    // only a good trade while most of its elements are above the floor.
    // Measured at an aerial view of SpaceX, 11.6% were - the merge
    // submitted 8.6x the triangles it needed. Below this fraction the
    // tile falls back to fragments, which skip what they do not draw.
    mergeMinDrawnFraction = 0.5,
  }) {
    if (!poolRegistry) {
      throw new Error('StreamingController: poolRegistry required (post-consolidation)');
    }
    this.poolRegistry = poolRegistry;
    this.fragmentsIndex = fragmentsIndex && fragmentsIndex.fragments ? fragmentsIndex.fragments : (fragmentsIndex || null);
    this.heavyFragments = new Set(Array.isArray(heavyFragments) ? heavyFragments : []);
    this.batching = !!batching;
    this.batchMaxInstances = batchMaxInstances;
    this.batchFoldMaxInstances = batchFoldMaxInstances;
    this.batchFoldMaxTris = batchFoldMaxTris;
    this._foldedGroups = 0;
    this._batchGrows = 0;
    this.singletonMerge = !!singletonMerge;
    this.maxMergeBytes = maxMergeBytes;
    this._mergeBytes = 0;
    this._mergeBudgetSkips = 0;
    // Why merges end up where they do. Every number the last capture
    // needed had to be inferred from two others; these say it outright.
    this._mergeKept = 0;
    this._mergeSortedSeen = 0;
    this._mergeLastError = '';
    this._mergeStaleDropped = 0;
    this.pinnedUids = null;
    // ELEMENTS SOMEBODY IS READING THE ROWS OF - a SECOND set, deliberately
    // not pinnedUids. Construction tracking owns pinnedUids and sets it
    // wholesale; the Project Files tree asks for the branch just opened. One
    // set shared by two owners means whichever called last silently cancels
    // the other, and neither can tell. See ensureUids().
    this.ensuredUids = null;
    this._ensuredTruncated = 0;
    this.drawEverything = false;
    this._mergeClaimed = 0;
    this._isolationChanges = 0;
    this._loadErrors = 0;
    this._lastLoadError = '';
    this.mergeMinDrawnFraction = mergeMinDrawnFraction;
    this._mergesRejected = 0;
    this._mergedTiles = 0;
    this._mergedItems = 0;
    this._mergeFailures = 0;
    this._mergeBoundsMissing = 0;
    this._staleMaterializations = 0;
    this._batchedTiles = 0;
    this._batchedItems = 0;
    this.clearanceState = clearanceState || createClearanceState(false);
    this.perInstancePixelSkip = perInstancePixelSkip;
    this.minFragmentPixels = minFragmentPixels;
    this.coneMinPx = coneMinPx;
    this.coneBudgetShare = coneBudgetShare;
    // WHAT THE REST PASSES' ANSWERS DEPEND ON, AS ONE NUMBER. Bumped
    // whenever geometry is installed, swapped, revived or unloaded, and
    // whenever a setting the size test reads changes. With the camera
    // still and this unchanged, the budget pass, the cone pass and the
    // per-tile rest checks would all compute exactly what they computed
    // last time - so they don't (see _restStampNow).
    this._layoutSeq = 0;
    // The subset of those changes that are INPUTS to the budget and cone
    // passes: tiles entering the count (loading start, unload), the focus
    // set, size settings. NOT the installs, swaps and revives those passes
    // themselves cause - recomputing on those made the admission sets
    // shift under their own effects and the rest pass oscillate (staging
    // BA03 headless, round 5: tiles retired and revived over and over at
    // rest, holes growing, ~1 swap/s; Tom: compactions every second).
    this._inputSeq = 0;
    this._budgetSeq = 0;
    this._coneSeq = 0;
    this._restSkips = 0;
    this._budgetSkips = 0;
    this.frustumShowSlotsPerTick = frustumShowSlotsPerTick;
    this.coneRefreshMs = coneRefreshMs;
    this._coneLastAt = -Infinity;
    this._coneMoveStamp = null;
    this.moveFarCap = moveFarCap;
    this._farRestore = null;
    this._coneAdmitted = null;
    this._coneStats = null;
    this.connectorPxBoost = connectorPxBoost;
    this.heavyFragmentPixels = heavyFragmentPixels;
    this.lodFinePx = lodFinePx;
    this.lodMediumPx = lodMediumPx;
    this.heavyFinePx = heavyFinePx;
    this.heavyMediumPx = heavyMediumPx;
    this.trisPerPx2 = trisPerPx2;
    this.quality = quality;
    this.materializeBudgetMs = materializeBudgetMs;
    this.materializeBudgetMsLoading = materializeBudgetMsLoading;
    const devGb = (typeof navigator !== 'undefined' && navigator.deviceMemory) ? navigator.deviceMemory : 0;
    this.maxLibraryBytes = maxLibraryBytes || (devGb ? devGb * 120 * 1024 * 1024 : 480 * 1024 * 1024);
    // The materialisation slice restarts every animation frame; a
    // yield is taken only when THIS frame's slice is already spent
    // (a slice that only reset on yields made every post-fetch call
    // yield once).
    const deviceBudget = StreamingController.deviceBudget();
    this.maxTrianglesPerFrame = maxTrianglesPerFrame == null ? deviceBudget.triangles : maxTrianglesPerFrame;
    this.maxDrawCalls = maxDrawCalls == null ? deviceBudget.drawCalls : maxDrawCalls;
    this.focusPxBoost = focusPxBoost;
    this._budgetCalls = 0;
    this.maxFetchesPerTile = maxFetchesPerTile;
    this.coldStartMs = coldStartMs;
    this.coldMaterializeBudgetMs = coldMaterializeBudgetMs;
    this.coldMaxFetchesPerTile = coldMaxFetchesPerTile;
    this.coldMaxConcurrentTileLoads = coldMaxConcurrentTileLoads;
    this.coldMaxConcurrentFetches = coldMaxConcurrentFetches;
    // Set by the first tile dispatch; null again once the window ends.
    this._coldStartAt = null;
    this._coldEndedAt = null;
    this._coldActive = false;
    this._coldRestoreFetches = null;
    this.batchGrowHeadroom = batchGrowHeadroom;
    this.restChecksPerTick = restChecksPerTick;
    this.forcedSwapsPerTick = forcedSwapsPerTick;
    this.focusTriangleCeiling = focusTriangleCeiling;
    this.coarsenWhileMoving = coarsenWhileMoving;
    this.progressiveFlushMs = progressiveFlushMs;
    this.progressiveChunkMs = progressiveChunkMs;
    this.progressiveMinItems = progressiveMinItems;
    this.globalOrder = globalOrder;
    this.reprioritizeMs = reprioritizeMs;
    this._lastReprioritizeAt = 0;
    this._reprioritizePending = false;
    this._progressiveChunks = 0;
    this._progressiveFlushes = 0;
    this.coarsenSwapsPerTick = coarsenSwapsPerTick;
    this._coarsenSwaps = 0;
    this.budgetRebalanceMs = budgetRebalanceMs;
    this.retireDwellMs = retireDwellMs;
    this.reviveMargin = reviveMargin;
    this.anticipateUnseen = anticipateUnseen;
    this.dynamicMinPx = 0;
    this._budgetLastAt = 0;
    this._budgetTris = 0;
    this._sliceStart = performance.now();
    this._budgetYields = 0;
    this._poolsDisposed = 0;
    if (typeof requestAnimationFrame === 'function') {
      const tickSlice = () => {
        this._sliceStart = performance.now();
        this._sliceFrame = (this._sliceFrame || 0) + 1;
        // Nothing tears the controller down explicitly; stop once the
        // viewer's canvas has left the document.
        const el = this.viewer && this.viewer.renderer && this.viewer.renderer.domElement;
        if (el && el.isConnected === false) { this._sliceRaf = null; return; }
        this._sliceRaf = requestAnimationFrame(tickSlice);
      };
      this._sliceRaf = requestAnimationFrame(tickSlice);
    }
    this._deferredHashCount = 0;
    this._revivedHashCount = 0;

    this.viewer = viewer;
    this.registry = registry;
    this.frustumTester = frustumTester;
    this.lodSelector = lodSelector;
    this.fetcher = fetcher;
    this.manifestLoader = manifestLoader;
    this.cache = cache;
    this.adapter = adapter;
    this.applicator = applicator;
    this.sceneRoot = sceneRoot;
    this.fetchTickMs = fetchTickMs;
    this.maxConcurrentTileLoads = maxConcurrentTileLoads;
    this.sidecarPrefetchRadius = sidecarPrefetchRadius;

    this.maxInstancedEntries = maxInstancedEntries;
    this.maxLibraryEntries = maxLibraryEntries;
    this.evictionTickMs = evictionTickMs;

    this.coverageThresholdPixels = coverageThresholdPixels;
    this.maxHiddenTimeMs = maxHiddenTimeMs;
    this.lodRestMs = lodRestMs;
    this.frustumPaddingFactor = frustumPaddingFactor;
    this.focusGuarantee = focusGuarantee !== false;
    this.focusRestMs = focusRestMs;
    this._focusTiles = new Set();
    this._focusStats = { tiles: 0, loading: 0, pending: 0, revived: 0, at: 0 };
    // Scratch AABB for tile frustum testing — cloned per-tile from
    // tile.box and inflated by the padding factor. Allocated once.
    this._paddedBox = new Box3();

    this._lastFetchTick = 0;
    this._lastEvictionTick = 0;
    this._activeTileLoads = 0;
    this._showOutliers = true;

    this._totalInstancedEvicted = 0;
    this._totalLibraryEvicted = 0;
    this._totalMissingHashesSkipped = 0;

    // Reusable per-frame scratch. Constructed once so the per-tile
    // coverage projection allocates nothing.
    this._viewProj = new Matrix4();
    this._tmpVec = new Vector3();

    // Camera-rest tracking. We stamp _lastCameraMoveTime whenever the
    // camera pose differs from the previous tick; LOD recompute only
    // fires after (now - _lastCameraMoveTime) ≥ lodRestMs.
    this._lastCamPos = new Vector3(NaN, NaN, NaN);
    this._lastCamQuatX = NaN;
    this._lastCamQuatY = NaN;
    this._lastCamQuatZ = NaN;
    this._lastCamQuatW = NaN;
    this._lastCameraMoveTime = 0;

    // Per-tile timestamp of the last frame the tile was ABOVE the
    // coverage threshold. Used by the unload-after-hidden gate. We
    // keep this in a controller-side Map rather than mutating Tile
    // objects so the registry stays a plain data structure.
    this._tileLastAboveThresholdMs = new Map();

    // Stats counters (extend the existing pair).
    this._totalCoverageUnloaded = 0;

    // Priority-boost hint for the tile dispatch queue. Set via
    // viewer.streaming.prefetchTiles(tileKeys) — a caller that KNOWS
    // which tiles the user is about to look at (e.g. the issue
    // resolve page's candidate-fix elements) pushes those keys in
    // here and the tick() sort floats them to the front, keeping
    // the coverage/hysteresis/LOD gates intact. Does NOT bypass
    // frustum gating — a boosted tile still has to be a legitimate
    // load candidate to dispatch.
    this._priorityTileKeys = new Set();
  }

  // Called by viewer.streaming.prefetchTiles(tileKeys). Additive —
  // multiple hints from different code paths stack until the caller
  // clears them. clearPrefetch() empties the set.
  addPriorityTiles(tileKeys) {
    if (!Array.isArray(tileKeys)) return;
    for (const k of tileKeys) if (typeof k === 'string' && k) this._priorityTileKeys.add(k);
  }
  clearPriorityTiles() { this._priorityTileKeys.clear(); }

  // Tile dispatch order. See tick() for the sort that consumes this.
  // 'near' = nearest tiles dispatch first (default, original behavior).
  // 'far'  = farthest tiles dispatch first (exterior shell paints first).
  setLoadOrder(order) {
    this._loadOrderFarFirst = (order === 'far');
    console.log('[StreamingController] load order:', this._loadOrderFarFirst ? 'FAR FIRST (exterior shell)' : 'NEAR FIRST (default)');
    return this._loadOrderFarFirst ? 'far' : 'near';
  }
  getLoadOrder() {
    return this._loadOrderFarFirst ? 'far' : 'near';
  }

  setShowOutliers(show) {
    const next = !!show;
    if (next === this._showOutliers) return;
    this._showOutliers = next;

    if (!next) {
      this.registry.forEach(tile => {
        if (!tile.inTrimmedBounds && tile.state === TileState.VISIBLE) {
          this._unload(tile, 'outliers');
        }
      });
    }
  }

  getShowOutliers() { return this._showOutliers; }

  _tileDistance(tile, cameraPos) {
    return tile.box.distanceToPoint(cameraPos);
  }

  // Estimate the on-screen pixel area of a tile's bounding box.
  //
  // Projects all 8 corners of `tile.box` through the precomputed
  // view+projection matrix (`this._viewProj`, refreshed once per tick),
  // does the perspective divide manually so we can reject corners
  // behind the camera before they produce flipped NDC coords, clips
  // the resulting AABB in NDC to [-1, 1]², and converts to pixels
  // using the current canvas size.
  //
  // Returns 0 for tiles that are entirely behind the camera or whose
  // projected AABB collapses outside the screen. Conservative on
  // tiles that straddle the near plane: when some corners are in
  // front and some behind, we use only the in-front corners, which
  // tends to OVERESTIMATE area for close foreground tiles. That's the
  // safe direction — we'd rather fetch a tile we don't need than miss
  // one we do.
  //
  // Cost: 8 dot products + a clip clamp per tile. For 850 tiles per
  // frame this is well under a millisecond on a modern machine, much
  // cheaper than the network fetch we're gating.
  _estimateCoverageInPixels(tile, viewportWidth, viewportHeight) {
    const box = tile.box;
    if (!box) return 0;

    const e = this._viewProj.elements;
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let anyInFront = false;

    // Iterate the 8 corners inline. Beats Vector3 allocation per
    // corner; this method runs N×M times per frame.
    for (let cx = 0; cx < 2; cx++) {
      const x = cx ? box.max.x : box.min.x;
      for (let cy = 0; cy < 2; cy++) {
        const y = cy ? box.max.y : box.min.y;
        for (let cz = 0; cz < 2; cz++) {
          const z = cz ? box.max.z : box.min.z;

          // Manual viewProj × (x,y,z,1). We pull w out so we can reject
          // behind-camera corners (w ≤ 0) before the perspective divide
          // produces nonsense. Three.js's Vector3.applyMatrix4 does the
          // divide unconditionally, which corrupts NDC for w ≤ 0 — bad
          // for any tile that straddles the camera.
          const w = e[3] * x + e[7] * y + e[11] * z + e[15];
          if (w <= 0) continue;
          const invW = 1 / w;
          const ndcX = (e[0] * x + e[4] * y + e[8] * z + e[12]) * invW;
          const ndcY = (e[1] * x + e[5] * y + e[9] * z + e[13]) * invW;

          anyInFront = true;
          if (ndcX < minX) minX = ndcX;
          if (ndcX > maxX) maxX = ndcX;
          if (ndcY < minY) minY = ndcY;
          if (ndcY > maxY) maxY = ndcY;
        }
      }
    }

    if (!anyInFront) return 0;

    // Clip to the visible NDC range. A bbox can project to extents
    // larger than the screen (e.g. tile fills more than the viewport);
    // clipping to [-1, 1] gives us the on-screen portion.
    if (minX < -1) minX = -1;
    if (maxX >  1) maxX =  1;
    if (minY < -1) minY = -1;
    if (maxY >  1) maxY =  1;

    if (maxX <= minX || maxY <= minY) return 0;

    // NDC extent ∈ [0, 2] maps to viewport pixels ∈ [0, viewportW|H].
    const widthPx  = (maxX - minX) * 0.5 * viewportWidth;
    const heightPx = (maxY - minY) * 0.5 * viewportHeight;
    return widthPx * heightPx;
  }

  // Update the camera-rest timestamp. Returns true iff the camera is
  // currently AT rest (still for ≥ lodRestMs). This is the gate for
  // per-tile LOD swaps.
  _updateCameraRest(now) {
    const cam = this.viewer.camera;
    const p = cam.position;
    const q = cam.quaternion;
    const moved =
      p.x !== this._lastCamPos.x ||
      p.y !== this._lastCamPos.y ||
      p.z !== this._lastCamPos.z ||
      q.x !== this._lastCamQuatX ||
      q.y !== this._lastCamQuatY ||
      q.z !== this._lastCamQuatZ ||
      q.w !== this._lastCamQuatW;

    if (moved) {
      this._lastCamPos.copy(p);
      this._lastCamQuatX = q.x;
      this._lastCamQuatY = q.y;
      this._lastCamQuatZ = q.z;
      this._lastCamQuatW = q.w;
      this._lastCameraMoveTime = now;
      return false;
    }
    return (now - this._lastCameraMoveTime) >= this.lodRestMs;
  }

  // Fire-and-forget parallel sidecar prefetch for every in-bounds tile.
  // Called once from the loader right after the registry is hydrated
  // from the manifest, BEFORE the tick loop starts dispatching.
  //
  // Why this matters: without it, the tick loop's `_beginLoad` would
  // serialize sidecar fetches inside each concurrent slot — 16 in
  // flight at a time, ~53 rounds for an 850-tile project, every round
  // paying a full RTT on a tiny JSON payload. The HTTP/2 stream limit
  // is far higher than 16, so launching ALL sidecar requests in one
  // shot lets the multiplex finish in roughly one RTT-batch instead.
  //
  // ManifestLoader.loadSidecar already dedupes by URL (in-flight map),
  // so by the time `_beginLoad` later does its own `loadSidecar` call
  // for a tile, it joins the in-flight promise from this prefetch —
  // no duplicate request.
  //
  // We DON'T await any individual promise here — fire-and-forget is
  // the whole point. tick() reads tile.instancesByHash to know when a
  // sidecar has landed (`_loadSidecar` populates it via registry
  // hydrate); tiles whose sidecars are still in-flight just queue and
  // wait for their turn.
  prefetchAllSidecars() {
    let dispatched = 0;
    this.registry.forEach(tile => {
      if (!tile.inTrimmedBounds && !this._showOutliers) return;
      if (tile.instancesByHash) return;
      if (!tile.sidecarUrl) return;
      // Kick off but don't await. _loadSidecar handles state and
      // hydration; the in-flight dedup in ManifestLoader prevents
      // duplicate work if tick reaches the same tile concurrently.
      this._loadSidecar(tile, this._sidecarDemand(tile, true)).catch(err => {
        if (!(err && err.cancelled)) console.warn(`[StreamingController] prefetch sidecar ${tile.key} failed`, err);
        // CRITICAL: reset the tile state back to IDLE so the tick
        // loop's enqueue branch picks it up for a retry via
        // _beginLoad. _loadSidecar sets state to SIDECAR_LOADING at
        // its top; if the underlying fetch throws, state stays at
        // SIDECAR_LOADING with NO instancesByHash. The tick's
        // enqueue condition requires SIDECAR_LOADING + instancesByHash
        // OR IDLE/UNLOADED — so without this reset, a failed-prefetch
        // tile is permanently orphaned (geometry never appears in the
        // scene). This was the "model not fully downloading" symptom
        // observed 2026-05-13.
        if (tile.state === TileState.SIDECAR_LOADING && !tile.instancesByHash) {
          tile.state = TileState.IDLE;
        }
      });
      dispatched++;
    });
    return dispatched;
  }

  async prefetchNearbySidecars() {
    if (this.sidecarPrefetchRadius == null) return;
    const camera = this.viewer.camera.position;

    const candidates = [];
    this.registry.forEach(tile => {
      if (!tile.inTrimmedBounds && !this._showOutliers) return;
      const d = this._tileDistance(tile, camera);
      if (d <= this.sidecarPrefetchRadius) candidates.push({ tile, distance: d });
    });
    candidates.sort((a, b) => a.distance - b.distance);

    for (const { tile } of candidates) {
      try { await this._loadSidecar(tile); } catch (err) {
        if (!(err && err.cancelled)) console.warn(`[StreamingController] prefetch sidecar ${tile.key} failed`, err);
      }
    }
  }

  // One streaming tick. The fragment-index memo (see _metaOf) is on for its
  // duration only: nothing changes the index inside a tick.
  tick() {
    this._metaMemoOn = true;
    try {
      return this._tick();
    } finally {
      this._metaMemoOn = false;
      this._metaLastHash = undefined;
      this._metaLast = null;
    }
  }

  _tick() {
    // Refresh the camera frustum once per tick — drives the tile-level
    // hide / unload gate below. Each VISIBLE tile's AABB is tested
    // against a padded frustum (frustumPaddingFactor); inside → render,
    // outside → soft-hide immediately + start the hiddenWindow timer
    // for eventual unload. Coverage is no longer the unload signal.
    this.frustumTester.update();

    const now = performance.now();
    const canDispatch = (now - this._lastFetchTick) >= this.fetchTickMs;
    if (canDispatch) this._lastFetchTick = now;

    // Refresh the view+projection matrix once per tick. Diagnostics +
    // coverage projection (stats-only now) read from this.
    const cam = this.viewer.camera;
    this._viewProj.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);

    const cameraAtRest = this._updateCameraRest(now);
    // THE FAR PLANE COMES BACK BEFORE ANYTHING MEASURES THE VIEW. It used to
    // be restored at the END of the first rest tick, after the budget pass
    // and the cone pass had already sized the view through the 200 ft
    // moving frustum: on an aerial that is nothing, so the rebalance
    // reported 0 triangles / 2 calls, dropped the floor to 0, and the rest
    // passes started reviving the whole model (Tom, real GPU 2026-10-13:
    // budget 0.0M/2c after every orbit, then 173-226 M triangles).
    if (cameraAtRest && this._farRestore != null) {
      this._applyMoveFar(true);
      this.frustumTester.update();
      this._viewProj.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    }
    // The camera moved: what is largest on screen changed, so re-rank the
    // downloads still waiting for a slot (throttled; see globalOrder).
    if (this.globalOrder && this.fetcher && typeof this.fetcher.reprioritize === 'function') {
      if (this._lastCameraMoveTime > this._lastReprioritizeAt) this._reprioritizePending = true;
      if (this._reprioritizePending && now - this._lastReprioritizeAt >= this.reprioritizeMs) {
        this._lastReprioritizeAt = now;
        this._reprioritizePending = false;
        try { this.fetcher.reprioritize(); } catch (e) { /* ordering is best effort */ }
        try { if (this.manifestLoader && this.manifestLoader.reprioritize) this.manifestLoader.reprioritize(); } catch (e) { /* best effort */ }
      }
    }
    const hiddenWindow = this.maxHiddenTimeMs;
    const padFactor = this.frustumPaddingFactor;

    const cameraPos = this.viewer.camera.position;
    if (cameraAtRest) this._rebalanceTriangleBudget(now, cameraPos, this._focalPx());
    // Then the cone exemption. Order is NOT what protects the budget here -
    // a mutation proved that, by running this first and changing nothing.
    // THE PROTECTION IS INDEPENDENCE: _admitCone writes only _coneAdmitted
    // and _coneStats, and _rebalanceTriangleBudget reads neither. If anyone
    // ever makes the budget consult the cone set, ordering will not save
    // them - coneRule.test.js asserts the independence directly.
    // Throttled (_coneDue): a full pass over every group in view is far too
    // expensive to repeat every 16 ms tick for an answer that has not moved.
    if (cameraAtRest && this._coneDue(now)) this._admitCone(cameraPos, this._focalPx());
    this._applyMoveFar(cameraAtRest);
    // Per-tick budgets for the rest-window work (SpaceX, real GPU:
    // render 2-18 ms but 300-900 ms of main-thread time per frame,
    // growing with visible tiles). A forced re-materialisation of a
    // 16k-row tile is ~50-100 ms synchronous; the group scans are
    // ~100k _pxOf/_lodForHash calls per pass. Both are now spread over
    // frames: at most restChecksPerTick tiles are examined per tick
    // (round-robin through the 500 ms per-tile throttle) and at most
    // forcedSwapsPerTick tiles are re-materialised per tick.
    let restChecks = 0;
    let showSlots = 0;
    // Settled: nothing loading and the camera still for 2 s.
    const restSettled = cameraAtRest && !((this._activeTileLoads || 0) > 0)
      && now - (this._lastCameraMoveTime || 0) >= 2000;
    const restStamp = cameraAtRest ? this._restStampNow() + (restSettled ? '|S' : '|L') : null;
    let forcedSwaps = 0;
    let coarsenSwaps = 0;
    const queue = [];
    const toUnload = [];
    // The focus set is rebuilt once the camera has been still long
    // enough to mean it; rebuilding it mid-pan would drag the whole
    // sweep into the guarantee.
    const rebuildFocus = this.focusGuarantee && cameraAtRest
      && (now - (this._lastCameraMoveTime || 0)) >= this.focusRestMs;
    const nextFocus = rebuildFocus ? new Set() : null;
    let focusPending = 0;

    this.registry.forEach(tile => {
      // Outliers (out-of-trimmed-bounds) only stream when the debug
      // "show outliers" toggle is on. Unchanged.
      if (!tile.inTrimmedBounds && !this._showOutliers) return;

      const inFrustum = this._isTileInPaddedFrustum(tile, padFactor);

      if (inFrustum) {
        // Refresh the "last seen relevant" stamp. Used by the unload
        // gate below to decide when a tile has been irrelevant long
        // enough to drop.
        this._tileLastAboveThresholdMs.set(tile.key, now);
        // The guarantee's set, built here rather than in a pass of its
        // own: this tile's frustum test is already paid for, this tick.
        if (rebuildFocus) {
          nextFocus.add(tile.key);
          if (tile.state !== TileState.VISIBLE) focusPending++;
          else if (tile._deferredHashes && tile._deferredHashes.size) focusPending++;
        }
      }

      if (tile.state === TileState.VISIBLE) {
        if (!inFrustum) {
          // Soft-hide a VISIBLE tile that's left the padded frustum.
          // hideSlots zeros the LIVE instanceMatrix on the tile's claimed
          // pool slots but leaves the snapshotMatrices intact, so re-show
          // is a single setMatrixAt loop (no fetch, no re-materialize,
          // no slot churn). The tile also drops out of each pool's
          // activeTiles set; when no tile contributes unhidden slots the
          // pool's mesh.visible flips to false → entire pool draw skipped.
          //
          // The historical concerns (zoom/rotate dissolve, single-owner-
          // uid vanish on full _unload) were caused by:
          //   * the WRONG per-pool boundingSphere when frustumCulled=true
          //     (zero-scaled released slots pulling sphere toward origin)
          //   * full _unload after hiddenWindow firing on single-owner
          //     uids
          // Neither applies here: we're using the per-TILE AABB ∩ padded-
          // frustum test (correct even when the AABB contains the camera,
          // and direction-independent under rotation since the padded
          // frustum includes the camera position), and we're soft-hiding
          // (slots stay claimed, no uid promotion).
          this._frustumHide(tile);
          return;
        }

        // In-frustum. If a previous tick soft-hid this tile (legacy from
        // before this patch — _frustumHidden=true set before unload),
        // call _frustumShow to restore. After this patch ships and the
        // flag is never set, this branch is a no-op.
        // RE-SHOWN A SLICE AT A TIME. Coming back from an orbit (or from
        // the short moving far plane) puts most of the model back in view
        // in ONE tick, and restoring it all at once was a 13-30 s freeze
        // on BA03 (real GPU, 2026-10-13). frustumShowSlotsPerTick slots
        // per tick, at least one tile; the rest wait - still hidden, so
        // nothing draws half-stated - for the next tick.
        if (tile._frustumHidden) {
          const cap = this.frustumShowSlotsPerTick;
          if (cap > 0 && showSlots > 0 && showSlots >= cap) { this._frustumShowDeferred = (this._frustumShowDeferred || 0) + 1; return; }
          showSlots += this._claimedSlotCount(tile);
          this._frustumShow(tile);
          if (tile._frustumHidden) return;
        }

        // VISIBLE + on-screen. Only swap LOD if the camera has been
        // still long enough — drag-orbits should not trigger constant
        // LOD churn.
        if (!cameraAtRest) {
          // Moving: shed detail the distance no longer justifies (see
          // `coarsenWhileMoving`). Only coarser, only a couple of tiles
          // per tick, and _applyLodSwap still refuses any swap whose
          // libraries are not already cached.
          if (this.coarsenWhileMoving && coarsenSwaps < this.coarsenSwapsPerTick
              && tile.lod != null) {
            const dist = this._tileDistance(tile, cameraPos);
            const want = this.lodSelector.select(dist, tile.lod);
            if (want > tile.lod) {
              coarsenSwaps++;
              this._coarsenSwaps = (this._coarsenSwaps || 0) + 1;
              this._applyLodSwap(tile, want, false, 'coarsen');
            }
          }
          return;
        }

        if (cameraAtRest) {
          const distance = this._tileDistance(tile, cameraPos);
          const targetLod = this.lodSelector.select(distance, tile.lod);
          if (targetLod !== tile.lod) {
            this._applyLodSwap(tile, targetLod, false, 'lod');
          }
          // Deferral both ways, throttled per tile: retire hashes that
          // no longer pass the size test (re-materialise at the same
          // LOD), then bring back deferred ones that now pass.
          // Skipped outright when nothing it reads has changed since this
          // tile last came out clean (camera, installed geometry, floor,
          // budget and cone passes): it re-derived every group of every
          // tile about once a second at rest (24 tiles a tick).
          if (this.fragmentsIndex && restStamp !== null && tile._restStamp === restStamp && tile._deferredCheckedAt) {
            this._restSkips = (this._restSkips || 0) + 1;
          } else if (this.fragmentsIndex && restChecks < this.restChecksPerTick
              && now - (tile._deferredCheckedAt || 0) > 500) {
            tile._restStamp = null;
            restChecks++;
            tile._deferredCheckedAt = now;
            const focalPx = this._focalPx();
            // Everything in this tile loads if the camera is pointing at
            // it, so this pass neither defers nor retires for size.
            const focus = this.inFocus(tile);
            // A merged tile's elements move in and out of the floor with
            // the camera exactly as fragments do, and re-testing them is
            // a texture write rather than a re-materialise. What it
            // cannot fix is the merge becoming a bad trade as the camera
            // pulls back: the mesh submits every triangle it holds, so a
            // tile whose merge has fallen below the threshold is sent
            // back through materialisation, which drops it in favour of
            // fragments.
            let mergeWantsRetire = false;
            if (tile._merge) {
              const drawnNow = this._applyMergeSizes(tile, cameraPos, focalPx);
              const items = tile._merge.itemCount || 0;
              mergeWantsRetire = !tile._merge.sortedByRadius && items > 0
                && (drawnNow / items) < this.mergeMinDrawnFraction;
            }
            // Count the groups whose deferral or LOD would change; a
            // tile is only re-materialised when the change is worth its
            // cost (hysteresis: >= 2 % of its groups, minimum one).
            let changed = 0;
            let groups = 0;
            // WHY the groups would change, for the thrash ledger's retire
            // reason (thrashLedger.js). Bookkeeping only.
            let why = null;
            const coneDrops = this._coneDropped ? this._coneDropped.get(tile.key) : null;
            const noteWhy = (w) => { why = why || {}; why[w] = (why[w] || 0) + 1; };
            tile.instancesByHash.forEach((inst, hash) => {
              groups++;
              if (tile._deferredHashes && tile._deferredHashes.has(hash)) return;
              const gd = this._groupDistance(inst, cameraPos);
              this._deferWhy = null;
              if (this._shouldDefer(hash, gd, focalPx, inst, focus, tile.key)) {
                changed++;
                noteWhy(coneDrops && coneDrops.has(hash) ? 'cone' : (this._deferWhy || 'size'));
                return;
              }
              const claim = tile._poolClaims && tile._poolClaims.get(hash);
              if (claim && typeof claim.lod === 'number') {
                // Same fallback the forced swap uses (tile.lod), so the
                // check and the swap cannot disagree and loop.
                const want = this._lodForHash(hash, gd, focalPx, tile.lod == null ? targetLod : tile.lod, this._libraryFor(tile, hash));
                if (want !== claim.lod) { changed++; noteWhy('lod'); }
              } else {
                // A group in the tile's batch has a level too, fixed when the
                // batch was built. Round 9 folds groups of up to nine rows in
                // there, so a batched group left at the level it was built at
                // is no longer a rounding error: the plan counts the level it
                // WANTS. Only with its library in hand, so the swap can build
                // what this asks for (else the two disagree and loop).
                const blod = this._batchLodOf(tile, hash);
                const lib = blod != null ? this._libraryFor(tile, hash) : null;
                if (lib) {
                  const want = this._lodForHash(hash, gd, focalPx, tile.lod == null ? targetLod : tile.lod, lib);
                  if (want !== blod) { changed++; noteWhy('lod'); }
                }
              }
            });
            if (mergeWantsRetire) { changed = Math.max(changed, Math.max(1, Math.ceil(groups * 0.02))); noteWhy('merge'); }
            // THE SETTLED TIDY. While loading, a tile is re-materialised only
            // when >= 2 % of its groups would change (each costs 50-100 ms).
            // Once the view has settled that threshold left one or two stale
            // groups per tile drawn for good - small, but often each its own
            // pool, i.e. its own draw call: Tom, round 7 at rest, ~2x the
            // planned calls. Settled, one group is enough; it is a one-time
            // rebuild per tile, spread by forcedSwapsPerTick.
            const retire = changed >= (restSettled ? 1 : Math.max(1, Math.ceil(groups * 0.02)));
            // Churn guard: a static camera should converge in one pass.
            // A tile that keeps asking to be re-materialised (SpaceX:
            // 21k forced swaps in one run, pools doubling before the old
            // ones were disposed) backs off - 5 s after a swap, doubling
            // up to 60 s while the answer keeps being "swap again".
            const sinceSwap = now - (tile._lastForcedSwapAt || 0);
            const backoff = restSettled
              ? Math.min(8000, 1000 * Math.pow(2, tile._swapStreak || 0))
              : Math.min(60000, 5000 * Math.pow(2, tile._swapStreak || 0));
            // THE DWELL (retireDwellMs, round 11): a tile built moments ago
            // is not rebuilt for a derived reason - the floor, the cone or
            // the size test - until it has been up for the dwell. Checked
            // again when the dwell ends, against the floor of that moment.
            const dwellLeft = retire ? this._retireDwellLeft(tile, why, now) : 0;
            if (dwellLeft > 0) {
              this._dwellHolds = (this._dwellHolds || 0) + 1;
              tile._deferredCheckedAt = now + dwellLeft - 500;
            } else if (retire && sinceSwap < backoff) {
              tile._deferredCheckedAt = now + backoff - sinceSwap - 500;
            } else if (retire && forcedSwaps < (restSettled ? Math.min(1, this.forcedSwapsPerTick) : this.forcedSwapsPerTick)) {
              forcedSwaps++;
              tile._swapStreak = sinceSwap < 30000 ? (tile._swapStreak || 0) + 1 : 0;
              tile._lastForcedSwapAt = now;
              let top = null;
              if (why) for (const k of Object.keys(why)) if (top === null || why[k] > why[top]) top = k;
              this._applyLodSwap(tile, tile.lod, true, top ? `retire:${top}` : 'retire');
              if (coneDrops) this._coneDropped.delete(tile.key);
              this._retiredPasses = (this._retiredPasses || 0) + 1;
            } else if (retire) {
              // Over this tick's swap budget: try again next window.
              tile._deferredCheckedAt = now - 400;
            } else if (tile._deferredHashes && tile._deferredHashes.size) {
              tile._swapStreak = 0;
              tile._restStamp = restStamp;
              this._reviveDeferred(tile, distance, focalPx).catch(err => {
                console.warn('[StreamingController] revive failed for', tile.key, err && err.message);
              });
            } else {
              tile._restStamp = restStamp;
            }
          }
        }
        return;
      }

      // Non-visible tile: enqueue UNCONDITIONALLY. Even tiles outside
      // the current padded frustum get fetched so a small camera pan
      // doesn't have to wait on the network — the frustum gate fires
      // at the RENDER tier (hide/unload), not at the fetch tier.
      // Dispatch is still capped by `maxConcurrentTileLoads`.
      // Nothing in this tile is inside the isolation. Its sidecar has
      // landed, so this is known rather than guessed.
      if (this._tileIsolatedOut(tile)) return;
      if (this._dispatchable(tile)) {
        const distance = this._tileDistance(tile, cameraPos);
        queue.push({ tile, distance });
      }
    });

    // Unload first (frees concurrency slots before we dispatch new
    // loads). _unload releases pool slots and unregisters from the
    // adapter; cached library entries stay and the LRU eviction below
    // reclaims memory eventually.
    if (rebuildFocus) {
      const prevFocus = this._focusTiles;
      let focusChanged = !prevFocus || prevFocus.size !== nextFocus.size;
      if (!focusChanged) for (const k of nextFocus) { if (!prevFocus.has(k)) { focusChanged = true; break; } }
      if (focusChanged) this._bumpInput();
      this._focusTiles = nextFocus;
      this._focusStats = {
        tiles: nextFocus.size,
        loading: this._activeTileLoads || 0,
        pending: focusPending,
        revived: this._focusStats ? this._focusStats.revived : 0,
        at: now,
      };
    }
    // (Nothing pushes into toUnload today: an out-of-view tile is soft-hidden
    // and keeps its geometry. Tagged so the ledger would say if it came back.)
    for (const tile of toUnload) {
      this._unload(tile, 'frustum');
      this._tileLastAboveThresholdMs.delete(tile.key);
      this._totalCoverageUnloaded++;
    }

    if (canDispatch && queue.length > 0) {
      // Tile dispatch order. Tunable at runtime via
      // viewer.streaming.setLoadOrder('near' | 'far'):
      //   'far'  → exterior shell first, central detail last
      //          (user-suggested 2026-05-13: outer tiles paint
      //          first so the user sees the building outline
      //          immediately; dense central tiles fill in last).
      //   'near' → original behavior, nearest tiles first.
      //
      // On top of that, priority tiles (viewer.streaming
      // .prefetchTiles([...]) — used by the issue resolve page to
      // boost the tiles that carry candidate-fix elements) float to
      // the front regardless of distance.
      const farFirst = this._loadOrderFarFirst === true;
      const priority = this._priorityTileKeys;
      const hasPriority = priority.size > 0;
      queue.sort((a, b) => {
        if (hasPriority) {
          const ap = priority.has(a.tile.key) ? 1 : 0;
          const bp = priority.has(b.tile.key) ? 1 : 0;
          if (ap !== bp) return bp - ap;
        }
        // Cost-aware: among tiles at similar distance, the cheap one
        // first. A 17k-row / 2.2k-fragment leaf (SpaceX 2/5/5/5/5/5/*)
        // that starts before its 100-row siblings holds the fetch slots
        // for a minute while they starve behind it.
        // Distinct fragments are the fetches a tile needs (bytes as the
        // tie-break); rows only when the export predates the facts.
        const cost = (t) => 1 + Math.log10(1 + (t.hashCount || t.instanceCount || 0)) + (t.fragmentBytes ? Math.log10(1 + t.fragmentBytes / 1048576) * 0.25 : 0);
        const ka = a.distance * cost(a.tile);
        const kb = b.distance * cost(b.tile);
        return farFirst ? (kb - ka) : (ka - kb);
      });
      const tileLoadCap = this._tileLoadCap();
      for (const { tile, distance } of queue) {
        if (this._activeTileLoads >= tileLoadCap) break;
        this._beginLoad(tile, distance);
      }
    }

    // Compaction is housekeeping, not a reaction: at most every 10 s, only
    // with the camera still for 2 s and nothing installed or swapped for
    // 2 s, and only when the holes add up (see _compactPools). Round 5 ran
    // it every second and chased holes that the rest pass kept creating.
    // (Round 7 also waited for 2 s with no install or swap, and the
    // settled tidy's steady trickle of swaps kept that from ever holding:
    // 35-72k holes on Tom's runs, 0 compactions. A swap is synchronous, so
    // between ticks no index is in flight; _compactPools itself refuses
    // while anything loads or revives.)
    if (restSettled && this.compactPools !== false
        && now - (this._lastCompactAt || 0) >= (this.compactEveryMs || 10000)) {
      this._lastCompactAt = now;
      try { this._compactPools(); } catch (e) { console.warn('[StreamingController] pool compaction failed', e && e.message); }
    }

    if ((now - this._lastEvictionTick) >= this.evictionTickMs) {
      this._lastEvictionTick = now;

      const instEvicted = this.cache.evictInstanced(
        this.maxInstancedEntries,
        isInstancedEntryInUse
      );
      if (this.poolRegistry && typeof this.poolRegistry.disposeEmptyPools === 'function') {
        this._poolsDisposed += this.poolRegistry.disposeEmptyPools(now, 5000);
      }
      // A library a tile BATCH draws from is in use too: evicting it left
      // the batch unable to be rebuilt, which is what made the retire swap
      // refuse itself (round 4). Collected once per eviction tick.
      const batchHashes = new Set();
      this.registry.forEach((t) => {
        if (!t._batch) return;
        this._inTileBatch(t, '');
        if (t._batch._hashes) t._batch._hashes.forEach((h) => batchHashes.add(h));
      });
      const libEvicted = this.cache.evictLibrary(this._libraryEntryCap(), this.maxLibraryBytes, (h) => this._libraryInUse(h) || batchHashes.has(h));

      this._totalInstancedEvicted += instEvicted;
      this._totalLibraryEvicted += libEvicted;

      // Watchdog also runs at the slower eviction cadence (1 Hz) since
      // its threshold is ~45s — checking every tick (60 Hz) wastes CPU.
      this._watchdogStuckTiles(now);
    }
  }

  // May this tile be handed to _beginLoad right now?
  //
  // The state test alone is not enough. A tile's state does not become
  // FRAGMENTS_LOADING until _materializeTile runs, which sits behind
  // _beginLoad's first await, so between dispatch and that line the tile
  // still looks idle and the next tick dispatches it AGAIN. Each run
  // bumps the materialisation epoch and invalidates the one before it,
  // and the load slots fill with duplicates of a few tiles.
  //
  // With merges on, the extra fetch and parse widens that window from
  // milliseconds to hundreds of them, which is why three captures froze
  // at the same 237 tiles: every merge was loaded, parsed, and then
  // thrown away by a newer run of its own tile - not kept, not
  // rejected, not failed, so no counter moved.
  // Is this tile one the camera is pointing at? The frustum test the
  // tick already ran, remembered per rest window.
  // A tile's sidecar request. The background prefetch of every sidecar
  // ranks below anything a tile about to be drawn asks for (1e9 behind),
  // nearest first; a load asks with what the camera points at first. The
  // SIDECAR is what stood between the first tiles and their geometry: 1,377
  // of them queued first-come-first-served, the tiles in view behind the
  // far ones (SpaceX, 2026-09-17: no pack requested in 90 s).
  _sidecarDemand(tile, background) {
    if (!this.globalOrder) return 0;
    return {
      rank: () => {
        const cam = this.viewer && this.viewer.camera;
        const d = cam ? this._tileDistance(tile, cam.position) : 0;
        const dist = Number.isFinite(d) ? d : 1e8;
        return background ? 1e9 + dist : (this.inFocus(tile) ? -1e7 : 0) + dist;
      },
      isStale: () => false,
    };
  }

  // A download request for one fragment of one materialisation run: ranked
  // by how large the fragment is on screen right now (lower = sooner; what
  // the camera points at ahead of everything), stale once the run is.
  // Cheap on purpose - reprioritize() calls rank() for every queued
  // download - so it uses the tile's distance, not every instance's.
  _fetchDemand(tile, epoch, hash) {
    if (!this.globalOrder) return this._fetchPriority(tile);
    return {
      rank: () => {
        const cam = this.viewer && this.viewer.camera;
        if (!cam) return 0;
        const d = this._tileDistance(tile, cam.position);
        const px = rankPx(this, hash, d, this._focalPx());
        const size = px == null ? 0 : px;
        return (this.inFocus(tile) ? -1e7 : 0) - size + (Number.isFinite(d) ? d * 1e-6 : 0);
      },
      isStale: () => (epoch != null && tile._materializeEpoch !== epoch) || tile.state === TileState.UNLOADED,
    };
  }

  // Lower = fetched sooner. A tile in view outranks every tile outside
  // it; within each, the nearer tile goes first.
  _fetchPriority(tile) {
    const d = this.viewer && this.viewer.camera
      ? this._tileDistance(tile, this.viewer.camera.position) : 0;
    return (this.inFocus(tile) ? 0 : 1e9) + (Number.isFinite(d) ? Math.max(0, d) : 1e8);
  }

  inFocus(tile) {
    if (!this.focusGuarantee || !tile) return false;
    return this._focusTiles.has(tile.key);
  }

  // What the guarantee covers and what it still owes, so "is the view
  // complete" has an answer rather than being a judgement of a
  // screenshot. Read by the viewer's perf snapshot and by the tests.
  focusStatus() {
    return { ...this._focusStats, guarantee: this.focusGuarantee, tiles: this._focusTiles.size };
  }

  setFocusGuarantee(on) {
    this.focusGuarantee = !!on;
    if (!on) this._focusTiles.clear();
  }

  // --- COLD-START BURST -------------------------------------------
  // See the `coldStartMs` option. Opens on the first tile dispatch,
  // closes on the timer or as soon as the queue drains, and every
  // burst value is read through the helpers below so there is one
  // place where the window is honoured.

  _coldStartBegin() {
    if (this._coldStartAt !== null || this._coldEndedAt !== null) return;
    if (!(this.coldStartMs > 0)) return;
    this._coldStartAt = performance.now();
    this._coldActive = true;
    const f = this.fetcher;
    if (f && typeof f.setMaxConcurrentFetches === 'function'
        && this.coldMaxConcurrentFetches > f.maxConcurrentFetches) {
      this._coldRestoreFetches = f.maxConcurrentFetches;
      f.setMaxConcurrentFetches(this.coldMaxConcurrentFetches);
    }
  }

  _coldStartEnd() {
    if (!this._coldActive) return;
    this._coldActive = false;
    this._coldEndedAt = performance.now();
    const f = this.fetcher;
    if (f && this._coldRestoreFetches !== null && typeof f.setMaxConcurrentFetches === 'function') {
      f.setMaxConcurrentFetches(this._coldRestoreFetches);
    }
    this._coldRestoreFetches = null;
  }

  // True while the burst window is open. Also the place the window
  // closes: on the timer, or early when no tile is loading any more
  // (the first view is complete, so the budgets go back to protecting
  // interaction rather than throughput).
  inColdStart() {
    if (!this._coldActive) return false;
    const elapsed = performance.now() - this._coldStartAt;
    if (elapsed >= this.coldStartMs) { this._coldStartEnd(); return false; }
    // Give the queue a moment to fill before an empty one is read as
    // "done": the first dispatch is often alone for a frame or two.
    if (elapsed > 1000 && (this._activeTileLoads || 0) === 0) { this._coldStartEnd(); return false; }
    return true;
  }

  // What the window is doing, for the perf snapshot and the tests.
  coldStartStatus() {
    const active = this.inColdStart();
    return {
      active,
      msRemaining: active ? Math.max(0, Math.round(this.coldStartMs - (performance.now() - this._coldStartAt))) : 0,
      materializeBudgetMs: this._materializeBudget(),
      maxFetchesPerTile: active ? this.coldMaxFetchesPerTile : this.maxFetchesPerTile,
      maxConcurrentTileLoads: this._tileLoadCap(),
      maxConcurrentFetches: (this.fetcher && this.fetcher.maxConcurrentFetches) || null,
    };
  }

  // Main-thread milliseconds materialisation may spend per frame.
  _materializeBudget() {
    if (this.inColdStart()) return this.coldMaterializeBudgetMs;
    return (this._activeTileLoads || 0) > 0 ? this.materializeBudgetMsLoading : this.materializeBudgetMs;
  }

  _tileLoadCap() {
    return this.inColdStart()
      ? Math.max(this.maxConcurrentTileLoads, this.coldMaxConcurrentTileLoads)
      : this.maxConcurrentTileLoads;
  }

  _dispatchable(tile) {
    if (tile._dispatched) return false;
    return tile.state === TileState.IDLE ||
      tile.state === TileState.UNLOADED ||
      (tile.state === TileState.SIDECAR_LOADING && !!tile.instancesByHash);
  }

  async _beginLoad(tile, distance) {
    // Claimed synchronously, before anything can await: this is what
    // stops the next tick dispatching the same tile again.
    if (tile._dispatched) return;
    tile._dispatched = true;
    // First tile of the session opens the cold-start burst window.
    this._coldStartBegin();
    this._activeTileLoads++;
    // Stamp when this load started so the watchdog can identify
    // tiles that have been stuck in a loading state for too long.
    tile._loadStartedAt = performance.now();
    try {
      if (!tile.instancesByHash) {
        await this._loadSidecar(tile);
      }
      // Distance-based LOD at INITIAL materialize. _applyLodSwap stays
      // disabled — the swap had a one-frame ZERO_MATRIX window that
      // caused dropouts under horizontal rotation. The initial pick is
      // safe: it runs once when the tile first materializes, well before
      // the first render frame paints it. Tradeoff is that a tile loaded
      // when far stays at its coarse LOD if the camera later zooms in
      // (far-loaded tiles look coarse close-up). For most user paths
      // (load, then explore) tiles near the eventual viewing distance
      // get the right LOD up-front, which is what saves vertex work.
      const distance = this._tileDistance(tile, this.viewer.camera.position);
      const targetLod = this.lodSelector.select(distance, null);
      const installed = await this._materializeTile(tile, targetLod);
      if (installed === false) {
        // Unloaded or superseded while this run was in flight, so it
        // installed nothing. Back to unloaded, which is a state the load
        // scheduler considers - marking it visible would strand it empty
        // for the rest of the session.
        tile.state = TileState.UNLOADED;
        tile.lod = null;
        tile._loadStartedAt = null;
        return;
      }
      tile.lod = targetLod;
      tile.state = TileState.VISIBLE;
      tile._loadStartedAt = null;
    } catch (err) {
      // Counted and kept, not just logged. A console line does not reach
      // a headless capture or a perfSnapshot, so a load that threw on
      // every merge-carrying tile looked exactly like a load that
      // quietly decided not to keep anything.
      // A sidecar backing off after a failure (_loadSidecar) is not a new
      // failure: no request was made.
      if (!(err && err.cancelled)) {
        this._loadErrors = (this._loadErrors || 0) + 1;
        this._lastLoadError = String((err && err.message) || err);
        console.error(`[StreamingController] tile ${tile.key} load failed`, err);
      }
      tile.state = TileState.IDLE;
      tile._loadStartedAt = null;
    } finally {
      tile._dispatched = false;
      this._activeTileLoads--;
    }
  }

  // Force-reset tiles that have been in SIDECAR_LOADING or
  // FRAGMENTS_LOADING too long without an _activeTileLoads slot.
  // Catches the edge case where a fetch or GLTFLoader.parse hangs
  // without ever calling resolve/reject — the tile would otherwise
  // sit forever, blocking its geometry from ever appearing in the
  // scene. Cheap; runs once per eviction tick (~1 Hz).
  //
  // 60s — raised from 30s after the cache+watchdog deploy still
  // showed 156 resets. Tiles weren't actually hung — they were
  // legitimately queued behind the sidecar semaphore, and watchdog
  // was counting that wait against them. With maxConcurrentTileLoads
  // now matched to the semaphore (32), queue wait is gone; this 60s
  // is purely the safety net for genuinely hung work.
  // Does this tile still owe a heavy fragment (exporter-flagged or
  // over 500k triangles at any LOD in the index)? Its download + parse
  // can take minutes; the watchdog must not reset it every 60 s (on
  // SpaceX BA03 the scrubber tiles were reset and restarted in a loop).
  // WHERE A DEFERRED ROW WOULD BE, WITHOUT MATERIALISING IT.
  //
  // F could not frame a deferred element at all. unionBoxForUids reads
  // getActiveSlotsForUid and skips anything with no ACTIVE slot, so a row the
  // budget is holding contributes NOTHING and the box comes back empty.
  // Verified on prod SpaceX 2026-09-27: three of three conduit fittings were
  // inactive at the home view - which IS the owner's original report, because
  // a small fitting seen from far away is exactly what gets held.
  //
  // A held row is not unknown, only undrawn: tile._deferredHashes keeps the
  // SAME rows array instancesByHash holds, so the placement matrix is right
  // there, and the fragment index gives the radius the size gate already uses.
  // Centre plus radius is a sphere - coarser than the real box and never
  // smaller, which is the safe direction for framing.
  //
  // ONE PASS FOR THE WHOLE SELECTION, not one walk per uid: a selection of a
  // thousand held rows would otherwise traverse every deferred tile a
  // thousand times.
  deferredPlacementsFor(uids) {
    const out = new Map();
    const want = new Set();
    for (const u of uids || []) if (u != null) want.add(String(u));
    if (!want.size || !this.registry) return out;
    const root = this.poolRegistry && this.poolRegistry.sceneRoot;
    const rootMatrix = root ? root.matrixWorld : null;
    this.registry.forEach((tile) => {
      if (!tile._deferredHashes || !tile._deferredHashes.size) return;
      tile._deferredHashes.forEach((rows, hash) => {
        if (!rows || !rows.length) return;
        const meta = this._metaOf(hash);
        const radius = meta && meta.r > 0 ? meta.r : 0;
        for (const row of rows) {
          const uid = row && row.uniqueId != null ? String(row.uniqueId) : '';
          if (!uid || !want.has(uid) || out.has(uid)) continue;
          // instanceWorldPosition, not a fourth copy of the cm branch: the
          // translation sits at 3/7/11 or 12/13/14 depending on sidecar
          // format, and reading the wrong three yields 0,0,0 - a plausible
          // position rather than an obvious fault.
          const p = instanceWorldPosition(row, rootMatrix, new Vector3());
          if (!p) continue;
          out.set(uid, { x: p.x, y: p.y, z: p.z, r: radius });
        }
      });
    });
    return out;
  }


  _tileHasHeavyPending(tile) {
    if (!tile.instancesByHash) return false;
    let heavy = false;
    tile.instancesByHash.forEach((_i, hash) => {
      if (heavy) return;
      if (tile._deferredHashes && tile._deferredHashes.has(hash)) return;
      if (this.heavyFragments.has(hash)) { heavy = true; return; }
      const meta = this._metaOf(hash);
      if (meta && (meta.h || (Array.isArray(meta.t) && Math.max(...meta.t) > 500000))) heavy = true;
    });
    return heavy;
  }

  _watchdogStuckTiles(now) {
    const STUCK_MS = 60_000;
    const HEAVY_STUCK_MS = 300_000;
    this.registry.forEach(tile => {
      if (tile.state !== TileState.SIDECAR_LOADING &&
          tile.state !== TileState.FRAGMENTS_LOADING) return;
      if (!tile._loadStartedAt) return;
      // A tile that is actively materialising under the frame budget is
      // making progress, not stuck.
      // A tile inside _materializeTile is waiting on fetches or the
      // frame budget; progress is stamped per fragment and per yield.
      // Resetting it does not stop the in-flight work, it only
      // re-enqueues the tile behind it (double download), so give it
      // the long limit measured from its last progress.
      if (tile._materializing && (now - (tile._materializeProgressAt || 0)) < HEAVY_STUCK_MS) return;
      const limit = (tile.state === TileState.FRAGMENTS_LOADING && this._tileHasHeavyPending(tile)) ? HEAVY_STUCK_MS : STUCK_MS;
      if ((now - tile._loadStartedAt) < limit) return;
      // Reset and let the tick enqueue handle it next pass. We don't
      // try to cancel any in-flight fetch — fetcher in-flight dedup
      // will handle a duplicate cleanly.
      console.warn(`[StreamingController] watchdog reset stuck tile ${tile.key} (state=${tile.state}, age=${Math.round((now - tile._loadStartedAt) / 1000)}s)`);
      tile.state = TileState.IDLE;
      tile._loadStartedAt = null;
      // The dispatch claim has to go too. A genuinely hung run never
      // reaches its `finally`, so leaving the claim set would make this
      // reset cosmetic: the tile would look idle and still be skipped
      // at dispatch forever. Re-dispatching while the hung run is
      // technically alive is the trade the watchdog already accepts for
      // the download, and the epoch guard keeps the loser harmless.
      tile._dispatched = false;
      this._totalWatchdogResets = (this._totalWatchdogResets || 0) + 1;
      thrashOf(this).tileUnloaded(tile.key, 'watchdog');
    });
  }

async _loadSidecar(tile, priority = this._sidecarDemand(tile, false)) {
  if (tile.instancesByHash) return;
  // A sidecar that failed waits before it is asked for again: 1 s, then
  // doubling to a minute. Without this a tile whose sidecar cannot be read
  // (staging 2026-09-19: tiles stored without their gzip encoding) was
  // re-fetched on every tick - ~1,700 requests per tile in 20 s.
  const nowMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (tile._sidecarRetryAt && nowMs < tile._sidecarRetryAt) {
    throw Object.assign(new Error('sidecar retry backing off'), { cancelled: true });
  }
  tile.state = TileState.SIDECAR_LOADING;
  try {
    await this._loadSidecarOnce(tile, priority);
    tile._sidecarFailures = 0;
    tile._sidecarRetryAt = 0;
  } catch (err) {
    if (!(err && err.cancelled)) {
      tile._sidecarFailures = (tile._sidecarFailures || 0) + 1;
      tile._sidecarRetryAt = nowMs + Math.min(60000, 1000 * Math.pow(2, tile._sidecarFailures - 1));
    }
    throw err;
  }
}

async _loadSidecarOnce(tile, priority) {
  // Prefer the binary sidecar (no per-row JSON parse, matrices already in
  // three.js order); fall back to the JSON one if it is missing or bad.
  let sidecar = null;
  if (tile.binUrl && typeof this.manifestLoader.loadSidecarBinary === 'function') {
    try {
      sidecar = await this.manifestLoader.loadSidecarBinary(tile.key, tile.binUrl, priority);
      this._binarySidecars = (this._binarySidecars || 0) + 1;
    } catch (err) {
      this._binarySidecarFallbacks = (this._binarySidecarFallbacks || 0) + 1;
      if (this._binarySidecarFallbacks <= 3) console.warn('[StreamingController] binary sidecar failed, using JSON for', tile.key, err && err.message);
      sidecar = null;
    }
  }
  if (!sidecar) sidecar = await this.manifestLoader.loadSidecar(tile.key, tile.sidecarUrl, priority);
  this.registry.hydrate(tile, sidecar);

  // Patch: sidecar is authoritative for both instancesByHash and
  // fragmentUrls. Overwrite unconditionally — anything else risks
  // bugs from partial-map seeding in earlier hydration steps.
  if (sidecar && sidecar.fragmentUrls) {
    tile.fragmentUrls = sidecar.fragmentUrls;
  }
}

  _resolveFragmentUrl(tile, hash) {
    if (!tile.fragmentUrls) return null;
    return tile.fragmentUrls[hash] || null;
  }

  // Pixels per world unit at distance 1 for the current camera.
  _focalPx() {
    const cam = this.viewer && this.viewer.camera;
    const el = this.viewer && this.viewer.renderer && this.viewer.renderer.domElement;
    const h = (el && el.clientHeight) || (this.viewer && this.viewer.container && this.viewer.container.offsetHeight) || 800;
    const fov = (cam && cam.fov) || 60;
    return h / (2 * Math.tan((fov * Math.PI) / 360));
  }

  // Should this hash wait rather than materialise now? Only when the
  // exporter told us its size (fragmentsIndex); unknown fragments never
  // wait. Heavy fragments wait until they are big on screen.
  // Camera distance to one (tile, hash) group: the mean translation of
  // its rows, cached on the rows array. The tile box is NOT usable for
  // this - with the exporter's membership cap a leaf that holds a
  // spanning element carries that element's full bounds, so every
  // fitting in it read as "distance 0" from anywhere and stayed at fine
  // LOD forever (SpaceX capture 4: 3.19M tris at the aerial after one
  // interior visit).
  _groupDistance(instances, cameraPos) {
    if (!instances || !instances.length) return 0;
    let c = instances._center;
    if (!c) {
      let x = 0, y = 0, z = 0, n = 0;
      for (const inst of instances) {
        const m = inst.deltaMatrix;
        if (!m || m.length < 16) continue;
        // JSON rows are C# row-major (translation at 3,7,11); binary
        // rows are column-major (translation at 12,13,14).
        if (inst.cm) { x += m[12]; y += m[13]; z += m[14]; } else { x += m[3]; y += m[7]; z += m[11]; }
        n++;
      }
      c = new Vector3(n ? x / n : 0, n ? y / n : 0, n ? z / n : 0);
      const root = this.poolRegistry && this.poolRegistry.sceneRoot;
      if (root && root.matrixWorld) c.applyMatrix4(root.matrixWorld);
      instances._center = c;
    }
    return c.distanceTo(cameraPos);
  }


  // Hold back the rows of an admitted group that are individually too small
  // to see. Returns the rows to draw; anything held is added to the tile's
  // deferred map, which the rest-window revive re-checks.
  //
  // The group has ALREADY passed _shouldDefer here - this only ever removes
  // rows the group test could not see, because it works from one centroid.
  _pixelSkipRows(tile, hash, instances, focalPx, cameraPos, deferredMap) {
    if (!this.perInstancePixelSkip) return instances;
    const meta = this._metaOf(hash);
    if (!meta || !(meta.r > 0)) return instances;
    // A fragment the page insists on is never held: isolate and focus
    // outrank the budget, and this is a budget.
    if (this.inFocus(tile) || (isolationOutranksBudget && isolationOutranksBudget(this, hash))) return instances;
    const root = this.poolRegistry && this.poolRegistry.sceneRoot;
    const { admit, skip } = splitByPixelSize(instances, {
      radius: meta.r,
      focalPx,
      minPx: this.dynamicMinPx || this.minFragmentPixels,
      cameraPos,
      rootMatrix: root && root.matrixWorld,
      keep: this._pixelSkipKeep || null,
    });
    if (!skip.length) return instances;
    // HELD, NOT DROPPED - and held in the SAME MAP a fully deferred group
    // uses, so _reviveDeferred brings them back with no new revive path.
    // A separate store would have been a permanent drop: the revive reads
    // tile._deferredHashes and nothing else, so rows parked anywhere else
    // are never seen again. That is the bug this whole change is supposed
    // to avoid re-creating.
    if (!deferredMap) return instances;   // no way to hold them: draw them
    deferredMap.set(hash, skip);
    this._pixelHeldRows = (this._pixelHeldRows || 0) + skip.length;
    return admit;
  }


  // Turn clearance zones on or off.
  //
  // Hiding and showing both go through the SAME re-materialisation the level
  // system uses - _applyLodSwap with force - rather than a second mechanism
  // that would have to agree with the first. Only tiles that actually carry
  // clearance are touched, so a model without any pays nothing.
  //
  // This costs a synchronous re-materialisation per affected tile, which is
  // the expensive operation in this file. It is spent on an EXPLICIT user
  // action rather than on camera motion, which is the difference between
  // this and raising the swap rate.
  setClearanceVisible(visible) {
    if (!this.clearanceState.set(visible)) return 0;   // no change, no work
    let touched = 0;
    this.registry.forEach((tile) => {
      if (!tile.instancesByHash) return;
      let carries = false;
      tile.instancesByHash.forEach((rows, hash) => {
        if (carries) return;
        if (shouldHideGroup(hash, rows, { isVisible: () => false })) carries = true;
      });
      if (!carries) return;
      touched++;
      try { this._applyLodSwap(tile, tile.lod, true, 'clearance'); }
      catch (e) { console.warn('[StreamingController] clearance swap failed for', tile.key, e && e.message); }
      // Shells switched on but never fetched wait in the deferred map; the
      // rest pass revives them, and a still camera only runs it on a new
      // input.
      tile._deferredCheckedAt = 0;
    });
    this._bumpInput();
    return touched;
  }

  clearanceVisible() { return this.clearanceState.isVisible(); }

  // WHICH MODEL FILES THIS TOGGLE CAN ACTUALLY SEE.
  //
  // Clearance is flagged by the exporter per gather, and on SpaceX today the
  // HOST section carries 44,549 clearance records while all 16 LINKS carry
  // ZERO - they were banked before clearance flagging existed (e5d0c7b,
  // 2026-09-25) and have been reused since. So the signal is complete for
  // the host and EMPTY for every link, and a toggle that said nothing would
  // look implemented while being blind on sixteen sections out of seventeen.
  //
  // COUNTED FROM THE ROWS, NEVER ASSUMED: when the links are re-gathered this
  // widens on its own with no code change, and if somebody's model has
  // clearance everywhere it says so from the first tile.
  clearanceCoverage() {
    const withClearance = new Set();
    const seen = new Set();
    let rows = 0;
    this.registry.forEach((tile) => {
      if (!tile.instancesByHash) return;
      tile.instancesByHash.forEach((list, hash) => {
        const clearGroup = isClearanceHash(hash);
        for (const row of list) {
          const doc = row && row.f;
          if (doc !== undefined && doc !== null) seen.add(doc);
          if (clearGroup || (row && row.c === true)) {
            rows++;
            if (doc !== undefined && doc !== null) withClearance.add(doc);
          }
        }
      });
    });
    return { filesWithClearance: withClearance.size, filesSeen: seen.size, rows };
  }

  _pxOf(hash, distance, focalPx) {
    const meta = this._metaOf(hash);
    if (!meta || !(meta.r > 0)) return null;
    return (meta.r * 2 * focalPx) / Math.max(distance, 1);
  }

  // The size a fragment COMPETES at - against the pixel floors, the
  // budget and the download order: its screen size, and a fitting's
  // times connectorPxBoost (see the constructor). The level of detail
  // still follows its real size (_lodForHash), so a boosted elbow draws
  // cheap. The category is the head of the fragment key
  // ("ConduitFitting_...|gc=...").
  _rankPxOf(hash, distance, focalPx) { return rankPx(this, hash, distance, focalPx); }

  // Elements the page says must be on screen whatever their size.
  //
  // Construction tracking colours elements by what the field has done to
  // them, and the whole point of that picture is to read the state of
  // the job from a distance - so an element small enough for the pixel
  // floor to skip is exactly the one somebody is looking for. A pinned
  // element is never deferred and never falls below a size floor. Empty
  // by default: the model viewer pins nothing and is unaffected.
  setPinnedUids(uids) {
    this.pinnedUids = uids && uids.length ? new Set(uids.map(String)) : null;
    this._bumpInput();
    // The deferral decisions already taken have to be revisited, or a
    // pinned element stays skipped until the camera happens to move.
    this.registry.forEach((tile) => { tile._deferredCheckedAt = 0; });
  }

  // Load every fragment, whatever its size on screen.
  //
  // The model viewer skips what is not worth its pixels: a fragment too
  // small waits, a heavy one waits longer, and the triangle budget raises
  // the floor for the rest. Construction tracking cannot afford a skipped
  // element - its picture is a plan of the whole job with every element
  // coloured by where it stands, and an element that is not there cannot
  // be tagged. So nothing is deferred and no floor is raised.
  //
  // LEVEL OF DETAIL IS NOT TOUCHED. A fragment still draws fine, medium
  // or coarse by its distance and footprint exactly as it does in the
  // model viewer (owner, 2026-09-13: "the LOD needs to work as usual with
  // distance, just all fragments need to load"). What changes is whether
  // the fragment is there, not how many triangles it spends.
  //
  // An isolation still wins: a page that cuts to one level is a choice,
  // not a shortcut, and loading the other floors behind it would be
  // paying for geometry the page has said it does not show.
  //
  // A per-page opt-in that the viewer never sets on its own.
  setDrawEverything(on) {
    const next = !!on;
    if (next === this.drawEverything) return;
    this.drawEverything = next;
    this._bumpInput();
    this.dynamicMinPx = 0;
    this._budgetLastAt = 0;
    this._coneLastAt = -Infinity;
    // Every deferral and LOD verdict already taken is stale either way.
    this.registry.forEach((tile) => { tile._deferredCheckedAt = 0; });
  }

  // Isolation is an instruction about what to LOAD, not only about what
  // to show.
  //
  // Isolating hid everything else and then went on streaming the rest of
  // the model into the hidden state behind it: the fetches, the parses,
  // the pools and the memory, all for geometry nobody had asked to see,
  // and the owner watched the model carry on loading inside a view that
  // was supposed to hold one branch of the tree. While an isolation is
  // active, a fragment group holding nothing in it is not worth
  // downloading.
  //
  // Deliberately expressed as a deferral rather than as a new gate:
  // deferral is already consulted at materialise, at the retire check
  // and at the fetch decision, and it already reverses itself, so
  // clearing the isolation brings the model back through machinery that
  // is known to work.
  _isolationSet() {
    const store = this.applicator && this.applicator.store;
    const isolated = store && store.isolated;
    return isolated && isolated.size ? isolated : null;
  }

  // Is there nothing in this group the isolation wants?
  //
  // A RULE answers for the whole group at once from the fragment hash - one
  // test instead of a loop, because a pool holds one fragment. It still
  // falls through to the per-instance test when the hash misses, so
  // merged-geometry uids (which carry no hash) are not lost.
  _isolatedOut(instances, hash) {
    const isolated = this._isolationSet();
    if (!isolated || !instances) return false;
    if (hash !== undefined && isolated.isFragmentIsolation && isolated.hasHash(hash)) return false;
    for (const inst of instances) {
      if (inst && isolated.has(inst.uniqueId, hash)) return false;
      // A nested part is wanted when its element is (ViewerStateStore.
      // isInIsolation): a tile holding more of an isolated hanger's rods
      // must still load, or the hanger comes back as the pieces that
      // happened to be on screen when I was pressed.
      if (inst && inst.parentUid && isolated.has(inst.parentUid)) return false;
    }
    return true;
  }

  // Every group in this tile is outside the isolation, so the tile as a
  // whole has nothing to contribute. Only answerable once its sidecar has
  // landed; before that the tile is loaded as usual, which is cheap.
  _tileIsolatedOut(tile) {
    if (!this._isolationSet() || !tile || !tile.instancesByHash) return false;
    let out = true;
    tile.instancesByHash.forEach((instances, hash) => {
      if (!out) return;
      if (!this._isolatedOut(instances, hash)) out = false;
    });
    return out;
  }

  // The isolation changed. Every tile's deferral verdict is now stale -
  // including the tiles that were skipped, which have to be reconsidered
  // or clearing an isolation would leave holes in the model.
  onIsolationChanged() {
    this._isolationChanges = (this._isolationChanges || 0) + 1;
    this._bumpInput();
    this.registry.forEach((tile) => { tile._deferredCheckedAt = 0; });
  }

  // ---- ASKED FOR BY NAME ---------------------------------------------
  //
  // The Project Files tree shows the no-mesh icon on every row whose geometry
  // the viewer has not built (viewer.checkMesh -> meshesObject.has(uid)). On a
  // streaming export that is most rows most of the time, because a fragment
  // materialises when it is worth its pixels - so opening a branch the camera
  // is not pointing at gives a column of icons that read as "this model is
  // broken" when the truth is "not fetched yet".
  //
  // ensureUids is how a page says "these particular elements are being read,
  // build them whatever their size". It is exactly the pinnedUids exemption,
  // with two differences that matter:
  //   - ITS OWN SET. pinnedUids belongs to construction tracking, which sets
  //     it wholesale. Sharing one set would mean the tree cancels tracking's
  //     pins by opening a branch, invisibly.
  //   - BOUNDED, AND IT SAYS SO. A category on the biggest staging commit
  //     spans 692 tiles and over 100,000 elements. `cap` is what keeps "show
  //     me this branch" from meaning "load the model"; ensuredStatus().
  //     truncated is how the page can tell the reader it asked for a part.
  //
  // Additive, like addPriorityTiles: several branches open at once stack
  // until clearEnsuredUids().
  ensureUids(uids, { cap = 20000 } = {}) {
    if (!Array.isArray(uids) || !uids.length) return this.ensuredStatus();
    const set = this.ensuredUids || new Set();
    let added = 0;
    for (const u of uids) {
      if (set.size >= cap) { this._ensuredTruncated += 1; break; }
      const k = String(u || '');
      if (!k || set.has(k)) continue;
      set.add(k); added += 1;
    }
    this.ensuredUids = set.size ? set : null;
    if (added) {
      this._bumpInput();
      // The deferral verdicts already taken are stale for these elements, or
      // a newly asked-for row waits for the camera to happen to move.
      this.registry.forEach((tile) => { tile._deferredCheckedAt = 0; });
    }
    return this.ensuredStatus();
  }

  clearEnsuredUids() {
    this.ensuredUids = null;
    this._ensuredTruncated = 0;
    this._bumpInput();
    this.registry.forEach((tile) => { tile._deferredCheckedAt = 0; });
  }

  ensuredStatus() {
    return {
      uids: this.ensuredUids ? this.ensuredUids.size : 0,
      truncated: this._ensuredTruncated > 0,
      overflow: this._ensuredTruncated,
    };
  }

  // Pinned by construction tracking OR asked for by name by a page reading
  // its rows. Two owners, two sets, one exemption.
  _hasPinned(instances) {
    if (!instances) return false;
    const pinned = this.pinnedUids;
    const ensured = this.ensuredUids;
    if (!pinned && !ensured) return false;
    for (const inst of instances) {
      if (!inst) continue;
      const uid = String(inst.uniqueId);
      if (pinned && pinned.has(uid)) return true;
      if (ensured && ensured.has(uid)) return true;
    }
    return false;
  }

  // `focus` = this group is in a tile the camera is pointing at (see
  // focusGuarantee). Nothing in focus is skipped for being small.
  // The index is optional - an export can ship without one, and one
  // that ships can still not know a hash - so it is never indexed
  // directly. Reading it as `this.fragmentsIndex[hash]` threw
  // "Cannot read properties of null" for EVERY tile on a project whose
  // export has no fragments index, and nothing drew at all.
  // A group's entry in the fragment index. The planning passes ask for the
  // same hash four or five times in a row (its size, its level - twice - its
  // triangles, its call), and the index is a plain object of ~143k long
  // string keys: Tom's r10 profiles put 0.9-1.2 s of self time here. Inside
  // a tick (see tick()) the last answer is remembered, which turns most of
  // those lookups into one string comparison. Outside a tick - tests that
  // edit the index in place between calls - every call reads it.
  _metaOf(hash) {
    const index = this.fragmentsIndex;
    if (!index) return null;
    if (this._metaMemoOn) {
      if (hash === this._metaLastHash) return this._metaLast;
      const m = index[hash] || null;
      this._metaLastHash = hash;
      this._metaLast = m;
      return m;
    }
    return index[hash] || null;
  }

  // SHORT FAR PLANE WHILE MOVING, RESTORED AT REST.
  //
  // Applied only on TRANSITION, never per frame: updateProjectionMatrix is
  // cheap but not free, and writing the same value every frame is how a
  // profile grows a line nobody can explain.
  //
  // It remembers the camera's ORIGINAL far and restores exactly that, rather
  // than restoring a constant - anything else silently becomes the authority
  // on a value it does not own. If something outside changes far while we
  // hold it short, the restore puts back what we took, which is the least
  // surprising of the available wrong answers.
  //
  // NOT DONE HERE, and stated so nobody assumes it was: the scene fog still
  // reaches past the cap, so geometry is CLIPPED at the boundary rather than
  // faded into it. If the cut reads badly while orbiting, pulling fog.far to
  // the cap during movement is the follow-up - it is a look change and it
  // wants eyes on it, not a guess at 11am.
  _applyMoveFar(cameraAtRest) {
    const cap = this.moveFarCap;
    const cam = this.viewer && this.viewer.camera;
    if (!cam || !(cap > 0)) return;
    if (!cameraAtRest) {
      if (this._farRestore === null && cam.far > cap) {
        this._farRestore = cam.far;
        cam.far = cap;
        cam.updateProjectionMatrix();
      }
      return;
    }
    if (this._farRestore !== null) {
      cam.far = this._farRestore;
      this._farRestore = null;
      cam.updateProjectionMatrix();
    }
  }

  // WHAT THE CAMERA IS ACTUALLY LOOKING AT, ADMITTED WHOLE.
  //
  // Owner, 2026-09-27: "why arent we dynamically reload all missing in the
  // camera cone, culling, whenever the user stops moving, and hide all less
  // than N px when we move the camera". This is the rest half.
  //
  // === IT MUST NOT TOUCH THE BUDGET, AND THE ORDER IS WHY ===
  //
  // The previous attempt at this symptom changed _groupDistance's answer so
  // that spread groups counted as near. THAT NUMBER IS READ BY
  // _rebalanceTriangleBudget: more groups ranked near, the budget filled,
  // and the adaptive floor climbed from 142 to 446 px close in and from
  // 10.75 to 20.93 at the aerial - about 200,000 fewer rows on the first
  // view anybody opens. One washer, at the price of the scene.
  //
  // So this is an EXEMPTION LIST, consulted by _shouldDefer and by NOTHING
  // ELSE. It cannot move dynamicMinPx because the budget pass does not read
  // _coneAdmitted or _coneStats - and that, not the call order, is the
  // guarantee. I wrote "it runs after ranking, on purpose" first; a mutation
  // that ran it BEFORE ranking changed no test and no behaviour, which is
  // how I learned my own comment was describing the wrong mechanism.
  //
  // === AND IT REPORTS WHAT IT COULD NOT DO ===
  //
  // Measured on BA03 at the Level-1 camera: everything in the cone is 87,031
  // deferred rows and 11.55 M triangles against an 8 M budget - it does NOT
  // fit. At 20 px it is 6,039 rows and 0.84 M and it does. The owner asked
  // for 4 px, which nobody has measured, so THE THRESHOLD IS A PARAMETER and
  // the backstop is real: nearest-first until the budget is spent, with
  // counters saying WANTED versus ADMITTED. A cone rule that silently
  // delivers two thirds of what it promised is the failure this whole night
  // has been about.
  // Whether the cone set is due for a recompute: on the first rest tick
  // after the camera moved (what the user stopped on is answered at once),
  // and otherwise at most every coneRefreshMs while tiles keep arriving.
  _coneDue(now) {
    const moved = this._coneMoveStamp !== this._lastCameraMoveTime;
    // A NEW BUDGET PASS MAKES THE CONE DUE AT ONCE (round 11). The cone set
    // is defined against the floor (it admits only rows UNDER it), so a cone
    // set older than the floor is answering a different question. With the
    // 1 s refresh in between, the rest checks of that second saw the new
    // floor and the old cone set: rows the new floor dropped and the cone
    // would now admit were retired (retire:budget), then brought back by
    // the cone a second later, then dropped again when the cone moved on
    // (retire:cone) - Tom's r10 tiles alternating budget, cone, budget.
    // Budget passes are themselves throttled (budgetRebalanceMs), so this
    // cannot run the cone more often than they run.
    const budgetMoved = this._coneBudgetSeq !== (this._budgetSeq || 0);
    if (!moved && !budgetMoved && now - this._coneLastAt < (this.coneRefreshMs || 0)) return false;
    this._coneBudgetSeq = this._budgetSeq || 0;
    // Still camera and nothing installed or swapped since: same answer.
    const seq = (this._inputSeq || 0) + '|' + (this._budgetSeq || 0);
    if (!moved && this._coneLayoutKey === seq) return false;
    this._coneLayoutKey = seq;
    this._coneMoveStamp = this._lastCameraMoveTime;
    this._coneLastAt = now;
    this._coneSeq = (this._coneSeq || 0) + 1;
    return true;
  }

  _admitCone(cameraPos, focalPx) {
    const minPx = this.coneMinPx;
    if (!(minPx > 0) || !this.fragmentsIndex || this.drawEverything) {
      noteConeDrops(this, this._coneAdmittedByTile, null);
      this._coneAdmitted = null;
      this._coneAdmittedByTile = null;
      this._coneStats = null;
      return;
    }
    // THE CONE SPENDS A SHARE OF THE BUDGET, NOT A SECOND BUDGET. It used to
    // admit up to a whole maxTrianglesPerFrame on top of the budget the
    // floor had already spent, so close in the view drew 1.6-1.7x what the
    // viewer reported (BA03 real data, 2026-10-13: 8.0 M budget, 12.6-13.7 M
    // drawn). And it spent that allowance on rows that were above the floor
    // anyway - drawn with or without it - so the rows it was for got less.
    // Now: only rows the floor would hold count, against coneBudgetShare of
    // the budget. 1 restores the old allowance.
    const share = this.coneBudgetShare != null ? this.coneBudgetShare : 1;
    const tLim = this.maxTrianglesPerFrame > 0 ? this.maxTrianglesPerFrame * share : Infinity;
    const floor = this.dynamicMinPx || 0;
    const boost = this.focusPxBoost || 1;
    const candidates = [];
    this.registry.forEach((tile) => {
      const counted = tile.state === TileState.VISIBLE || tile.state === TileState.FRAGMENTS_LOADING;
      // NOT tile._frustumHidden (round 11), for the reason the budget pass
      // gives: the flag is the LAST tick's answer. Coming back from an orbit
      // the tick re-shows tiles a slice at a time, and the first rest tick -
      // the one that recomputes the cone - found most of the view still
      // flagged. Their rows fell out of the cone set and stayed out (nothing
      // re-ran the cone afterwards), so each re-shown tile was then
      // re-materialised to drop them: Tom's r10, ~200 retire:cone after every
      // orbit back to the SAME view. The frustum test below is this camera.
      if (!counted || !tile.instancesByHash) return;
      const focus = floor > 0 && this.inFocus(tile);
      // THE TRUE CONE, not the padded box the streamer fetches with: padding
      // exists so a small pan does not wait on the network, and admitting
      // what is off screen is not what was asked for.
      if (!this._isTileInPaddedFrustum(tile, 1)) return;
      tile.instancesByHash.forEach((instances, hash) => {
        const d = this._groupDistance(instances, cameraPos);
        const px = this._pxOf(hash, d, focalPx);
        if (px === null || px < minPx) return;
        // Above the budget floor, or already admitted by the focus share, it
        // draws anyway: not the cone's to pay for.
        if (floor > 0) {
          if (this._admittedRow('focus', hash, tile.key)) return;
          const rank = rankPx(this, hash, d, focalPx);
          if (rank !== null && rank * (focus ? boost : 1) >= floor) return;
        }
        // The same level and call key the budget pass counts (its call set
        // is what `paid` below is checked against).
        const lod = this._lodForHash(hash, d, focalPx, tile.lod == null ? 0 : tile.lod, this._libraryFor(tile, hash));
        const call = this._callKey(tile, hash, lod, instances);
        candidates.push({ hash, d, tris: this._trisOfClaim(hash, lod, instances.length), tile: tile.key, call });
      });
    });
    // NEAREST FIRST, so a view that cannot afford everything keeps what the
    // person is standing in front of rather than whatever sorted first.
    candidates.sort((a, b) => a.d - b.d);
    const admitted = new Set();
    const byTile = new Map();
    let tris = 0;
    let spent = false;
    // DRAW CALLS TOO. The cone admitted small rows by triangles alone, and a
    // small row is very often its own pool - its own draw call. Rows whose
    // call the budget already pays for (a pool or tile batch it keeps) cost
    // none; new calls come out of coneBudgetShare of maxDrawCalls.
    const cLim = this.maxDrawCalls > 0 ? Math.floor(this.maxDrawCalls * share) : Infinity;
    const paid = this._budgetCallSet instanceof Set ? this._budgetCallSet : null;
    const newCalls = new Set();
    // No incumbency on purpose (round 11, measured in thrashSim.test.js):
    // letting last pass's rows keep their place made the set depend on the
    // path to the view rather than on the view, so an orbit away and back
    // to the same pose recomputed a different set and re-materialised
    // tiles to match it. Same view + same rows = same set.
    for (const c of candidates) {
      if (tris + c.tris > tLim) { spent = true; continue; }
      const addsCall = !(paid && paid.has(c.call)) && !newCalls.has(c.call);
      if (addsCall && newCalls.size + 1 > cLim) { spent = true; continue; }
      if (addsCall) newCalls.add(c.call);
      tris += c.tris;
      admitted.add(c.hash);
      // Per ROW, like the focus share: see _rebalanceTriangleBudget.
      let set = byTile.get(c.tile);
      if (!set) { set = new Set(); byTile.set(c.tile, set); }
      set.add(c.hash);
    }
    noteConeDrops(this, this._coneAdmittedByTile, byTile);
    this._coneAdmitted = admitted;
    this._coneAdmittedByTile = byTile;
    this._coneStats = { wanted: candidates.length, admitted: admitted.size, triangles: tris, calls: newCalls.size, budgetSpent: spent, minPx };
  }

  // See noteConeDrops / thrashOf (module level, so a borrowed method with a
  // plain `this` cannot throw on bookkeeping).
  _noteConeDrops(prev, next) { noteConeDrops(this, prev, next); }
  _thrash() { return thrashOf(this); }

  // How long a retire of this tile for these reasons must still wait (ms),
  // 0 when it may go now. Only DERIVED reasons wait - the budget floor, the
  // cone set, the size floors - because those are what climb pass after
  // pass while a load is still bringing tiles in. A level change (the
  // camera came closer), an isolation or a merge retire goes at once.
  _retireDwellLeft(tile, why, now) {
    const dwell = this.retireDwellMs;
    if (!(dwell > 0) || !tile || tile._installedAt == null) return 0;
    if (why) for (const k of Object.keys(why)) if (!DWELL_REASONS.has(k)) return 0;
    const left = tile._installedAt + dwell - now;
    return left > 0 ? left : 0;
  }

  // A tile's geometry was (re)built: first install, a swap, a revive. Starts
  // its retire dwell (see retireDwellMs).
  _noteInstalled(tile) {
    if (tile) tile._installedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  // Was this (tile, hash) row admitted past the floor by the focus share or
  // the cone? With a tile key the answer is per ROW (the budget counted
  // rows, so the exemption must be rows too); without one - only older
  // callers and tests - the per-hash sets answer as before.
  _admittedRow(which, hash, tileKey) {
    if (tileKey != null) {
      const byTile = which === 'focus' ? this._focusAdmittedByTile : this._coneAdmittedByTile;
      if (byTile instanceof Map) { const set = byTile.get(tileKey); return !!(set && set.has(hash)); }
    }
    const set = which === 'focus' ? this._focusAdmitted : this._coneAdmitted;
    return set instanceof Set && set.has(hash);
  }

  // `floorScale` multiplies the DYNAMIC floor only (never the fixed size
  // floors): a deferred group asks with 1 + reviveMargin, so it comes back
  // only once it is clearly above the floor - see reviveMargin.
  _shouldDefer(hash, distance, focalPx, instances = null, focus = false, tileKey = null, floorScale = 1) {
    // While an isolation is active it is the ONLY rule.
    //
    // A group holding something isolated loads whatever its size - the
    // point of isolating three fittings is to look at three fittings, and
    // a size floor that then refuses to draw them leaves an empty view.
    // A group holding none of them is not downloaded at all.
    //
    // It also outranks pinning: construction tracking pins everything it
    // has coloured, which would otherwise drag the whole coloured set
    // back into a view the user had narrowed by hand.
    if (instances && this._isolationSet()) {
      // Nothing isolated in this group: skip it whatever else is true.
      if (this._isolatedOut(instances, hash)) { this._deferWhy = 'isolation'; return true; }
      // Something isolated IS here. A SMALL isolation still outranks every
      // other rule - isolating three fittings must draw three fittings, and
      // a floor that then refuses them leaves an empty view. A LARGE one
      // falls through to the normal floor and budget below, because a
      // category can be three-quarters of the model and drawing that with
      // the brakes off is how the tab dies. See isolationRule.js.
      if (isolationOutranksBudget(this._isolationSet())) return false;
    }
    if (this.drawEverything) return false;
    if (focus && this.focusGuarantee) {
      // Pinned elements (construction tracking) are never floored.
      if (instances && this._hasPinned(instances)) return false;
      // Everything the camera points at loads - with one exception,
      // for geometry the simplifier could not make cheaper.
      //
      // Fifty fragments in the SpaceX export carry over 50,000
      // triangles each (one scrubber is 723,739) and their coarse
      // level is barely below their fine one, because they are DWG
      // soup rather than modelled solids. Exempting those from the
      // size floors puts a million triangles into a view over
      // something the size of a thumbnail, which is the frame budget
      // gone and every pipe in the view hidden to pay for it.
      //
      // So a fragment whose CHEAPEST level is still over
      // focusTriangleCeiling keeps the heavy floor: it loads once it
      // is genuinely large on screen, like it did before. Everything
      // else - which is 118,245 of the 118,295 fragments - loads
      // whatever its size, as promised.
      const m = this._metaOf(hash);
      let cheapest = 0;
      if (m && Array.isArray(m.t)) {
        for (const v of m.t) if (typeof v === 'number' && v > 0 && (cheapest === 0 || v < cheapest)) cheapest = v;
      }
      const pxFocus = rankPx(this, hash, distance, focalPx);
      // Over budget, the view's smallest pieces wait (see focusPxBoost).
      // ...unless it was admitted into the focus share of the budget at the
      // last rest window (_rebalanceTriangleBudget).
      const admitted = this._admittedRow('focus', hash, tileKey) || this._admittedRow('cone', hash, tileKey);
      if (pxFocus !== null && this.dynamicMinPx > 0 && !admitted
          && pxFocus * (this.focusPxBoost || 1) < this.dynamicMinPx * floorScale) { this._deferWhy = 'budget'; return true; }
      if (cheapest <= (this.focusTriangleCeiling || 0)) return false;
      if (pxFocus === null) return false;
      this._deferWhy = 'heavy';
      return pxFocus < this.heavyFragmentPixels * (this.quality || 1);
    }
    if (instances && this._hasPinned(instances)) return false;
    // The cone exemption applies outside the focus set too: a tile can be in
    // the frustum without being in focus, and the person is looking at it
    // either way.
    if (this._admittedRow('cone', hash, tileKey)) return false;
    const px = rankPx(this, hash, distance, focalPx);
    if (px === null) return false;
    const q = this.quality || 1;
    const dyn = (this.dynamicMinPx || 0) * floorScale;
    if (px < Math.max(this.minFragmentPixels * q, dyn)) {
      // 'budget' when the dynamic floor is what refused it, else the fixed size floor.
      this._deferWhy = dyn > this.minFragmentPixels * q ? 'budget' : 'size';
      return true;
    }
    const meta = this._metaOf(hash);
    if (((meta && meta.h) || this.heavyFragments.has(hash)) && px < this.heavyFragmentPixels * q) { this._deferWhy = 'heavy'; return true; }
    return false;
  }

  // LOD for one hash from its screen size; the tile's distance-band LOD
  // when the index does not know the fragment. Never finer than the
  // band (the band is the ceiling for far tiles) and clamped to the LOD
  // slots the library actually holds (a slot can be null).
  _lodForHash(hash, distance, focalPx, fallbackLod, libraryParts) {
    let lod = fallbackLod;
    const px = this._pxOf(hash, distance, focalPx);
    if (px !== null) {
      const q = this.quality || 1;
      const meta = this._metaOf(hash);
      const heavy = !!(meta && meta.h) || this.heavyFragments.has(hash)
        || !!(meta && Array.isArray(meta.t) && meta.t[0] > 150000);
      const finePx = heavy ? Math.max(this.lodFinePx, this.heavyFinePx) : this.lodFinePx;
      const mediumPx = heavy ? Math.max(this.lodMediumPx, this.heavyMediumPx) : this.lodMediumPx;
      lod = px >= finePx * q ? 0 : (px >= mediumPx * q ? 1 : 2);
      const maxLod = (this.lodSelector && this.lodSelector.levelCount ? this.lodSelector.levelCount : 3) - 1;
      lod = Math.max(0, Math.min(maxLod, lod));
      // Triangle-per-pixel guard: never spend more triangles than the
      // footprint justifies.
      if (meta && Array.isArray(meta.t)) {
        const allowed = (px * px * this.trisPerPx2) / q;
        while (lod < maxLod && (meta.t[lod] || 0) > allowed) lod++;
      }
      if (typeof fallbackLod === 'number' && lod < fallbackLod) lod = fallbackLod;
    }
    if (Array.isArray(libraryParts)) {
      if (libraryParts[lod]) return lod;
      for (let l = lod + 1; l < libraryParts.length; l++) if (libraryParts[l]) return l;
      for (let l = lod - 1; l >= 0; l--) if (libraryParts[l]) return l;
    }
    return lod;
  }

  // Cooperative main-thread budget: yield to the next animation frame
  // once this frame's materialisation slice is spent. Shared across all
  // tiles materialising concurrently.
  async _budgetYield(tile) {
    if (tile) tile._materializeProgressAt = performance.now();
    const now = performance.now();
    const budget = this._materializeBudget();
    // The slice is measured from the moment materialisation work first
    // runs in this frame, not from the frame start: the render runs
    // first in a frame, and once it took longer than the budget (60 ms
    // at 6k draw calls) every yield resumed with the budget already
    // spent and yielded again - streaming froze with the fetcher idle
    // (SpaceX: 734 visible / 643 loading for 170 s, then again at 883).
    if (this._workFrame !== this._sliceFrame) {
      this._workFrame = this._sliceFrame;
      this._workStart = now;
    }
    if (now - this._workStart < budget) return;
    this._budgetYields++;
    await new Promise((resolve) => {
      if (typeof document !== 'undefined' && document.hidden) setTimeout(resolve, 0);
      else if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve());
      else setTimeout(resolve, 0);
    });
    // Hidden tab: the slice loop is throttled too, so restart it here.
    if (typeof document !== 'undefined' && document.hidden) { this._sliceStart = performance.now(); this._sliceFrame = (this._sliceFrame || 0) + 1; }
  }

  // How many fragment libraries the cache may hold before the LRU trims it.
  //
  // With fragment packs, one pack load fills the library of EVERY fragment
  // it carries (about 60 on SpaceX: 318,820 prefilled from 5,284 pack
  // loads), so the old 8,000-entry cap - meant for one fetch per hash -
  // threw away prefilled libraries within a second, oldest (= never used)
  // first. A tile asking for one of them later had to load its pack AGAIN:
  // headless staging cold load, 264,451 libraries evicted while they held
  // 80 MB of a 1,920 MB budget, and 2,961 pack re-loads, every one of them
  // for a fragment that had been prefilled and evicted. With packs the
  // entry count is therefore allowed up to the number of packed fragments,
  // and the byte budget (maxLibraryBytes) is what bounds the cache.
  _libraryEntryCap() {
    const f = this.fetcher;
    const packed = f && f.packHomes ? f.packHomes.size : 0;
    return Math.max(this.maxLibraryEntries, packed);
  }

  // A library is in use while any pool of that hash holds an occupied slot.
  _libraryInUse(hash) {
    const pr = this.poolRegistry;
    if (!pr || !pr.pools) return false;
    const arr = pr.pools.get(hash);
    if (!arr) return false;
    for (const p of arr) if (p && (p.count || 0) > 0 && (!p.freeSlots || p.freeSlots.length < p.count)) return true;
    return false;
  }

  setQuality(q) { this.quality = Math.max(0.25, Number(q) || 1); }

  // Promise.all with a concurrency cap; errors propagate like Promise.all.
  async _mapLimit(items, limit, fn) {
    const n = Math.max(1, limit | 0);
    let next = 0;
    const results = new Array(items.length);
    const worker = async () => {
      while (next < items.length) {
        const i = next++;
        results[i] = await fn(items[i], i);
      }
    };
    await Promise.all(Array.from({ length: Math.min(n, items.length) }, worker));
    return results;
  }
  setTriangleBudget(n) { this.maxTrianglesPerFrame = Math.max(0, Number(n) || 0); this.dynamicMinPx = 0; this._bumpInput(); }
  setDrawCallBudget(n) { this.maxDrawCalls = Math.max(0, Number(n) || 0); this.dynamicMinPx = 0; this._bumpInput(); }

  // Budgets sized from what the browser says about the machine. Chrome
  // reports deviceMemory (capped at 8) and every browser reports cores; a
  // browser that says neither gets the middle tier.
  static deviceBudget(nav = (typeof navigator !== 'undefined' ? navigator : null)) {
    const mem = nav && Number(nav.deviceMemory);
    const cores = nav && Number(nav.hardwareConcurrency);
    const known = (Number.isFinite(mem) && mem > 0) || (Number.isFinite(cores) && cores > 0);
    if (!known) return { tier: 'mid', triangles: 5000000, drawCalls: 3000 };
    const m = Number.isFinite(mem) && mem > 0 ? mem : 8;
    const c = Number.isFinite(cores) && cores > 0 ? cores : 8;
    if (m >= 8 && c >= 8) return { tier: 'high', triangles: 8000000, drawCalls: 4000 };
    if (m >= 4 && c >= 4) return { tier: 'mid', triangles: 5000000, drawCalls: 3000 };
    return { tier: 'low', triangles: 2500000, drawCalls: 1800 };
  }

  // Triangles a (hash, lod) claim contributes: index count x instances.
  _trisOfClaim(hash, lod, instances) {
    const meta = this._metaOf(hash);
    const t = meta && Array.isArray(meta.t) ? (meta.t[lod] != null ? meta.t[lod] : meta.t[meta.t.length - 1]) : 0;
    return (t || 0) * instances;
  }

  // The share of each budget held back for the cone pass: 0 when the cone
  // is off (or everything draws anyway), else coneBudgetShare, capped at
  // half so the floor always has the larger part.
  _coneReserve() {
    if (!(this.coneMinPx > 0) || this.drawEverything) return 0;
    const share = Number(this.coneBudgetShare);
    if (!Number.isFinite(share) || share <= 0) return 0;
    return Math.min(0.5, share);
  }

  // Once per rest window: size every group in view, keep the largest
  // until a budget - triangles or draw calls - is spent, and set
  // dynamicMinPx to the size of the first one that did not fit. Tiles
  // then retire below that floor through the normal rest pass, and tiles
  // still loading defer below it as they install.
  //
  // In-view groups rank focusPxBoost times larger (what the camera
  // points at goes first). Until 2026-09-17 they were counted as spent
  // and could never be floored, which is how the view reached 11k draw
  // calls and the budget meant nothing: on a whole-site view every tile
  // is in view.
  //
  // Loading tiles count too, from their sidecars: waiting for them to
  // finish let a cold load draw everything and then retire most of it.
  _rebalanceTriangleBudget(now, cameraPos, focalPx) {
    const limited = this.maxTrianglesPerFrame > 0 || this.maxDrawCalls > 0;
    if (!this.fragmentsIndex || !limited || this.drawEverything) { this.dynamicMinPx = 0; return; }
    if (now - this._budgetLastAt < this.budgetRebalanceMs) return;
    // NOTHING CHANGED, NOTHING TO RECOMPUTE: same camera, same installed
    // geometry, same settings. It ran every 2 s at rest regardless (Tom,
    // real GPU: 5.7-6.7 s of main thread in a 92 s window).
    const budgetKey = (this._lastCameraMoveTime || 0) + '|' + (this._inputSeq || 0);
    if (this._budgetKey === budgetKey) { this._budgetSkips = (this._budgetSkips || 0) + 1; return; }
    this._budgetKey = budgetKey;
    this._budgetLastAt = now;
    this._budgetSeq = (this._budgetSeq || 0) + 1;
    const passStart = typeof performance !== 'undefined' ? performance.now() : 0;
    const rows = [];
    let unsizedTris = 0;
    const unsizedCalls = new Set();
    const boost = this.focusPxBoost || 1;
    const anticipate = this.anticipateUnseen !== false;
    const unseen = [];
    const known = { tris: 0, rowCount: 0, bandTris: 0, trisWithBand: 0, tiles: 0 };
    this.registry.forEach(tile => {
      const counted = tile.state === TileState.VISIBLE || tile.state === TileState.FRAGMENTS_LOADING;
      // NOT tile._frustumHidden: the frustum test below is the answer for
      // THIS camera, and the flag is the last tick's - a tile still waiting
      // to be re-shown is in view and must be counted.
      if (!this._isTileInPaddedFrustum(tile, this.frustumPaddingFactor)) return;
      if (!counted || !tile.instancesByHash) {
        // Not loading yet (its sidecar is still on the way): estimated
        // below, see _anticipateUnseen.
        if (anticipate && tile.state !== TileState.UNLOADED && (tile.inTrimmedBounds || this._showOutliers)
            && !(tile.instancesByHash && this._tileIsolatedOut(tile))) unseen.push(tile);
        return;
      }
      const focus = this.inFocus(tile);
      const fallbackLod = tile.lod == null ? 0 : tile.lod;
      const band = anticipate ? this._bandTris(tile, cameraPos) : null;
      if (band != null) known.bandTris += band;
      known.tiles++;
      tile.instancesByHash.forEach((instances, hash) => {
        const d = this._groupDistance(instances, cameraPos);
        // THE LEVEL THE GROUP WANTS, NOT THE ONE IT WAS LAST BUILT AT (round
        // 11). This pass used to count an installed group at its claim's (or
        // its batch's) level - i.e. at whatever the last swap installed. So a
        // swap this pass caused changed this pass's next answer: the focus
        // share (nearest first, skipping what does not fit) admitted a
        // different set, the floor moved, and the rest pass swapped again.
        // Tom, r10: the same top tiles re-materialised 6-9 times per run.
        // The wanted level is what the rest check converges every group to
        // (it retires a group whose level != this), so at rest the two are
        // the same number, and before rest this pass no longer reads its own
        // output: same camera + same rows = same plan.
        const lod = this._lodForHash(hash, d, focalPx, fallbackLod, this._libraryFor(tile, hash));
        const px = rankPx(this, hash, d, focalPx);
        // Triangles at the LOD the group would draw at this distance
        // (a deferred group counts at its would-be LOD: it is a
        // candidate for revival if it fits).
        const tris = this._trisOfClaim(hash, lod, instances.length);
        // The draw call it adds: its pool, or its tile's batch.
        const call = this._callKey(tile, hash, lod, instances);
        if (anticipate) {
          known.tris += tris; known.rowCount += instances.length;
          if (band != null) known.trisWithBand += tris;
        }
        // Unknown size: always drawn, so spent up front.
        if (px === null || this._hasPinned(instances)) { unsizedTris += tris; unsizedCalls.add(call); return; }
        rows.push({ px, rank: focus ? px * boost : px, tris, call, focus, hash, d, tile: tile.key });
      });
    });
    const virtualRows = unseen.length ? this._anticipateUnseen(unseen, rows, known, cameraPos, boost) : null;
    let anticipated = 0;
    if (virtualRows) for (const v of virtualRows) { rows.push(v); anticipated += v.tris; }
    rows.sort((a, b) => b.rank - a.rank);
    let sum = unsizedTris;
    const calls = new Set(unsizedCalls);
    let floor = 0;
    // WHAT THE CAMERA POINTS AT FIRST (owner, 2026-09-19: "fragments don't
    // show up" on zoom-in). Ranked by pixels alone, the big pieces across
    // the rest of the site used up the budget, and the greedy cut then
    // floored every small fitting 15-20 ft in front of the camera - 240 of
    // 241 fragment groups in a focused SpaceX tile, 12-96 triangles each.
    // The focus groups now go first, NEAREST first - what you zoomed into
    // is the near end of the focus cone; ranked by pixels, the large
    // pieces further along it filled the share and the fittings in front
    // of the camera still waited - into focusBudgetShare of each budget,
    // SKIPPING what does not fit rather than stopping there. The admitted
    // hashes never wait for the floor; the floor is set from everything
    // else, as before.
    // THE CONE'S SHARE COMES OUT OF THE BUDGET, NOT ON TOP OF IT (round 9).
    // The cone pass admits up to coneBudgetShare more after this pass has
    // filled the whole budget, so the plan itself was 1.15x the cap before
    // anything else went wrong (staging BA03: planned 9.2 M against 8.0 M).
    // This pass now fills what the cone leaves. It still does not READ the
    // cone's set (coneRule.test.js): the reserve is a constant share,
    // whatever the cone admits.
    const reserve = this._coneReserve();
    const tCap = this.maxTrianglesPerFrame > 0 ? this.maxTrianglesPerFrame * (1 - reserve) : 0;
    const cCap = this.maxDrawCalls > 0 ? this.maxDrawCalls * (1 - reserve) : 0;
    const share = this.focusBudgetShare != null ? this.focusBudgetShare : 0.5;
    const tLim = tCap > 0 ? tCap * share : Infinity;
    const cLim = cCap > 0 ? cCap * share : Infinity;
    const admitted = new Set();
    const admittedRows = new Set();
    // WHICH (tile, hash) ROWS were admitted. The exemption used to be keyed by
    // HASH alone, so admitting one tile's three struts exempted every strut
    // in the model: on BA03 the budget reported 8.0 M while 105 M drew -
    // 13.1 M of it one strut hash (real data, 2026-10-13). _shouldDefer now
    // asks about the row it is deciding; the hash set stays for diagnostics.
    const admittedByTile = new Map();
    if (share > 0) {
      let fTris = 0; const fCalls = new Set();
      const nearestFirst = rows.filter((r) => r.focus).sort((a, b) => (a.d - b.d) || (b.rank - a.rank));
      for (const r of nearestFirst) {
        const addsCall = !fCalls.has(r.call) && !calls.has(r.call);
        if (fTris + r.tris > tLim || fCalls.size + (addsCall ? 1 : 0) > cLim) continue;
        fTris += r.tris; if (addsCall) fCalls.add(r.call);
        admitted.add(r.hash); admittedRows.add(r);
        let set = admittedByTile.get(r.tile);
        if (!set) { set = new Set(); admittedByTile.set(r.tile, set); }
        set.add(r.hash);
      }
      sum += fTris;
      fCalls.forEach((call) => calls.add(call));
    }
    this._focusAdmitted = admitted;
    this._focusAdmittedByTile = admittedByTile;
    let virtualSpent = 0;
    for (const r of rows) {
      if (admittedRows.has(r)) continue;
      const overTris = tCap > 0 && sum + r.tris > tCap;
      // An estimated row has no call of its own to count (see _anticipateUnseen).
      const overCalls = cCap > 0 && r.call != null && !calls.has(r.call) && calls.size + 1 > cCap;
      if (overTris || overCalls) { floor = r.rank; break; }
      sum += r.tris;
      if (r.virtual) virtualSpent += r.tris; else calls.add(r.call);
    }
    // What this plan holds back for tiles still on their way, apart from what
    // it spends on rows it has (diagnostics: perfSnapshot().deferral).
    this._anticipatedTris = anticipated;
    this._anticipatedSpent = virtualSpent;
    this._anticipatedTiles = unseen.length;
    sum -= virtualSpent;
    this._budgetTris = sum;
    this._budgetCalls = calls.size;
    this._budgetCallSet = calls;
    // A pass that could size NOTHING (camera looking at the sky, or nothing
    // in view yet) is not evidence that the budget is free: keep the floor,
    // or turning back would revive everything the floor was holding.
    if (rows.length > 0) {
      // Hysteresis so the floor does not flicker between rest windows.
      const next = floor > 0 ? floor : 0;
      if (Math.abs(next - this.dynamicMinPx) > Math.max(0.5, this.dynamicMinPx * 0.1)) this.dynamicMinPx = next;
    }
    // The interval runs from the END of the pass: a pass longer than
    // budgetRebalanceMs could otherwise run back to back (seen once on a
    // warm BA03 profile: 2-6 s passes every 2 s, no frames for minutes).
    if (passStart) this._budgetLastAt = now + Math.max(0, performance.now() - passStart);
  }

  // A tile's triangles at its distance band, from the manifest's per-tile
  // facts (trisPerLod), or null when the export does not carry them.
  _bandTris(tile, cameraPos) {
    const t = tile && tile.trisPerLod;
    if (!Array.isArray(t) || !t.length) return null;
    let lod = tile.lod == null ? 0 : tile.lod;
    if (this.lodSelector && typeof this.lodSelector.select === 'function' && tile.box) {
      lod = this.lodSelector.select(this._tileDistance(tile, cameraPos), lod);
    }
    const v = t[Math.max(0, Math.min(t.length - 1, lod | 0))];
    return typeof v === 'number' && v > 0 ? v : 0;
  }

  // THE TILES WHOSE SIDECARS HAVE NOT LANDED YET, ESTIMATED (round 11).
  //
  // The floor was set from the tiles it could see rows for. On a cold load
  // whose sidecars trickle in (Tom, r10 slow runs: 1,469 sidecars over 50-60 s)
  // that floor started near nothing (3.6 px at 31 s) and climbed to its real
  // value (47.5 px) only at 73 s - and every tile that installed on the way
  // installed against the low floor. 28 M triangles were drawn by the end of
  // the load against an 8 M budget, and the rest pass then had to rebuild
  // each of those tiles to retire the excess, one per tick: still 8.7 M over
  // two minutes later, the 1.6-1.8x (c) failure, and the same near tiles
  // rebuilt again and again as the floor climbed. The fast run, whose
  // sidecars were all in by 38 s, had its floor at 47.5 px by 30 s.
  //
  // So the tiles in view without rows yet now count, as estimated rows:
  //   HOW MANY TRIANGLES: the tile's manifest triangles at its band
  //   (trisPerLod), scaled by what the KNOWN tiles' rows actually cost
  //   against their own manifest figure (the wanted level is often coarser
  //   than the band) - or, on exports without trisPerLod, its row count
  //   times the known triangles per row.
  //   AT WHAT SIZES: the known rows' sizes (pixels x distance, which does
  //   not depend on where the camera is), spread over the tile at its own
  //   distance - a far tile's rows rank smaller.
  // They only take part in the greedy cut that sets the floor: never in the
  // focus share (they have no row to admit) and never as draw calls. When the
  // last sidecar lands there is nothing left to estimate and the pass is
  // exactly what it was. anticipateUnseen = false turns it off.
  //
  // The error goes the safe way: tiles still to come are on average FURTHER
  // than the ones that came first (the queue is nearest first), so their
  // rows really rank smaller than this spreads them - the early floor is a
  // little high and comes down as they land, which revives a few groups
  // (cheap) rather than rebuilding whole tiles (the cost this removes).
  _anticipateUnseen(unseen, rows, known, cameraPos, boost) {
    // A handful of tiles is too small a sample to speak for the rest.
    if (!rows.length || !(known.tris > 0) || known.tiles < ANTICIPATE_MIN_TILES) return null;
    // Sizes of the known rows, weighted by triangles, in quarter-octave bins.
    const sizeBins = new Map();
    let sized = 0;
    for (const r of rows) {
      const size = r.px * Math.max(r.d || 0, 1);
      if (!(size > 0) || !(r.tris > 0)) continue;
      const b = Math.floor(Math.log2(size) * 4);
      sizeBins.set(b, (sizeBins.get(b) || 0) + r.tris);
      sized += r.tris;
    }
    if (!(sized > 0)) return null;
    const perBand = known.bandTris > 0 ? known.trisWithBand / known.bandTris : null;
    const perRow = known.rowCount > 0 ? known.tris / known.rowCount : 0;
    const byRank = new Map();
    for (const tile of unseen) {
      const band = this._bandTris(tile, cameraPos);
      const est = band != null && perBand != null ? band * perBand : (tile.instanceCount || 0) * perRow;
      if (!(est > 0)) continue;
      const D = Math.max(1, tile.center ? tile.center.distanceTo(cameraPos) : this._tileDistance(tile, cameraPos));
      const mult = this.inFocus(tile) ? boost : 1;
      sizeBins.forEach((w, b) => {
        const rank = (Math.pow(2, (b + 0.5) / 4) / D) * mult;
        const rb = Math.floor(Math.log2(rank) * 4);
        byRank.set(rb, (byRank.get(rb) || 0) + est * (w / sized));
      });
    }
    if (!byRank.size) return null;
    const out = [];
    byRank.forEach((tris, rb) => out.push({ rank: Math.pow(2, (rb + 0.5) / 4), px: 0, tris, call: null, focus: false, virtual: true }));
    return out;
  }

  // Materialise the hashes of a tile that were deferred and now pass
  // the size test, under a virtual tile key. Called at camera rest.
  async _reviveDeferred(tile, distance, focalPx) {
    if (!tile._deferredHashes || !tile._deferredHashes.size || tile._reviving) return;
    const ready = [];
    const cameraPosNow = this.viewer.camera.position;
    const reviveScale = 1 + Math.max(0, Number(this.reviveMargin) || 0);
    tile._deferredHashes.forEach((instances, hash) => {
      // A clearance group stays deferred while the switch is off, or the
      // rest-window revive would put back what the toggle just removed.
      if (shouldHideGroup(hash, instances, this.clearanceState)) return;
      if (!this._shouldDefer(hash, this._groupDistance(instances, cameraPosNow), focalPx, instances, this.inFocus(tile), tile.key, reviveScale)) ready.push({ hash, instances });
    });
    if (!ready.length) return;
    tile._reviving = true;
    try {
      const lod = tile.lod == null ? 0 : tile.lod;
      const vkey = `${tile.key}#v${(tile._virtualSeq = (tile._virtualSeq || 0) + 1)}`;
      const allFragments = [];
      const mergedRecords = new Map();
      const claims = new Map();
      const batchItems = [];
      await Promise.all(ready.map(async ({ hash, instances }) => {
        // The level this group will draw at, decided BEFORE the fetch so
        // a split fragment downloads that level and not the fine one.
        const wantLod = this._lodForHash(hash, this._groupDistance(instances, cameraPosNow), focalPx,
          tile.lod == null ? 0 : tile.lod, null);
        let libraryParts = this.cache.getLibrary(hash);
        if (!libraryParts || !libraryParts[wantLod]) {
          const relUrl = this._resolveFragmentUrl(tile, hash);
          if (!relUrl) return;
          libraryParts = await this.fetcher.fetchFragment(hash, relUrl, wantLod, this._fetchDemand(tile, null, hash));
        }
        await this._budgetYield();
        // A small revived group goes into ONE batch for this revive, like a
        // first install would have put it in the tile batch - not into a
        // pool (= a draw call) of its own. Headless staging BA03 round 8:
        // of 3,621 visible pool meshes at rest, 2,187 were outside the
        // budget's call plan, most of them revived or cone-admitted groups
        // of 1-2 rows the plan had counted as part of the tile batch.
        const groupLod = this._lodForHash(hash, this._groupDistance(instances, cameraPosNow), focalPx, lod, libraryParts);
        if (this._batchEligible(instances, hash, groupLod)) {
          this._collectBatchItems(batchItems, hash, instances, libraryParts, groupLod);
          return;
        }
        const { fragments, records, _poolClaim } = InstanceMaterializer.materialize({
          libraryParts, tileKey: vkey, hash, instances,
          initialLod: this._lodForHash(hash, this._groupDistance(instances, cameraPosNow), focalPx, lod, libraryParts),
          poolRegistry: this.poolRegistry, applicator: this.applicator,
        });
        if (_poolClaim) claims.set(_poolClaim.hash, _poolClaim);
        fragments.forEach(f => allFragments.push(f));
        records.forEach((rec, uid) => mergedRecords.set(uid, rec));
      }));
      if (tile.state === TileState.UNLOADED) {
        // Unloaded while we were fetching: give the slots straight back.
        claims.forEach((claim, hash) => {
          const pool = this.poolRegistry.get(hash, claim.lod);
          if (!pool) return;
          for (const idx of claim.indices) pool.release(idx);
          pool.removeTile(vkey);
        });
        flushDirtyPools();
        return;
      }
      // ONE BATCH PER TILE, revives included. Round 8b gave each revive a
      // batch of its own - one more draw call per tile per revive, which the
      // budget counted as the tile's one batch call (staging BA03, headless:
      // 1,829 batch meshes in the scene against 1,481 tile batches). The
      // revived rows now join the tile's batch: it is rebuilt with every
      // existing item first, so those keep their ids and registrations, and
      // the new rows are registered under this revive's key as before.
      const grown = batchItems.length ? this._growTileBatch(tile, vkey, batchItems) : null;
      if (grown) {
        grown.batch.meshes.forEach((m) => allFragments.push(m));
        grown.added.forEach((rec, uid) => mergedRecords.set(uid, rec));
      }
      this.adapter.register(vkey, mergedRecords, allFragments);
      this._sweepRegistered(allFragments, claims);
      flushDirtyPools();
      (tile._virtualClaims = tile._virtualClaims || []).push({
        key: vkey, claims, batch: null, batchHashes: grown ? grown.hashes : null,
      });
      ready.forEach(({ hash }) => tile._deferredHashes.delete(hash));
      this._revivedHashCount += ready.length;
      thrashOf(this).tileLoaded(tile.key, 'revive');
      this._noteInstalled(tile);
      this._layoutSeq++;
    } finally {
      tile._reviving = false;
    }
  }

  // Drop every virtual (revived) registration of a tile and put its
  // hashes back on the deferred list, so a LOD swap or unload deals
  // with one key only.
  _foldVirtual(tile, backToDeferred) {
    if (!tile._virtualClaims || !tile._virtualClaims.length) return;
    for (const v of tile._virtualClaims) {
      this.adapter.unregister(v.key);
      // Revived rows that went into the TILE's batch: the batch is the
      // tile's and goes with its next rebuild or unload; only the hashes
      // need handing back.
      if (v.batchHashes && backToDeferred && tile.instancesByHash) {
        v.batchHashes.forEach((h) => { if (tile.instancesByHash.has(h)) (tile._deferredHashes = tile._deferredHashes || new Map()).set(h, tile.instancesByHash.get(h)); });
      }
      if (v.batch) {
        if (backToDeferred && tile.instancesByHash) {
          const hs = new Set(); this._inTileBatch({ _batch: v.batch }, '');
          if (v.batch._hashes) v.batch._hashes.forEach((h) => hs.add(h));
          hs.forEach((h) => { if (tile.instancesByHash.has(h)) (tile._deferredHashes = tile._deferredHashes || new Map()).set(h, tile.instancesByHash.get(h)); });
        }
        this._disposeBatch(v.batch);
        v.batch = null;
      }
      v.claims.forEach((claim, hash) => {
        const pool = this.poolRegistry.get(hash, claim.lod);
        if (!pool) return;
        for (const idx of claim.indices) pool.release(idx);
        pool.removeTile(v.key);
        if (backToDeferred && tile.instancesByHash && tile.instancesByHash.has(hash)) {
          (tile._deferredHashes = tile._deferredHashes || new Map()).set(hash, tile.instancesByHash.get(hash));
        }
      });
    }
    tile._virtualClaims = [];
  }

  // A hash with very few rows in this tile is cheaper drawn inside the
  // tile's batch than given an InstancedMesh of its own. Everything
  // denser stays in the pools, where instancing still pays.
  //
  // With the hash, a group of up to batchFoldMaxInstances rows is folded in
  // too when copying it is cheap: rows x the fragment's finest-level
  // triangles <= batchFoldMaxTris. Unknown size keeps the pool. Every
  // caller that decides WHERE a group draws, and every pass that COUNTS the
  // call it costs (budget, cone, stale), asks this same question with the
  // same arguments, so the plan and the scene agree.
  _batchEligible(instances, hash = null, lod = null) {
    if (!this.batching || !instances || instances.length === 0) return false;
    const n = instances.length;
    if (n <= this.batchMaxInstances) return true;
    const fold = this.batchFoldMaxInstances || 0;
    if (hash == null || n > fold) return false;
    const meta = this._metaOf(hash);
    if (!meta || !Array.isArray(meta.t)) return false;
    // The triangles that would be COPIED: the level it draws at when the
    // caller knows it, else the finest (the most a copy could cost).
    let each = (lod != null && typeof meta.t[lod] === 'number') ? meta.t[lod] : 0;
    if (!(each > 0)) for (const v of meta.t) if (typeof v === 'number' && v > each) each = v;
    return each > 0 && n * each <= (this.batchFoldMaxTris || 0);
  }

  // The draw call a group costs, as the plan counts it: its (hash, LOD)
  // pool, or its tile's batch - and a batch is TWO meshes when the tile has
  // blended items as well as opaque ones (the blended pass needs its own
  // draw), so a group whose material is known to be blended counts against
  // the tile's blend batch. Unknown (no library yet) counts as opaque.
  // Headless staging BA03 at rest: 333 of 1,681 batch calls were blend
  // batches the plan had folded into the tile's one.
  _callKey(tile, hash, lod, instances) {
    if (!this._batchEligible(instances, hash, lod)) return `${hash}|${lod}`;
    return this._isBlended(hash, lod) ? `b|${tile.key}|blend` : `b|${tile.key}`;
  }

  _isBlended(hash, lod) {
    const lib = this.cache && typeof this.cache.getLibrary === 'function' ? this.cache.getLibrary(hash) : null;
    if (!Array.isArray(lib)) return false;
    let slot = lib[lod];
    if (!slot) for (const x of lib) if (x) { slot = x; break; }
    return !!(slot && slot.material && slot.material.transparent);
  }

  // Collect one hash's rows as batch items. Same coincident-transform
  // dedupe as the pool path: two identical rows of one geometry render
  // to the same pixels, and the picker can only resolve one of them, so
  // the loser wins z-fighting and selection looks broken.
  _collectBatchItems(into, hash, instances, libraryParts, lod) {
    const slot = libraryParts && libraryParts[lod];
    if (!slot || !slot.geometry) return 0;
    const seen = new Set();
    let taken = 0;
    if (instances.length > this.batchMaxInstances) this._foldedGroups++;
    for (const entry of instances) {
      const key = coincidentKey(entry);
      if (seen.has(key)) continue;
      seen.add(key);
      into.push({ hash, instance: entry, geometry: slot.geometry, material: slot.material, lod });
      taken++;
    }
    return taken;
  }

  // Rebuild the tile's batch with `added` items appended, registered (by
  // the caller) under `ownerKey`. Existing items go in first and in their
  // original order, so each keeps its id in a mesh of the same kind; every
  // reference the adapter holds to an old mesh is then moved to its
  // replacement (_refreshMeshRef - the same index a pool grow uses, so this
  // touches only what points at this tile's batch, never a walk of the
  // model). State (hidden, isolated, colours, duplicates) is re-derived by
  // the caller's post-register sweep over the new meshes (_sweepRegistered
  // applies the whole of a non-pool mesh), a sweep of this tile's batch
  // alone; all of it runs synchronously, so no frame sees the new batch
  // before its state is laid on.
  // Returns { batch, added: Map(uid -> record), hashes: Set } or null.
  _growTileBatch(tile, ownerKey, added) {
    if (!added || added.length === 0) return null;
    for (const it of added) it.ownerKey = ownerKey;
    const old = tile._batch;
    // IN PLACE FIRST. Round 9 rebuilt the whole tile batch on every revive:
    // a new BatchedMesh, every existing item's attributes copied again,
    // the whole buffer uploaded again, and - the mesh being new - three's
    // lazy per-item bounds scanned again. When the batch has room (a batch
    // that was rebuilt by a grow keeps headroom for exactly this), the new
    // rows are appended: nothing existing is copied, no reference moves.
    if (old && typeof old.append === 'function') {
      const appended = old.append(added);
      if (appended) {
        // The per-batch indexes (_batchLodOf, _inTileBatch) were built from
        // the items as they were; they are rebuilt on next use.
        old._lods = null;
        old._hashes = null;
        this._batchedItems += appended.size;
        this._batchAppends = (this._batchAppends || 0) + 1;
        return { batch: old, added: appended, hashes: new Set(added.map((it) => it.hash)) };
      }
    }
    const before = old && Array.isArray(old.items) ? old.items : [];
    const built = this._buildBatchForTile(tile, before.concat(added), this.batchGrowHeadroom);
    if (!built) return null;
    const addedUids = new Set(added.map((it) => it.instance && it.instance.uniqueId));
    const addedRecords = new Map();
    built.records.forEach((rec, uid) => { if (addedUids.has(uid)) addedRecords.set(uid, rec); });
    if (old) {
      const byKind = new Map(built.meshes.map((m) => [m.userData.batchKind, m]));
      const moved = new Map();
      for (const om of old.meshes) {
        const nm = byKind.get(om.userData && om.userData.batchKind);
        if (!nm) continue;
        moved.set(om, nm);
        if (this.adapter && typeof this.adapter._refreshMeshRef === 'function') this.adapter._refreshMeshRef(om, nm);
      }
      // The records the old batch handed to register (owner records are
      // also in viewer.meshesObject): _refreshMeshRef moved the owners'
      // .mesh; this moves the rest and the .batch aliases.
      old.records.forEach((rec, uid) => {
        if (moved.has(rec.mesh)) rec.mesh = moved.get(rec.mesh);
        if (moved.has(rec.batch)) rec.batch = moved.get(rec.batch);
        if (Array.isArray(rec.fragments)) {
          for (const f of rec.fragments) {
            if (moved.has(f.mesh)) f.mesh = moved.get(f.mesh);
            if (moved.has(f.batch)) f.batch = moved.get(f.batch);
          }
        }
        // Keep the identity the adapter registered, not the rebuild's copy.
        if (!addedUids.has(uid)) built.records.set(uid, rec);
      });
      this._disposeBatch(old);
    }
    tile._batch = built;
    this._batchGrows++;
    const hashes = new Set(added.map((it) => it.hash));
    return { batch: built, added: addedRecords, hashes };
  }

  // Build this tile's batch from the items collected during
  // materialisation. Returns the meshes so the caller can register them
  // with the adapter alongside the pool meshes.
  _buildBatchForTile(tile, batchItems, headroom = 0) {
    if (!batchItems || batchItems.length === 0) return null;
    const built = buildTileBatch({
      tileKey: tile.key,
      items: batchItems,
      sceneRoot: this.poolRegistry && this.poolRegistry.sceneRoot,
      viewer: this.viewer,
      headroom,
    });
    if (!built) return null;
    this._batchedTiles++;
    this._batchedItems += built.itemCount;
    return built;
  }

  // The merged singleton mesh for this tile at this level of detail, or
  // null when the export has none for it. One fetch and one parse per
  // tile, cached by URL like every other sidecar.
  async _loadTileMerge(tile, lodIndex) {
    if (!this.singletonMerge || !tile.merge) return null;
    const lodName = MERGE_LOD_NAMES[lodIndex];
    if (!lodName) return null;
    const relative = tile.merge[lodName];
    if (!relative) return null;
    // Already has this one. A tile is materialised more than once - a
    // forced re-materialisation, a load that raced an unload - and
    // rebuilding its merge each time both wasted the parse and left the
    // previous mesh in the scene: 7,166 merges alive against a model of
    // 1,377 tiles, drawing on top of each other, which is why draw calls
    // went UP when the whole point was to bring them down.
    if (tile._merge && tile._merge.lodIndex === lodIndex) return tile._merge;
    // Over budget: this tile takes the fragment path. Counted rather than
    // silent, because "merges stopped helping" and "merges stopped
    // loading" look identical from the outside.
    if (this._mergeBytes >= this.maxMergeBytes) {
      this._mergeBudgetSkips++;
      return null;
    }
    // And two runs for the same tile share one load rather than racing.
    if (tile._mergeLoading && tile._mergeLoadingLod === lodIndex) return tile._mergeLoading;
    const load = (async () => {
    try {
      const url = this.manifestLoader.sidecarUrlBuilder(`${tile.key}:${lodName}`, relative);
      const buffer = await this.manifestLoader.loadBinary(url, 'merge');
      const gltf = await parseMergeBuffer(buffer, this.fetcher && this.fetcher.workerPool);
      const rowsByUid = new Map();
      tile.instancesByHash.forEach((instances) => {
        for (const row of instances) rowsByUid.set(row.uniqueId, row);
      });
      const built = buildMergeMesh({
        gltf,
        tileKey: tile.key,
        lodName,
        lodIndex,
        rowsByUid,
        sceneRoot: this.poolRegistry && this.poolRegistry.sceneRoot,
        viewer: this.viewer,
      });
      if (!built) {
        this._mergeFailures++;
        return null;
      }
      this._mergedTiles++;
      this._mergedItems += built.itemCount;
      this._mergeBytes += built.bytes || 0;
      if (built.sortedByRadius) this._mergeSortedSeen++;
      return built;
    } catch (e) {
      // A tile whose merge will not load simply materialises its
      // singletons the old way. The reason is kept: "merges are failing"
      // and "merges are being refused" look identical in the counters
      // and are entirely different problems.
      this._mergeFailures++;
      this._mergeLastError = (e && e.message) ? String(e.message).slice(0, 200) : 'unknown';
      return null;
    }
    })();
    tile._mergeLoading = load;
    tile._mergeLoadingLod = lodIndex;
    try {
      return await load;
    } finally {
      tile._mergeLoading = null;
    }
  }

  // Apply the pixel floor to a merged tile, element by element.
  //
  // The fragment path skips a fragment that would cover less than
  // minFragmentPixels; a merged mesh is one object, so without this it
  // draws everything it holds the moment its tile is claimed. Measured
  // at an aerial view of SpaceX that tripled the triangle count. Each
  // element carries its own centre and radius (from the export when it
  // ships them, derived from the geometry when it does not), so the same
  // test that governs a fragment governs a merged element.
  // `candidate` is for the merge a tile has just loaded and not yet
  // installed: the acceptance test has to measure the thing it is about
  // to keep, not the one the tile is still holding. Reading tile._merge
  // here measured either the previous merge or nothing, which is how a
  // fresh merge could be judged on another merge's numbers - and why
  // mergeDrawnItems read zero while the merges were plainly drawing.
  _applyMergeSizes(tile, cameraPos, focalPx, candidate = null) {
    const merge = candidate || (tile && tile._merge);
    if (!merge) return 0;
    // Without per-element bounds nothing can be culled, so everything it
    // holds is drawn - and saying so is the point. Left unset, this read
    // as zero drawn beside forty thousand held, which looks like a merge
    // paying for nothing rather than a merge with no sizes.
    if (!merge.centres || !merge.radii) {
      merge.drawnItems = merge.itemCount || 0;
      merge.boundsMissing = true;
      this._mergeBoundsMissing++;
      return merge.drawnItems;
    }
    const floor = Math.max(this.minFragmentPixels * (this.quality || 1), this.dynamicMinPx || 0);
    const centre = this._mergeCentre || (this._mergeCentre = new Vector3());
    // Through the merged mesh's OWN world matrix rather than the pool
    // root's: the two are the same only while the export carries no node
    // transform of its own. Reading the mesh's matrix is correct either
    // way, and an element measured in the wrong frame gets a distance
    // from somewhere it is not, which makes the size test meaningless
    // without making it look broken.
    const toWorld = merge.mesh && merge.mesh.matrixWorld;

    // How much is SUBMITTED, decided by the same per-element test that
    // decides what is DRAWN.
    //
    // The prefix used to be estimated from the tile's own distance, on
    // the reasoning that elements are ordered largest first so a floor
    // picks a count. Two things were wrong with that. A tile's distance
    // is to its nearest face, which is nearer than most of the elements
    // inside it, so the estimate kept more than it needed to; and it
    // measured size with the fragment index's radius while the
    // per-element pass used each element's own, so the submitted range
    // and the drawn set disagreed about the same element.
    //
    // Measured cost of that: with merges on, 35 % more triangles per
    // visible tile for a 4 % draw-call saving, while the prefix looked
    // 92 % "drawn" - because the elements it wrongly kept were being
    // switched off one texel at a time instead of left out of the
    // submission entirely. The pooled path drops a whole group below the
    // floor and draws nothing for it; this is the merge doing the same
    // thing properly.
    //
    // So the per-element pass runs first, over everything the merge
    // holds, and the prefix is then exactly deep enough to reach the
    // last element that passed. Ordered largest first, that is the
    // shortest contiguous range containing every visible element: it
    // cannot hide something that should draw, and it stops submitting a
    // tail that was only ever going to be switched off. A pinned element
    // passes the test itself, so it pulls the range with it and needs no
    // separate pass.
    let drawn = 0;
    let deepestVisible = -1;
    for (let i = 0; i < merge.radii.length; i++) {
      const radius = merge.radii[i];
      centre.set(merge.centres[i * 3], merge.centres[i * 3 + 1], merge.centres[i * 3 + 2]);
      if (toWorld) centre.applyMatrix4(toWorld);
      const distance = centre.distanceTo(cameraPos);
      const px = radius > 0 ? (radius * 2 * focalPx) / Math.max(distance, 1) : Infinity;
      // Handed to the fragment path: not drawn here however big it is,
      // and not counted as drawn either or the acceptance fraction
      // reports geometry this mesh is not putting on screen.
      if (typeof merge.isItemClaimed === 'function' && merge.isItemClaimed(i)) {
        merge.setItemSizeVisible(i, false);
        continue;
      }
      const pinned = !!(this.pinnedUids && merge.uidList
        && this.pinnedUids.has(String(merge.uidList[i])));
      const visible = this.drawEverything || pinned || px >= floor;
      if (visible) { drawn++; deepestVisible = i; }
      merge.setItemSizeVisible(i, visible);
    }
    if (merge.sortedByRadius && typeof merge.setDrawPrefix === 'function') {
      merge.setDrawPrefix(deepestVisible + 1);
      merge.submittedItems = deepestVisible + 1;
    } else {
      merge.submittedItems = merge.radii.length;
    }
    merge.drawnItems = drawn;
    return drawn;
  }

  _disposeMerge(merge) {
    if (!merge) return;
    this._mergedTiles = Math.max(0, this._mergedTiles - 1);
    this._mergedItems = Math.max(0, this._mergedItems - merge.itemCount);
    this._mergeBytes = Math.max(0, this._mergeBytes - (merge.bytes || 0));
    merge.dispose();
  }

  // Is this group already drawn by the tile's merged mesh? Only a group
  // of one row can be, and only when the level of detail it would have
  // been drawn at is no finer than the merge's.
  _coveredByMerge(merge, instances, chosenLod) {
    if (!merge || !instances || instances.length !== 1) return false;
    if (chosenLod < merge.lodIndex) return false;
    return merge.uids.has(instances[0].uniqueId);
  }

  // The fragment path is about to draw an element the merge also holds -
  // because it wants it FINER than the merge's level of detail, which is
  // the one case _coveredByMerge deliberately does not cover. Without
  // this the element was drawn twice, in two places, at two levels of
  // detail, and only one of them could be picked. Measured on the prod
  // SpaceX model, packing on submitted 35 % more triangles per visible
  // tile than packing off, which is the shape of a duplicate rather than
  // of a saving.
  _claimFromMerge(merge, instances) {
    if (!merge || !instances || typeof merge.claim !== 'function') return;
    for (const inst of instances) {
      if (inst && merge.uids.has(inst.uniqueId)) {
        merge.claim(inst.uniqueId);
        this._mergeClaimed = (this._mergeClaimed || 0) + 1;
      }
    }
  }

  _releasePoolClaims(claims, tileKey) {
    if (!claims) return;
    claims.forEach((claim, hash) => {
      const pool = this.poolRegistry.get(hash, claim.lod);
      if (!pool) return;
      for (const idx of claim.indices) pool.release(idx);
      pool.removeTile(tileKey);
    });
  }

  // Draw what a still-loading tile has so far (see `progressiveFlushMs`):
  // upload the pools, and build the long-tail items that arrived since
  // the last flush into a chunk. Never for a run that has gone stale.
  _progressiveFlush(tile, epoch, batchItems, progress) {
    if (!(this.progressiveFlushMs > 0) || tile._materializeEpoch !== epoch) return;
    const now = performance.now();
    if (now - progress.lastAt < this.progressiveFlushMs) return;
    progress.lastAt = now;
    this._progressiveFlushes++;
    // Marking pools dirty is cheap (three uploads each at most once per
    // frame); rebuilding a batch chunk is not, so chunks keep their own,
    // slower interval.
    flushDirtyPools();
    if (now - (progress.chunkAt || 0) < (this.progressiveChunkMs || 0)) return;
    if (batchItems.length - progress.built < this.progressiveMinItems) return;
    progress.chunkAt = now;
    const chunk = buildTileBatch({
      tileKey: tile.key,
      items: batchItems.slice(progress.built),
      sceneRoot: this.poolRegistry && this.poolRegistry.sceneRoot,
      viewer: this.viewer,
      role: 'chunk',
    });
    progress.built = batchItems.length;
    if (chunk) {
      progress.chunks.push(chunk);
      this._progressiveChunks++;
    }
  }

  // Early chunks are the tile's own, like its batch. Emptied in place so
  // a second call (unload, then the stale run finishing) is a no-op.
  _disposeChunks(chunks) {
    if (!chunks) return;
    for (const c of chunks.splice(0)) {
      try { c.dispose(); } catch (e) { /* already gone */ }
    }
  }

  _disposeBatch(batch) {
    if (!batch) return;
    this._batchedTiles = Math.max(0, this._batchedTiles - 1);
    this._batchedItems = Math.max(0, this._batchedItems - batch.itemCount);
    batch.dispose();
  }

  // Returns whether the tile actually ended up with geometry, which the
  // caller needs in order to decide between "visible" and "try again".
  async _materializeTile(tile, lod) {
    if (this.adapter.has(tile.key)) {
      this._applyLodSwap(tile, lod);
      return true;
    }
    tile.state = TileState.FRAGMENTS_LOADING;
    this._bumpInput();   // a loading tile counts in the budget pass
    tile._materializing = true;
    tile._materializeProgressAt = performance.now();
    try {
      const installed = await this._materializeTileBody(tile, lod);
      // A run thrown away (unloaded or superseded while in flight) will be
      // done again: counted as an unload so the next load is a reload.
      if (installed === false) thrashOf(this).tileUnloaded(tile.key, 'stale');
      else { thrashOf(this).tileLoaded(tile.key, 'load'); this._noteInstalled(tile); }
      return installed;
    } finally {
      tile._materializing = false;
    }
  }

  async _materializeTileBody(tile, lod) {
    // Which materialisation this is.
    //
    // The body awaits fetches and frame budgets, so a tile can be
    // unloaded - or materialised again - while one is still in flight.
    // Whatever that run then installs is attached to a tile nobody is
    // tracking any more: the mesh stays in the scene, keeps drawing, and
    // is never disposed, because the only handle to it was overwritten.
    // At an aerial framing that showed up as 9,571 merges built against
    // 1,377 tiles in the model, draw calls nearly doubled and the frame
    // time with them. A run whose epoch no longer matches throws its
    // work away instead of installing it.
    const epoch = (tile._materializeEpoch = (tile._materializeEpoch || 0) + 1);
    const allFragments = [];
    const mergedRecords = new Map();
    // Rows destined for this tile's batch rather than for a pool.
    const batchItems = [];

    const hashesToFetch = [];
    const missingHashes = [];
    const poolClaims = new Map();
    // Screen-size / heavy deferral: see _shouldDefer.
    const deferred = new Map();
    const cameraPosNow = this.viewer.camera.position;
    const focalPx = this._focalPx();

    // Below the two declarations above, and it must stay there. This
    // block read `cameraPosNow` and `focalPx` while sitting ABOVE their
    // `const`s, which is a temporal dead zone: the moment a tile
    // actually had a merge, this method threw ReferenceError before
    // reaching anything that counts. _beginLoad caught it, logged it and
    // set the tile back to IDLE, so every merge-carrying tile loaded,
    // was counted as seen, and installed nothing. Four captures froze at
    // 237 tiles - the ones with no merge - with kept, rejected, failed,
    // bounds-missing and budget-skipped all reading zero.
    //
    // The tile's merged singleton mesh, fetched before the groups are
    // walked because what it covers must not also be materialised: the
    // element would then be drawn twice, in two places, and the picker
    // could only resolve one of them. One small request buys the tile
    // dozens of fragment fetches it no longer has to make.
    // Installed at the end, with everything else, so a run that turns
    // out to be stale leaves no mesh behind.
    let merge = await this._loadTileMerge(tile, lod);
    if (merge) {
      // How much of it would actually draw from here. A tile seen from
      // far enough away that the floor rejects most of its singletons is
      // better served by fragments: they cost their fetches but submit
      // only what they draw. The merge is dropped rather than kept and
      // hidden, because a hidden element in a plain Mesh still costs its
      // triangles every frame.
      const drawn = this._applyMergeSizes(tile, cameraPosNow, focalPx, merge);
      const fraction = merge.itemCount > 0 ? drawn / merge.itemCount : 0;
      // A merge that can cull by prefix costs only what it draws, so
      // there is nothing to protect against: it is kept however little
      // of it passes the floor. The threshold exists for the older
      // merges, which submit everything they hold.
      if (!merge.sortedByRadius && fraction < this.mergeMinDrawnFraction) {
        this._mergesRejected++;
        this._disposeMerge(merge);
        merge = null;
      }
    }

    // For each hash this tile owns, either reuse the existing pool (if
    // the library is already cached) or queue a fragment fetch first.
    // Work is budgeted per animation frame (_budgetYield) so a tile
    // with thousands of rows does not freeze the page.
    for (const [hash, allRows] of tile.instancesByHash) {
      const deferDistance = this._groupDistance(allRows, cameraPosNow);
      if (this._shouldDefer(hash, deferDistance, focalPx, allRows, this.inFocus(tile), tile.key)) { deferred.set(hash, allRows); continue; }
      // Clearance shells are not part of the model somebody opened; they are
      // an overlay they can ask for. Held in the SAME deferred map, so the
      // toggle revives them exactly like a size-deferred group.
      if (shouldHideGroup(hash, allRows, this.clearanceState)) { deferred.set(hash, allRows); continue; }

      // The group is worth drawing; some of its ROWS may still not be. The
      // group test works from one centroid, so it cannot see this. Held rows
      // go to tile._pixelHeld and are revived with the deferred ones.
      const instances = this._pixelSkipRows(tile, hash, allRows, focalPx, cameraPosNow, deferred);
      if (!instances.length) continue;
      const libraryParts = this.cache.getLibrary(hash);
      if (libraryParts) {
        await this._budgetYield(tile);
        const chosenLod = this._lodForHash(hash, deferDistance, focalPx, lod, libraryParts);
        if (this._coveredByMerge(merge, instances, chosenLod)) continue;
        this._claimFromMerge(merge, instances);
        if (this._batchEligible(instances, hash, chosenLod)) {
          this._collectBatchItems(batchItems, hash, instances, libraryParts, chosenLod);
          continue;
        }
        const { fragments, records, _poolClaim } = InstanceMaterializer.materialize({
          libraryParts,
          tileKey: tile.key,
          hash,
          instances,
          initialLod: this._lodForHash(hash, deferDistance, focalPx, lod, libraryParts),
          poolRegistry: this.poolRegistry,
          applicator: this.applicator,
        });
        if (_poolClaim) poolClaims.set(_poolClaim.hash, _poolClaim);
        fragments.forEach(f => allFragments.push(f));
        records.forEach((rec, uid) => mergedRecords.set(uid, rec));
      } else {
        // Covered by the merge and no finer than it: the fragment is
        // already drawn, so it is not even downloaded. This is where the
        // merge pays for itself twice.
        if (this._coveredByMerge(merge, instances, lod)) continue;
        const relUrl = this._resolveFragmentUrl(tile, hash);
        if (!relUrl) {
          missingHashes.push(hash);
          continue;
        }
        hashesToFetch.push({ hash, relUrl, instances });
      }
    }

    if (missingHashes.length > 0) {
      this._totalMissingHashesSkipped += missingHashes.length;
      const sample = missingHashes[0].length > 80
        ? missingHashes[0].slice(0, 80) + '...'
        : missingHashes[0];
      console.warn(
        `[StreamingController] tile ${tile.key}: ${missingHashes.length} ` +
        `hash(es) missing from fragmentUrls (skipping). First: ${sample}`
      );
    }

    // Per-tile fetch fairness: at most maxFetchesPerTile fragment
    // downloads in flight for one tile, so a 2,000-fragment leaf shares
    // the fetcher's slots with its siblings instead of owning them.
    // Adaptive cap: the per-tile limit exists so a 360-fragment tile
    // cannot starve its siblings; when few tiles are loading (the tail:
    // the six dense sub-tiles of SpaceX took ~20 s each while the
    // fetcher sat mostly idle) the tile may use the idle capacity.
    const fetchCap = Math.max(
      this.inColdStart() ? this.coldMaxFetchesPerTile : this.maxFetchesPerTile,
      Math.floor(((this.fetcher && this.fetcher.maxConcurrentFetches) || 96) / Math.max(1, this._activeTileLoads))
    );
    // Nearest first when fetch slots are contended (see slotQueue): the
    // tiles the camera points at, then by distance.
    // What has been drawn early (see `progressiveFlushMs`). On the tile so
    // an unload mid-load can take the chunks away with it.
    const progress = { lastAt: performance.now(), chunkAt: performance.now(), built: 0, chunks: [] };
    tile._batchChunks = progress.chunks;
    // Global order: the tile's largest fragments first, and all of them in
    // the fetcher's queue at once so its ranking - across every loading
    // tile - decides what downloads next, rather than this tile's cap.
    if (this.globalOrder) {
      const d = this._tileDistance(tile, this.viewer.camera.position);
      hashesToFetch.sort((a, b) => (this._pxOf(b.hash, d, focalPx) || 0) - (this._pxOf(a.hash, d, focalPx) || 0));
    }
    const loopCap = this.globalOrder ? Math.max(fetchCap, hashesToFetch.length) : fetchCap;
    await this._mapLimit(hashesToFetch, loopCap, async ({ hash, relUrl, instances }) => {
      // Ask for the level this group will draw at: a split fragment then
      // fetches that file instead of the fine one.
      const wantLod = this._lodForHash(hash, this._groupDistance(instances, this.viewer.camera.position),
        focalPx, lod, null);
      let libraryParts;
      try {
        libraryParts = await this.fetcher.fetchFragment(hash, relUrl, wantLod, this._fetchDemand(tile, epoch, hash));
      } catch (err) {
        // Dropped because this run went stale: the epoch check after the
        // loop throws the run's work away, so there is nothing to do here.
        if (err && err.cancelled) return;
        throw err;
      }
      tile._materializeProgressAt = performance.now();   // a fragment arrived: progress
      await this._budgetYield(tile);
      const deferDistance = this._groupDistance(instances, this.viewer.camera.position);
      const chosenLod = this._lodForHash(hash, deferDistance, focalPx, lod, libraryParts);
      if (this._coveredByMerge(merge, instances, chosenLod)) return;
      this._claimFromMerge(merge, instances);
      if (this._batchEligible(instances, hash, chosenLod)) {
        this._collectBatchItems(batchItems, hash, instances, libraryParts, chosenLod);
        this._progressiveFlush(tile, epoch, batchItems, progress);
        return;
      }
      const { fragments, records, _poolClaim } = InstanceMaterializer.materialize({
        libraryParts,
        tileKey: tile.key,
        hash,
        instances,
        initialLod: this._lodForHash(hash, deferDistance, focalPx, lod, libraryParts),
        poolRegistry: this.poolRegistry,
        applicator: this.applicator,
      });
      if (_poolClaim) poolClaims.set(_poolClaim.hash, _poolClaim);
      fragments.forEach(f => allFragments.push(f));
      records.forEach((rec, uid) => mergedRecords.set(uid, rec));
      this._progressiveFlush(tile, epoch, batchItems, progress);
    });

    tile._poolClaims = poolClaims;
    tile._deferredHashes = deferred.size ? deferred : null;
    this._deferredHashCount += deferred.size;

    // One BatchedMesh per material for this tile's long tail, sized from
    // the geometries themselves. Built after the fetch loop so the size
    // is exact: a BatchedMesh allocates its buffers up front and cannot
    // grow.
    // Stale run: the tile was unloaded or re-materialised while this one
    // was awaiting. Drop what it built rather than attaching it to a
    // tile that is no longer this materialisation's.
    if (tile._materializeEpoch !== epoch) {
      // Counted, because a merge that is loaded, parsed and then dropped
      // by a newer run of its own tile was previously silent: not kept,
      // not rejected, not failed, just gone. Four captures could not say
      // where the merges went, and this is where.
      if (merge) this._mergeStaleDropped++;
      this._disposeMerge(merge);
      this._disposeChunks(progress.chunks);
      this._releasePoolClaims(poolClaims, tile.key);
      this._staleMaterializations++;
      // FALSE, not silence. The caller marks a tile VISIBLE when this
      // returns, and a tile marked visible with nothing in it is never
      // re-queued - the load scheduler only considers idle and unloaded
      // tiles. Before the epoch guard the same race installed onto a
      // tile that had been unloaded, which leaked a mesh but at least
      // drew geometry; discarding the work without saying so turned that
      // leak into a tile that is permanently empty and permanently
      // "loaded". On SpaceX that stalled the whole model at 210 of 1,377
      // tiles with every counter frozen.
      return false;
    }

    if (batchItems.length > 0) {
      await this._budgetYield(tile);
      // One chunk that already holds every item IS the final batch;
      // anything else is rebuilt as one, so the finished tile has the
      // draw calls it always had.
      let built;
      if (progress.chunks.length === 1 && progress.built === batchItems.length) {
        built = progress.chunks.pop();
        built.meshes.forEach((m) => { m.userData.batchRole = 'tile'; });
        this._batchedTiles++;
        this._batchedItems += built.itemCount;
      } else {
        built = this._buildBatchForTile(tile, batchItems);
      }
      if (built) {
        this._disposeBatch(tile._batch);
        tile._batch = built;
        built.meshes.forEach(m => allFragments.push(m));
        built.records.forEach((rec, uid) => mergedRecords.set(uid, rec));
      }
    }
    // The early chunks go only now, after the final batch is in the
    // scene, so no frame shows the tile without them.
    this._disposeChunks(progress.chunks);
    if (tile._batchChunks === progress.chunks) tile._batchChunks = null;

    if (merge) {
      this._mergeKept++;
      this._disposeMerge(tile._merge);
      tile._merge = merge;
      allFragments.push(merge.mesh);
      merge.records.forEach((rec, uid) => mergedRecords.set(uid, rec));
    }

    this.adapter.register(tile.key, mergedRecords, allFragments);

    // === POST-REGISTER SWEEP — single-active-instance enforcement ===
    //
    // The materializer's per-batch applyAll runs BEFORE this register
    // call, so it can't know which slots are duplicates that need to
    // stay zero-matrixed. As a result, when a NEW tile materializes,
    // its applyAll re-writes snapshot matrices to slots in OTHER tiles
    // (within the same pool) that were previously zeroed by their own
    // register — un-hiding cross-tile duplicates and producing the
    // "hide doesn't fully hide" + "isolate shows ghost geometry"
    // symptoms.
    //
    // Re-running applyAll with the adapter threaded through now
    // enforces the single-active-instance invariant: every slot whose
    // owner doesn't match its own tileKey collapses to zero matrix.
    // Per-pool dedup so we sweep each unique pool mesh once.
    this._sweepRegistered(allFragments, poolClaims);

    // Batched GL upload: pool.acquire/release queued dirty pools rather
    // than setting needsUpdate inline. One flush per materialize → one
    // GL re-upload per touched mesh per batch (vs. one per slot writes
    // before). This is the biggest single win for cold-load speed.
    flushDirtyPools();
    this._layoutSeq++;
    return true;
  }

  // Per-tile LOD migration — ATOMIC. The naive sequence (unload → re-
  // materialize) opens a multi-frame visible gap when hundreds of tiles
  // migrate at once on camera-rest, producing the "dissolve to see-
  // through" symptom users observed when zooming into dense areas.
  //
  // Atomic order: acquire NEW slots first, swap the adapter, THEN
  // release the old slots. All synchronous JavaScript — the browser
  // cannot inject a render frame mid-call. From the renderer's POV:
  // frame N shows the OLD pool's slots; frame N+1 shows the NEW pool's
  // slots; never a frame with nothing.
  //
  // If any required library is NOT cached (rare — would mean the LRU
  // evicted it while the tile was visible), fall back to the old
  // unload+materialize path. That path still has the flicker but
  // only fires in this edge case.
  // `force` re-materialises at the SAME LOD so the deferral set can be
  // re-evaluated (a tile loaded up close keeps its tiny fittings after
  // the user zooms out unless something retires them).
  // `reason` is for the thrash ledger only: 'lod', 'coarsen', 'clearance',
  // 'retire:<why>'. Defaults to 'retire' for a forced swap, else 'lod'.
  _applyLodSwap(tile, newLod, force = false, reason = null) {
    if (newLod === tile.lod && !force) return;
    if (!this.adapter.has(tile.key)) {
      tile.lod = newLod;
      this._layoutSeq++;
      return;
    }
    const cameraPosNow = this.viewer.camera.position;
    const focalPx = this._focalPx();

    // Fast-path check: every hash's library must be cached or the swap
    // would have to do an async fetch. The previous behavior on a cache
    // miss was to _unload the tile and asynchronously re-materialize
    // it — that produced a multi-frame visible gap where the tile was
    // gone (the "see-through dissolve when zooming"). Skipping the
    // swap entirely when libs aren't cached is the safer path: the
    // tile stays at its current LOD until the user holds still long
    // enough for the next tick, by which point the library may have
    // been fetched as a side effect of the regular streaming pipeline.
    // Hashes that will be deferred after this swap need no library;
    // everything else must be cached or the swap would have to fetch.
    //
    // ONLY WHAT IS DRAWN NOW CAN BLOCK THE SWAP. A group that is not drawn
    // (deferred, never fetched) and now passes the size test used to make
    // allCached false, so the swap was refused - and the rest pass, which
    // only revives (and so fetches) when it is NOT retiring, asked for the
    // same refused swap again, 5 s later, then 10, ... for ever. Measured on
    // staging BA03 (headless, main.9d2cd6fe): 5,099 forced swaps in 300 s,
    // tiles swapped 15 times, and the groups each swap was meant to retire
    // still drawn afterwards (124k before, 120k after) - the at-rest frame
    // ran ~1.5x the budget and the rest pass never went quiet. Such a group
    // now simply stays deferred; the next rest pass revives (fetches) it.
    // A group that IS drawn and whose library left the cache borrows its
    // pool's parts (the pool still holds the geometry it draws with).
    const libFor = (hash) => this._libraryFor(tile, hash);
    // What is deferred NOW asks with the revive margin, exactly as the
    // revive would (reviveMargin): a swap must not bring back a group the
    // revive would hold, or the two thresholds stop being two. Taken before
    // _foldVirtual below, which puts revived (drawn) groups on that list.
    const reviveScale = 1 + Math.max(0, Number(this.reviveMargin) || 0);
    const wasDeferred = tile._deferredHashes;
    const scaleOf = (hash) => (wasDeferred && wasDeferred.has(hash) ? reviveScale : 1);
    const heldNow = new Set();
    let allCached = true;
    tile.instancesByHash.forEach((inst, hash) => {
      // Clearance with the switch off is held like a deferred group. This
      // swap is what setClearanceVisible forces, and without the check it
      // rebuilt the tile with its shells still in it (owner 2026-10-19:
      // "the toggle clearance is not showing/hiding clearances").
      if (shouldHideGroup(hash, inst, this.clearanceState)) { heldNow.add(hash); return; }
      if (this._shouldDefer(hash, this._groupDistance(inst, cameraPosNow), focalPx, inst, this.inFocus(tile), tile.key, scaleOf(hash))) { heldNow.add(hash); return; }
      if (libFor(hash)) return;
      // Not drawn by a pool now: it can wait in the deferred map. That
      // includes a BATCHED group whose library was evicted (eviction did
      // not count batch use - see the eviction tick): it drops out of the
      // rebuilt batch and the next rest pass revives (refetches) it. Round 4
      // still refused here for those: 9,028 refusals in 420 s on staging.
      if (!(tile._poolClaims && tile._poolClaims.has(hash))) return;
      allCached = false;
      this._swapRefusedLast = { tile: tile.key, hash: String(hash).slice(0, 80), why: 'pool-claimed group with no library and no pool' };
    });
    if (!allCached) {
      // Don't mutate tile.lod — leave the tile as it actually is.
      this._swapsRefused = (this._swapsRefused || 0) + 1;
      return false;
    }
    // Revived hashes go back to the deferred list; the next rest pass
    // brings them back at the new LOD.
    this._foldVirtual(tile, true);

    // Phase 1: acquire NEW slots in the target-LOD pool. The
    // materializer's per-batch applyAll is skipped (applicator=null)
    // because we'll run a single sweep over all touched meshes once
    // the swap is complete; running it now would double-write.
    const oldClaims = tile._poolClaims;
    const allFragments = [];
    const mergedRecords = new Map();
    const newClaims = new Map();
    const deferredNow = new Map();
    const batchItems = [];

    tile.instancesByHash.forEach((instances, hash) => {
      const deferDistance = this._groupDistance(instances, cameraPosNow);
      if (heldNow.has(hash)) { deferredNow.set(hash, instances); return; }
      const libraryParts = libFor(hash);
      // Passes the size test but has no geometry here yet: held, so the
      // rest pass revives (fetches) it - never silently dropped.
      if (!libraryParts) { deferredNow.set(hash, instances); return; }
      // The merged mesh is NOT rebuilt here. Loading one is a fetch and a
      // parse, and this path is deliberately synchronous so no frame can
      // see the tile half-swapped. Its items therefore keep the level of
      // detail they were baked at until the tile is unloaded and
      // materialised again - passing the merge's own level as the test
      // keeps coverage unconditional, which is what stops an item being
      // drawn by the merge AND by a pool at once. Merged items are
      // single-instance fragments, 4% of the geometry.
      if (tile._merge
          && this._coveredByMerge(tile._merge, instances, tile._merge.lodIndex)) return;
      const groupLod = this._lodForHash(hash, deferDistance, focalPx, newLod, libraryParts);
      if (this._batchEligible(instances, hash, groupLod)) {
        this._collectBatchItems(batchItems, hash, instances, libraryParts, groupLod);
        return;
      }
      const { fragments, records, _poolClaim } = InstanceMaterializer.materialize({
        libraryParts,
        tileKey: tile.key,
        hash,
        instances,
        initialLod: this._lodForHash(hash, deferDistance, focalPx, newLod, libraryParts),
        poolRegistry: this.poolRegistry,
        applicator: null,
      });
      if (_poolClaim) newClaims.set(_poolClaim.hash, _poolClaim);
      fragments.forEach(f => allFragments.push(f));
      records.forEach((rec, uid) => mergedRecords.set(uid, rec));
    });

    // The tile's batch is rebuilt at the new LOD in the same order as
    // the pools: new one first, old one dropped after the swap, so no
    // frame sees the tile with nothing.
    const oldBatch = tile._batch;
    let newBatch = null;
    if (batchItems.length > 0) {
      newBatch = this._buildBatchForTile(tile, batchItems);
      if (newBatch) {
        newBatch.meshes.forEach(m => allFragments.push(m));
        newBatch.records.forEach((rec, uid) => mergedRecords.set(uid, rec));
      }
    }

    // The merge survives the swap, so its mesh and records go back in:
    // register replaces the tile's whole slot set, and anything left out
    // stops being tracked.
    if (tile._merge) {
      allFragments.push(tile._merge.mesh);
      tile._merge.records.forEach((rec, uid) => mergedRecords.set(uid, rec));
    }

    // Phase 2: atomically swap the adapter. Unregister drops old slot
    // tracking; register installs the new. No await between, no chance
    // of a render frame seeing the adapter half-updated.
    this.adapter.unregister(tile.key);
    this.adapter.register(tile.key, mergedRecords, allFragments);
    if (tile._deferredHashes) this._deferredHashCount -= tile._deferredHashes.size;
    tile._deferredHashes = deferredNow.size ? deferredNow : null;
    this._deferredHashCount += deferredNow.size;

    // Phase 3: release the OLD pool slots. From here the old slots
    // zero-scale (invisible) and the new slots take over rendering.
    if (oldClaims) {
      oldClaims.forEach((claim, hash) => {
        const pool = this.poolRegistry.get(hash, claim.lod);
        if (!pool) return;
        for (const idx of claim.indices) pool.release(idx);
        pool.removeTile(tile.key);
      });
    }
    tile._poolClaims = newClaims;
    tile._batch = newBatch;
    this._disposeBatch(oldBatch);

    // Phase 4: enforce the single-active-instance invariant over every
    // unique new-pool mesh. The applyAll(adapter) call collapses any
    // cross-tile duplicate slots in the new pool to zero, matching the
    // post-register sweep _materializeTile runs on first materialize.
    this._sweepRegistered(allFragments, newClaims);

    flushDirtyPools();
    tile.lod = newLod;
    this._layoutSeq++;
    // A swap rebuilds the tile: what it drew goes, the new set comes in.
    const ledger = thrashOf(this);
    ledger.tileUnloaded(tile.key, reason || (force ? 'retire' : 'lod'));
    ledger.tileLoaded(tile.key, 'swap');
    this._noteInstalled(tile);
  }

  // `reason` is for the thrash ledger only (thrashLedger.js).
  _unload(tile, reason = 'unknown') {
    thrashOf(this).tileUnloaded(tile.key, reason);
    this._bumpInput();
    // Adapter still tracks per-tile slots and per-uid active-owner
    // bookkeeping. It now returns nothing useful to remove from the
    // scene (pool meshes stay parented for all tiles' sake), but the
    // unregister call is still where the adapter cleans up its own
    // state and runs the "promote successor" logic for uids whose
    // active slot was in this tile.
    // Anything still materialising for this tile is now stale.
    tile._materializeEpoch = (tile._materializeEpoch || 0) + 1;
    this._foldVirtual(tile, false);
    tile._deferredHashes = null;
    this.adapter.unregister(tile.key);

    // The tile's batch is the tile's own: unlike a pool it is not shared
    // with anyone, so it goes away with the tile. Dropped after the
    // unregister above, which is what hands this tile's uids to a slot
    // in another tile if one holds them.
    this._disposeBatch(tile._batch);
    tile._batch = null;
    this._disposeChunks(tile._batchChunks);
    tile._batchChunks = null;
    this._disposeMerge(tile._merge);
    tile._merge = null;

    // Release the slot ledger we stashed during materialize. Each
    // _poolClaim records its (hash, lod) so we release back into the
    // exact pool that owned the slots — the 3-pool split means
    // looking up just by hash is ambiguous. Also drop this tile from
    // the pool's activeTiles set so mesh.visible can flip to false if
    // no other tile still contributes unhidden slots.
    if (tile._poolClaims) {
      tile._poolClaims.forEach((claim, hash) => {
        const pool = this.poolRegistry.get(hash, claim.lod);
        if (!pool) return;
        for (const idx of claim.indices) pool.release(idx);
        pool.removeTile(tile.key);
      });
      tile._poolClaims = null;
    }

    // Flush the GL upload triggered by the release calls above. Same
    // batching as in _materializeTile.
    flushDirtyPools();

    tile.lod = null;
    tile.state = TileState.UNLOADED;
    // Hidden flag is orthogonal to the lifecycle; clear it so a
    // future re-materialize starts unhidden.
    tile._frustumHidden = false;
  }

  // Tile-level frustum test with AABB padding. Each tile carries a
  // `box` (Box3) covering its instances. We clone into a scratch box,
  // inflate by (max-dim × (padFactor - 1)) / 2 in every direction, and
  // test intersection. padFactor=1.25 → 25% wider; padFactor=1.0 →
  // literal frustum (more aggressive culling).
  _isTileInPaddedFrustum(tile, padFactor) {
    if (!tile.box) return true;  // No bounds yet — don't cull.
    if (!Number.isFinite(padFactor) || padFactor <= 0) padFactor = 1.0;
    this._paddedBox.copy(tile.box);
    if (padFactor !== 1.0) {
      const size = this._paddedBox.getSize(this._tmpVec);
      const maxDim = Math.max(size.x, size.y, size.z);
      const pad = (maxDim * (padFactor - 1.0)) / 2.0;
      this._paddedBox.expandByScalar(pad);
    }
    return this.frustumTester.intersectsBox(this._paddedBox);
  }

  // Soft-hide a VISIBLE tile that left the padded frustum. Zero-scales
  // all of its claimed pool slots without releasing them. The tile
  // stays adapter-registered, the library cache stays intact, and the
  // pool keeps its slot reservations — so coming back into frustum is
  // a single setMatrixAt loop, not a fetch + re-materialize.
  _frustumHide(tile) {
    if (tile._frustumHidden) return;
    thrashOf(this).tileHidden(tile.key);
    let touched = false;
    if (tile._poolClaims) {
      tile._poolClaims.forEach((claim, hash) => {
        const pool = this.poolRegistry.get(hash, claim.lod);
        if (!pool) return;
        // Pass tile.key so the pool removes this tile from activeTiles;
        // when no tile contributes unhidden slots, pool.mesh.visible
        // flips to false and three.js skips it entirely.
        pool.hideSlots(claim.indices, tile.key);
        touched = true;
      });
    }
    // REVIVED ROWS LEAVE WITH THEIR TILE. They are claimed under the
    // revive's key (tile#vN), and only the tile's own claims were parked:
    // a revived row kept its pool drawn - a call and its triangles - from
    // anywhere in the model, while the budget stopped counting it the
    // moment its tile left the view.
    this._forVirtualClaims(tile, (pool, claim, key) => { pool.hideSlots(claim.indices, key); touched = true; });
    if (touched) flushDirtyPools();
    // The tile's batch culls per item, but it still walks every item every
    // frame to find that out; out of view it is simply not submitted.
    this._setBatchVisible(tile, false);
    tile._frustumHidden = true;
  }

  // Every (pool, claim, key) a tile holds under its revive keys.
  _forVirtualClaims(tile, fn) {
    if (!tile || !tile._virtualClaims || !tile._virtualClaims.length) return;
    for (const v of tile._virtualClaims) {
      if (!v || !v.claims) continue;
      v.claims.forEach((claim, hash) => {
        const pool = this.poolRegistry.get(hash, claim.lod);
        if (pool) fn(pool, claim, v.key, hash);
      });
    }
  }

  _setBatchVisible(tile, visible) {
    const sets = [tile && tile._batch];
    if (tile && tile._virtualClaims) for (const v of tile._virtualClaims) if (v && v.batch) sets.push(v.batch);
    for (const b of sets) {
      if (!b || !Array.isArray(b.meshes)) continue;
      for (const m of b.meshes) if (m && m.visible !== visible) { m.visible = visible; markSceneChanged('batchVisible'); }
    }
  }

  // Restore a previously frustum-hidden tile. Reads slot matrices back
  // from each pool's snapshot buffer, then re-runs the applicator over
  // every affected pool mesh so any hide/isolate/highlight state set
  // during the hidden window gets re-applied to the now-visible slots.
  // WHAT THIS TILE BELIEVES IT HOLDS VERSUS WHAT COULD ACTUALLY DRAW.
  //
  // The pair, not either half: the whole failure mode here is that the two
  // disagree while every count on its own looks healthy. `claimed` is slots
  // the tile still owns, `parked` is how many of those are zero-scaled, and
  // `drawable` is what is left - so claimed>0 with drawable===0 is exactly
  // the owner's symptom, stated as two numbers instead of a description of
  // an empty screen.
  _tileSlotCensus(tile) {
    let claimed = 0; let parked = 0; let poolsMissing = 0;
    if (tile._poolClaims) {
      tile._poolClaims.forEach((claim, hash) => {
        const pool = this.poolRegistry.get(hash, claim.lod);
        if (!pool) { poolsMissing += 1; return; }
        for (const idx of (claim.indices || [])) {
          claimed += 1;
          if (pool.hiddenSlots && pool.hiddenSlots.has(idx)) parked += 1;
        }
      });
    }
    return { claimed, parked, drawable: Math.max(0, claimed - parked), poolsMissing };
  }

  // THE POST-REGISTER SWEEP, OVER WHAT THIS REGISTRATION WROTE.
  //
  // After a tile (or a revive, or a forced swap) registers, store state and
  // the single-active-instance rule must be laid over its new slots. That
  // used to be applicator.applyAll over every pool mesh the tile touched -
  // and pools are global per hash, so each tile's install re-derived every
  // slot of every shared pool: O(tiles x pool) over a load, the
  // "applyAll <- _materializeTileBody" stack in the BA03 real-GPU trace.
  //
  // Only this tile's claimed slots changed. register() never moves an
  // existing uid's ownership (the first tile keeps it), and when unregister
  // promotes a successor in ANOTHER tile it writes that slot itself
  // (adapter._promoteSuccessor -> applyForUids). So the claimed indices are
  // exactly the slots whose answer can have changed. Meshes that belong to
  // the tile alone (its batch, its merge) are still swept whole; a pool
  // mesh this registration holds no claim in is left alone.
  _sweepRegistered(allFragments, claims) {
    if (!this.applicator || !allFragments || allFragments.length === 0) return;
    const byMesh = new Map();
    if (claims && typeof this.applicator.applySlots === 'function') {
      claims.forEach((claim, hash) => {
        const pool = this.poolRegistry && this.poolRegistry.get(hash, claim.lod);
        if (!pool || !pool.mesh || !claim.indices) return;
        const list = byMesh.get(pool.mesh);
        if (list) { for (const i of claim.indices) list.push(i); } else byMesh.set(pool.mesh, claim.indices.slice());
      });
    }
    // The claims name the pool's CURRENT mesh (a pool that grew during the
    // install swapped meshes; the fragments list may still hold the old one).
    byMesh.forEach((slots, m) => {
      this.applicator.applySlots(m, slots, this.adapter);
      this._sweptSlots = (this._sweptSlots || 0) + slots.length;
    });
    const seen = new Set();
    for (const m of allFragments) {
      if (!m || seen.has(m) || byMesh.has(m)) continue;
      seen.add(m);
      const pooled = byMesh.size > 0 && m.isInstancedMesh && !m.isSlotMesh && !m.isBatchedMesh && m.userData && m.userData.hash;
      if (pooled) continue;   // a shared pool's superseded mesh, or one this tile holds nothing in
      this.applicator.applyAll(m, this.adapter);
    }
  }

  // The geometry a group of this tile can be (re)built from: the library
  // cache, or - for a group drawn now whose library left the cache - the
  // parts its pool still draws with. The rest pass's LOD test and the
  // forced swap both use this, so they cannot disagree about which levels
  // exist and loop on it.
  _libraryFor(tile, hash) {
    const lib = this.cache.getLibrary(hash);
    if (lib) return lib;
    const claim = tile && tile._poolClaims && tile._poolClaims.get(hash);
    const pool = claim && this.poolRegistry && this.poolRegistry.get(hash, claim.lod);
    return (pool && pool.libraryParts) || null;
  }

  // The level a group in the tile's batch was built at, or null when the
  // group is not in it. Indexed once per batch object (a rebuilt batch is a
  // new object), from the items it was built from.
  _batchLodOf(tile, hash) {
    const b = tile && tile._batch;
    if (!b || !Array.isArray(b.items)) return null;
    if (!b._lods) {
      b._lods = new Map();
      for (const it of b.items) if (it && typeof it.lod === 'number') b._lods.set(it.hash, it.lod);
    }
    const lod = b._lods.get(hash);
    return lod === undefined ? null : lod;
  }

  // Is this hash drawn by the tile's batch right now? (Batch items carry
  // their hash on the row record; the set is cached per batch object.)
  _inTileBatch(tile, hash) {
    const b = tile && tile._batch;
    if (!b || !Array.isArray(b.meshes)) return false;
    if (!b._hashes) {
      b._hashes = new Set();
      for (const m of b.meshes) {
        const ud = m && m.userData;
        const n = (m && m.batchItemCount) || 0;
        for (let i = 0; i < n; i++) { const r = ud && ud[i]; if (r && r.hash) b._hashes.add(r.hash); }
      }
    }
    return b._hashes.has(hash);
  }

  // CLOSE POOL HOLES WHILE NOTHING IS MATERIALISING (see
  // GlobalInstancePool.compact). Only at rest with no tile loading or
  // reviving - an install in flight holds indices in its locals - and at
  // most `slotBudget` slots per call so one tick stays short. Every outside
  // reference to a moved slot is rewritten before returning: tile claims
  // (real and virtual) and the adapter's slot snapshots and owner records
  // (its mesh-reference index, mapMeshesAdapter._holdersByMesh).
  _compactPools(slotBudget = 60000) {
    const pr = this.poolRegistry;
    if (!pr || !pr.pools) return 0;
    let busy = (this._activeTileLoads || 0) > 0;
    if (!busy) this.registry.forEach((t) => { if (t.state === TileState.FRAGMENTS_LOADING || t._reviving || t._materializing) busy = true; });
    if (busy) return 0;
    // Low thresholds: on BA03 the holes were spread thin (8k hole slots
    // left after 17 compactions at 256 / 20 %), and a compaction is a
    // linear copy of one pool.
    const minHoles = this.compactMinHoles != null ? this.compactMinHoles : 64;
    const frac = this.compactMinFraction != null ? this.compactMinFraction : 0.1;
    // WEIGHED IN TRIANGLES, NOT ONLY IN SLOTS (round 9). A hole costs its
    // pool's triangles-per-instance every frame, and the slot thresholds
    // above only ever reached the big pools: headless staging BA03 at rest
    // still held 6,257 hole slots after 37 compactions, ~1.5 M triangles,
    // spread a few per pool over the small pools - each under 64 holes. A
    // pool whose holes add up to compactMinHoleTris is compacted however
    // few they are.
    const minHoleTris = this.compactMinHoleTris != null ? this.compactMinHoleTris : 256;
    const trisEach = (pool) => {
      const g = pool && pool.mesh && pool.mesh.geometry;
      if (!g) return 0;
      if (g.index) return g.index.count / 3;
      const pos = g.attributes && g.attributes.position;
      return pos ? pos.count / 3 : 0;
    };
    const worth = (pool) => {
      const holes = pool.freeSlots ? pool.freeSlots.length : 0;
      if (holes === 0) return false;
      if (holes >= minHoles && holes >= pool.count * frac) return true;
      return minHoleTris > 0 && holes * trisEach(pool) >= minHoleTris;
    };
    // Not worth a pass (and the redraw after it) for a few stray holes:
    // by slots, or by the triangles they cost.
    const totalMin = this.compactMinTotalHoles != null ? this.compactMinTotalHoles : 1024;
    const totalMinTris = this.compactMinTotalHoleTris != null ? this.compactMinTotalHoleTris : 65536;
    if (totalMin > 0) {
      let total = 0;
      let totalTris = 0;
      pr.pools.forEach((arr) => (Array.isArray(arr) ? arr : [arr]).forEach((p) => {
        if (p && p.freeSlots && p.freeSlots.length) { total += p.freeSlots.length; totalTris += p.freeSlots.length * trisEach(p); }
      }));
      if (total < totalMin && !(totalMinTris > 0 && totalTris >= totalMinTris)) return 0;
    }
    const remaps = new Map();   // 'hash|lod' -> Map(old -> new)
    let moved = 0;
    pr.pools.forEach((arr, hash) => {
      if (moved >= slotBudget) return;
      (Array.isArray(arr) ? arr : [arr]).forEach((pool, lod) => {
        if (!pool || !pool.mesh || moved >= slotBudget) return;
        const holes = pool.freeSlots ? pool.freeSlots.length : 0;
        if (!worth(pool)) return;
        const remap = pool.compact();
        if (remap.size) { remaps.set(`${hash}|${lod}`, { remap, pool }); moved += remap.size; }
        this._compactedHoles = (this._compactedHoles || 0) + holes;
      });
    });
    if (!remaps.size) return 0;
    const fix = (claims) => {
      if (!claims) return;
      claims.forEach((claim, hash) => {
        const r = remaps.get(`${hash}|${claim.lod}`);
        if (!r || !claim.indices) return;
        for (let i = 0; i < claim.indices.length; i++) {
          const to = r.remap.get(claim.indices[i]);
          if (to !== undefined) claim.indices[i] = to;
        }
      });
    };
    this.registry.forEach((t) => {
      fix(t._poolClaims);
      if (t._virtualClaims) for (const v of t._virtualClaims) fix(v.claims);
    });
    const holders = this.adapter && this.adapter._holdersByMesh;
    if (holders) {
      remaps.forEach(({ remap, pool }) => {
        const set = holders.get(pool.mesh);
        if (!set) return;
        const ud = pool.mesh.userData;
        set.forEach((obj) => {
          // The row record IS userData[i]: compact() already moved it.
          if (ud && ud[obj.index] === obj) return;
          const to = remap.get(obj.index);
          if (to !== undefined) obj.index = to;
        });
      });
    }
    flushDirtyPools();
    this._compactions = (this._compactions || 0) + remaps.size;
    this._compactedSlots = (this._compactedSlots || 0) + moved;
    return moved;
  }

  // What is drawn now that the size test would hold back (the rest pass
  // has not retired it yet), in triangles and distinct draw calls - the
  // gap between deferral.plannedTris/Calls and what the GPU draws.
  // On demand only (perfSnapshot); one pass over the groups in view.
  staleStats() {
    const cam = this.viewer && this.viewer.camera && this.viewer.camera.position;
    if (!cam || !this.fragmentsIndex) return null;
    const focalPx = this._focalPx();
    let tris = 0; let groups = 0; let tiles = 0; const calls = new Set();
    this.registry.forEach((tile) => {
      if (tile.state !== TileState.VISIBLE || !tile.instancesByHash) return;
      if (!this._isTileInPaddedFrustum(tile, this.frustumPaddingFactor)) return;
      const focus = this.inFocus(tile);
      let here = 0;
      tile.instancesByHash.forEach((inst, hash) => {
        if (tile._deferredHashes && tile._deferredHashes.has(hash)) return;
        const d = this._groupDistance(inst, cam);
        if (!this._shouldDefer(hash, d, focalPx, inst, focus, tile.key)) return;
        const c = tile._poolClaims && tile._poolClaims.get(hash);
        const lod = c && typeof c.lod === 'number' ? c.lod : this._lodForHash(hash, d, focalPx, tile.lod || 0, this.cache.getLibrary(hash));
        tris += this._trisOfClaim(hash, lod, inst.length);
        calls.add(this._callKey(tile, hash, lod, inst));
        groups++; here++;
      });
      if (here) tiles++;
    });
    return { tris, calls: calls.size, groups, tiles };
  }

  _bumpInput() {
    this._inputSeq = (this._inputSeq || 0) + 1;
    this._layoutSeq = (this._layoutSeq || 0) + 1;
  }

  // Everything a tile's rest check reads, as one key: a tile whose last
  // check came out clean under this key has nothing new to find.
  _restStampNow() {
    return (this._lastCameraMoveTime || 0) + '|' + (this._layoutSeq || 0) + '|' + (this.dynamicMinPx || 0)
      + '|' + (this._budgetSeq || 0) + '|' + (this._coneSeq || 0) + '|' + (this.quality || 1);
  }

  // Pool slots a tile holds (what _frustumShow will restore and re-derive).
  _claimedSlotCount(tile) {
    let n = 0;
    const add = (claim) => { n += (claim && claim.indices) ? claim.indices.length : 0; };
    if (tile && tile._poolClaims) tile._poolClaims.forEach(add);
    if (tile && tile._virtualClaims) {
      for (const v of tile._virtualClaims) if (v && v.claims) v.claims.forEach(add);
    }
    return n;
  }

  _frustumShow(tile) {
    if (!tile._frustumHidden) return;
    // WHAT THE RESTORE ACTUALLY DID, per pool, because this used to tick
    // the tile off as shown whether or not anything happened. showSlots
    // answers 'restored' | 'empty' | 'failed', and a pool that has gone
    // missing entirely is a fourth case handled below - the tile's claims
    // are then stale and the geometry really is gone, so the tile has to go
    // back to the loader rather than be marked shown over nothing.
    let failed = 0;
    let missing = 0;
    let restored = 0;
    if (tile._poolClaims) {
      const touched = [];
      tile._poolClaims.forEach((claim, hash) => {
        const pool = this.poolRegistry.get(hash, claim.lod);
        if (!pool) { missing += 1; return; }
        const outcome = pool.showSlots(claim.indices, tile.key);
        if (outcome === 'failed') failed += 1;
        else if (outcome === 'restored') restored += 1;
        if (pool.mesh) touched.push([pool.mesh, claim.indices]);
      });
      // Revived rows come back with the tile (see _frustumHide).
      // (Not counted as 'restored': whether the tile's OWN pools are gone is
      // what decides a reload below.)
      this._forVirtualClaims(tile, (pool, claim, key) => {
        if (pool.showSlots(claim.indices, key) === 'failed') failed += 1;
        if (pool.mesh) touched.push([pool.mesh, claim.indices]);
      });
      flushDirtyPools();
      // Re-apply store state (hide / isolate / highlight) over THIS TILE'S
      // slots - the only ones the restore wrote. Without this, a uid the
      // user hid while the tile was out of frustum would un-hide here. The
      // adapter param honours the single-active-instance invariant.
      //
      // NOT the whole pool: pools are global per hash, so applyAll here
      // cost (tiles re-shown this tick) x (every slot of every pool they
      // touch) - one tick after an orbit was 15-17 s on BA03. See
      // InstanceStateApplicator.applySlots.
      if (this.applicator) {
        for (const [m, indices] of touched) {
          if (typeof this.applicator.applySlots === 'function') this.applicator.applySlots(m, indices, this.adapter);
          else this.applicator.applyAll(m, this.adapter);
        }
      }
    }

    // A POOL THAT IS GONE MEANS THE GEOMETRY IS GONE. Marking the tile
    // shown would park it for the life of the page; sending it back to
    // UNLOADED lets the ordinary load gate fetch and materialise it again,
    // which is the recovery this path never had.
    if (missing > 0 && restored === 0) {
      const c = this._tileSlotCensus(tile);
      console.warn(`[StreamingController] tile ${tile.key}: ${missing} pool(s) gone while out of frustum - reloading it`);
      // REPORTED TO THE SERVER JOURNAL, because this condition is only
      // reachable on a model big enough to evict pools, and the machines
      // that reach it are not ours. Staging cannot host the model that
      // shows this: the live SpaceX export is 1,377 tiles and 1.47M
      // instances, which headless software rendering on this box will not
      // draw - so without this, the only evidence would be somebody saying
      // the model looks half empty.
      reportClientError('viewer.fragments.poolGone', {
        message: `tile=${tile.key} poolsMissing=${missing} claimed=${c.claimed} parked=${c.parked} drawable=${c.drawable} - reloaded`,
      });
      this._unload(tile, 'poolGone');
      return;
    }

    // ONLY TICKED OFF WHEN THERE IS NOTHING LEFT TO DO. A failed restore
    // leaves the flag set so the next frustum pass tries again - without
    // this, one failure parked those fragments permanently, because the
    // guard at the top of this function refuses to retry a tile that is no
    // longer marked hidden.
    if (failed > 0) {
      tile._frustumShowFailures = (tile._frustumShowFailures || 0) + 1;
      // Said once, with the condition named, rather than every pass: a
      // restore that keeps failing is a fact worth seeing, and a warning
      // per frame is a fact nobody can read.
      if (tile._frustumShowFailures === 3) {
        const c = this._tileSlotCensus(tile);
        console.warn(`[StreamingController] tile ${tile.key}: restore failed ${tile._frustumShowFailures} times (${failed} pool(s) could not restore their slots); it stays queued for retry`);
        // THE COUNTS, FROM THE MACHINE WHERE THIS ACTUALLY HAPPENS. Said
        // once at the third attempt, not per frame: the pair claimed vs
        // drawable is what distinguishes "parked and never restored" from
        // every other reason a model can look empty, and no screenshot can
        // make that distinction.
        reportClientError('viewer.fragments.restoreFailed', {
          message: `tile=${tile.key} poolsFailed=${failed} attempts=${tile._frustumShowFailures} claimed=${c.claimed} parked=${c.parked} drawable=${c.drawable}`,
        });
      }
      return;
    }
    tile._frustumShowFailures = 0;
    this._setBatchVisible(tile, true);
    tile._frustumHidden = false;
  }

  stats() {
    const byState = {
      [TileState.IDLE]: 0,
      [TileState.SIDECAR_LOADING]: 0,
      [TileState.FRAGMENTS_LOADING]: 0,
      [TileState.VISIBLE]: 0,
      [TileState.UNLOADED]: 0,
    };
    let inBounds = 0, outOfBounds = 0;
    this.registry.forEach(tile => {
      byState[tile.state] = (byState[tile.state] ?? 0) + 1;
      if (tile.inTrimmedBounds) inBounds++;
      else outOfBounds++;
    });

    return {
      tilesTotal:        inBounds + outOfBounds,
      tilesInBounds:     inBounds,
      tilesOutOfBounds:  outOfBounds,
      byState,
      activeTileLoads:   this._activeTileLoads,
      showOutliers:      this._showOutliers,
      totalInstancedEvicted: this._totalInstancedEvicted,
      totalLibraryEvicted:   this._totalLibraryEvicted,
      maxInstancedEntries:   this.maxInstancedEntries,
      maxLibraryEntries:     this.maxLibraryEntries,
      totalMissingHashesSkipped: this._totalMissingHashesSkipped,
      totalCoverageUnloaded: this._totalCoverageUnloaded,
      totalWatchdogResets:   this._totalWatchdogResets ?? 0,
      coverageThresholdPixels: this.coverageThresholdPixels,
      maxHiddenTimeMs:       this.maxHiddenTimeMs,
      lodRestMs:             this.lodRestMs,
      pools:                 this.poolRegistry?.stats?.() ?? null,
    };
  }
}